import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  cleanupPatternPerformanceDuplicates,
  executePatternScannerCleanup,
  fetchPatternPerformanceCalibration,
  fetchPatternPerformanceComparison,
  fetchPatternPerformanceDiagnostic,
  fetchPatternPerformanceDetail,
  fetchPatternPerformanceSummary,
  previewPatternScannerCleanup,
  purgeLegacyPatternPerformanceV1,
  purgePatternPerformanceHistory,
  purgeStablecoinPatternPerformanceData,
  rebuildPatternScannerBenchmark,
} from '../../api/adminPatternPerformance';
import {
  resolveCanonicalBias,
  resolveCanonicalBreakout,
  resolveCoherenceSnapshot,
  resolvePatternEngineV3Metadata,
} from '../pattern-scanner/scannerExperienceShared';

const DEFAULT_FILTERS = {
  version: '',
  engineBuild: '',
  pattern: '',
  patternFamily: '',
  crypto: '',
  exchange: '',
  timeframe: '',
  direction: '',
  status: '',
  scoreMin: '',
  scoreMax: '',
  scoreBucket: '',
  mtfScoreMin: '',
  mtfScoreMax: '',
  mtfBucket: '',
  mtfAlignment: '',
  confidenceBucket: '',
  assetType: '',
  volumeConfirmed: '',
  dateFrom: '',
  dateTo: '',
  benchmarkRunId: '',
  cohortId: '',
};

const BENCHMARK_PURGE_RESTRICTED_KEYS = [
  'version',
  'engineBuild',
  'pattern',
  'patternFamily',
  'crypto',
  'exchange',
  'timeframe',
  'direction',
  'status',
  'scoreMin',
  'scoreMax',
  'scoreBucket',
  'mtfScoreMin',
  'mtfScoreMax',
  'mtfBucket',
  'mtfAlignment',
  'confidenceBucket',
  'assetType',
  'volumeConfirmed',
  'dateFrom',
  'dateTo',
];

const STANDARD_BUCKET_OPTIONS = ['<50', '50-59', '60-69', '70-79', '80-89', '90-100'];
const MTF_BUCKET_OPTIONS = [...STANDARD_BUCKET_OPTIONS, 'UNKNOWN'];
const MTF_ALIGNMENT_OPTIONS = ['STRONG', 'MIXED', 'CONFLICT', 'UNKNOWN'];
const OUTCOME_DETECTION_STATUSES = new Set(['BROKEN_OUT', 'TARGET_1_HIT', 'TARGET_2_HIT', 'TARGET_HIT', 'INVALIDATED']);
const BREAKOUT_OUTCOME_STATUSES = new Set(['BROKEN_OUT', 'TARGET_1_HIT', 'TARGET_2_HIT', 'TARGET_HIT']);
const TARGET_OUTCOME_STATUSES = new Set(['TARGET_1_HIT', 'TARGET_2_HIT', 'TARGET_HIT']);
const ASSET_TYPE_OPTIONS = [
  { value: '', label: 'Hors stablecoins (défaut)' },
  { value: 'CRYPTO', label: 'Crypto' },
  { value: 'UNKNOWN', label: 'Type inconnu / legacy' },
];
const TAB_OPTIONS = [
  { value: 'overview', label: 'Vue d ensemble' },
  { value: 'diagnostic', label: 'Diagnostic versions' },
  { value: 'patterns', label: 'Par pattern' },
  { value: 'versions', label: 'Par version' },
  { value: 'timeframes', label: 'Par timeframe' },
  { value: 'score', label: 'Par score' },
  { value: 'mtf', label: 'MTF' },
  { value: 'calibration', label: 'Calibration' },
];
const PAGE_COLORS = {
  ink: '#17323c',
  inkSoft: '#355b63',
  sand: '#fffaf2',
  mist: '#edf3f6',
  border: '#e6d9c7',
  muted: '#4f646f',
  success: '#2f7d5d',
  warning: '#b57422',
  danger: '#9c4f4f',
  accent: '#c07c32',
};

function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeRatio(value) {
  const numeric = toNumber(value);
  if (numeric === null) {
    return null;
  }
  return Math.abs(numeric) > 1.5 ? numeric / 100 : numeric;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function formatPercent(value, fallback = 'n/a') {
  const normalized = normalizeRatio(value);
  if (normalized === null) {
    return fallback;
  }
  return `${(normalized * 100).toFixed(1)}%`;
}

function formatPercentValue(value, digits = 1, fallback = 'n/a') {
  const numeric = toNumber(value);
  if (numeric === null) {
    return fallback;
  }
  return `${numeric.toFixed(digits)}%`;
}

function formatDecimal(value, digits = 1, fallback = 'n/a') {
  const numeric = toNumber(value);
  if (numeric === null) {
    return fallback;
  }
  return numeric.toFixed(digits);
}

function formatScore(value, digits = 0, fallback = 'n/a') {
  return formatDecimal(value, digits, fallback);
}

function formatDuration(seconds) {
  const numeric = toNumber(seconds);
  if (numeric === null || numeric <= 0) {
    return 'n/a';
  }
  const totalSeconds = Math.round(numeric);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) {
    return `${days}j ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('fr-FR');
}

function formatMilliseconds(value, fallback = 'n/a') {
  const numeric = toNumber(value);
  if (numeric === null) {
    return fallback;
  }
  return `${Math.round(numeric)} ms`;
}

function formatPercentileTriplet(distribution, fallback = 'n/a') {
  if (!distribution) {
    return fallback;
  }
  const p50 = formatMilliseconds(distribution.p50Ms, null);
  const p95 = formatMilliseconds(distribution.p95Ms, null);
  const p99 = formatMilliseconds(distribution.p99Ms, null);
  if (!p50 && !p95 && !p99) {
    return fallback;
  }
  return `p50 ${p50 || 'n/a'} | p95 ${p95 || 'n/a'} | p99 ${p99 || 'n/a'}`;
}

function parseVersionsInput(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .join(',');
}

function countParsedVersions(value) {
  const normalized = parseVersionsInput(value);
  return normalized ? normalized.split(',').length : 0;
}

function isTwoVersionDiagnosticSelection(value) {
  const normalized = parseVersionsInput(value);
  if (!normalized) {
    return true;
  }
  const versions = normalized.split(',');
  return versions.length === 2 && new Set(versions).size === 2;
}

const DEFAULT_DIAGNOSTIC_VERSION_LABELS = {
  leftVersion: 'v4',
  rightVersion: 'v4.3',
  leftTag: 'V4',
  rightTag: 'V4.3',
  leftOnlyLabel: 'V4 only',
  rightOnlyLabel: 'V4.3 only',
  allLeftLabel: 'All V4',
  allRightLabel: 'All V4.3',
  leftBothLabel: 'V4 in common cohort',
  rightBothLabel: 'V4.3 in common cohort',
  commonLabel: 'Common cohort',
};

function formatVersionTag(value) {
  const normalized = String(value || '').trim();
  return normalized ? normalized.toUpperCase() : 'N/A';
}

function resolveDiagnosticVersionLabels(versions) {
  const normalized = Array.isArray(versions) && versions.length >= 2
    ? versions.filter(Boolean).slice(0, 2)
    : [DEFAULT_DIAGNOSTIC_VERSION_LABELS.leftVersion, DEFAULT_DIAGNOSTIC_VERSION_LABELS.rightVersion];
  const leftVersion = normalized[0] || DEFAULT_DIAGNOSTIC_VERSION_LABELS.leftVersion;
  const rightVersion = normalized[1] || DEFAULT_DIAGNOSTIC_VERSION_LABELS.rightVersion;
  const leftTag = formatVersionTag(leftVersion);
  const rightTag = formatVersionTag(rightVersion);
  return {
    leftVersion,
    rightVersion,
    leftTag,
    rightTag,
    leftOnlyLabel: `${leftTag} only`,
    rightOnlyLabel: `${rightTag} only`,
    allLeftLabel: `All ${leftTag}`,
    allRightLabel: `All ${rightTag}`,
    leftBothLabel: `${leftTag} in common cohort`,
    rightBothLabel: `${rightTag} in common cohort`,
    commonLabel: 'Common cohort',
  };
}

function resolveEngineVersionFamily(value) {
  const segments = parseEngineVersionSegments(value);
  if (segments.length) {
    return String(segments[0]);
  }
  const normalized = String(value || '').trim().toLowerCase();
  return normalized || null;
}

function shareDiagnosticVersionFamily(leftVersion, rightVersion) {
  const leftFamily = resolveEngineVersionFamily(leftVersion);
  const rightFamily = resolveEngineVersionFamily(rightVersion);
  return Boolean(leftFamily) && leftFamily === rightFamily;
}

function resolvePopulationOptionValue(populations, candidates) {
  if (!populations) {
    return null;
  }
  return candidates.find((candidate) => populations[candidate]);
}

function resolveDiagnosticPopulationOptions(expansion, versionLabels) {
  const populations = expansion?.populations;
  if (!populations) {
    return [];
  }

  const sameFamily = shareDiagnosticVersionFamily(versionLabels.leftVersion, versionLabels.rightVersion);
  const optionSpecs = sameFamily
    ? [
      { candidates: ['LEFT_ONLY', 'V3_ONLY'], label: versionLabels.leftOnlyLabel },
      { candidates: ['COMMON', 'LEFT_BOTH', 'RIGHT_BOTH'], label: versionLabels.commonLabel },
      { candidates: ['RIGHT_ONLY', 'V4_ONLY'], label: versionLabels.rightOnlyLabel },
      { candidates: ['LEFT_ALL'], label: versionLabels.allLeftLabel },
      { candidates: ['RIGHT_ALL', 'ALL_V4'], label: versionLabels.allRightLabel },
      { candidates: ['LEFT_BOTH'], label: versionLabels.leftBothLabel },
      { candidates: ['RIGHT_BOTH'], label: versionLabels.rightBothLabel },
    ]
    : [
      { candidates: ['RIGHT_ONLY', 'V4_ONLY'], label: versionLabels.rightOnlyLabel },
      { candidates: ['COMMON', 'LEFT_BOTH', 'RIGHT_BOTH'], label: versionLabels.commonLabel },
      { candidates: ['RIGHT_ALL', 'ALL_V4'], label: versionLabels.allRightLabel },
      { candidates: ['LEFT_ONLY', 'V3_ONLY'], label: versionLabels.leftOnlyLabel },
      { candidates: ['LEFT_ALL'], label: versionLabels.allLeftLabel },
      { candidates: ['LEFT_BOTH'], label: versionLabels.leftBothLabel },
      { candidates: ['RIGHT_BOTH'], label: versionLabels.rightBothLabel },
    ];

  return optionSpecs
    .map((spec) => {
      const value = resolvePopulationOptionValue(populations, spec.candidates);
      return value ? { value, label: spec.label } : null;
    })
    .filter(Boolean);
}

function resolveDefaultDiagnosticPopulation(options, versionLabels) {
  if (!options?.length) {
    return '';
  }

  const sameFamily = shareDiagnosticVersionFamily(versionLabels.leftVersion, versionLabels.rightVersion);
  const preferred = sameFamily
    ? ['LEFT_ONLY', 'V3_ONLY', 'COMMON', 'LEFT_BOTH', 'RIGHT_BOTH', 'RIGHT_ONLY', 'V4_ONLY']
    : ['RIGHT_ONLY', 'V4_ONLY', 'COMMON', 'LEFT_BOTH', 'RIGHT_BOTH', 'LEFT_ONLY', 'V3_ONLY'];

  return preferred.find((candidate) => options.some((option) => option.value === candidate)) || options[0].value;
}

function parseEngineVersionSegments(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized.startsWith('v') || normalized.length < 2) {
    return [];
  }
  const segments = normalized.slice(1).split('.').map((token) => Number.parseInt(token, 10));
  return segments.every((segment) => Number.isInteger(segment)) ? segments : [];
}

function compareEngineVersionLabels(left, right) {
  if (left === right) {
    return 0;
  }
  const leftSegments = parseEngineVersionSegments(left);
  const rightSegments = parseEngineVersionSegments(right);
  if (leftSegments.length && rightSegments.length) {
    const segmentCount = Math.max(leftSegments.length, rightSegments.length);
    for (let index = 0; index < segmentCount; index += 1) {
      const leftPart = index < leftSegments.length ? leftSegments[index] : -1;
      const rightPart = index < rightSegments.length ? rightSegments[index] : -1;
      if (leftPart !== rightPart) {
        return leftPart - rightPart;
      }
    }
  } else if (leftSegments.length) {
    return -1;
  } else if (rightSegments.length) {
    return 1;
  }
  return String(left || '').localeCompare(String(right || ''));
}

function formatValue(value, fallback = 'n/a') {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return value;
}

function formatDirectionLabel(value, fallback = 'n/a') {
  switch (String(value || '').toUpperCase()) {
    case 'BULLISH':
      return 'Haussier';
    case 'BEARISH':
      return 'Baissier';
    case 'NEUTRAL':
      return 'Neutre';
    case 'MIXED':
      return 'Mixte';
    default:
      return fallback;
  }
}

function formatCanonicalBiasValue(bias) {
  const code = String(bias?.code || '').toUpperCase();
  const bullishProbability = Number.isFinite(Number(bias?.bullishProbability)) ? Math.round(Number(bias.bullishProbability)) : null;
  const bearishProbability = Number.isFinite(Number(bias?.bearishProbability)) ? Math.round(Number(bias.bearishProbability)) : null;

  if (code === 'BULLISH') {
    return bullishProbability !== null ? `Haussier ${bullishProbability}%` : 'Haussier';
  }
  if (code === 'BEARISH') {
    return bearishProbability !== null ? `Baissier ${bearishProbability}%` : 'Baissier';
  }
  if (bullishProbability !== null && bearishProbability !== null) {
    return `Neutre ${bullishProbability}% / ${bearishProbability}%`;
  }
  return 'Neutre';
}

function formatCanonicalBiasHelper(bias) {
  const parts = [];
  if (Number.isFinite(Number(bias?.bullishProbability))) {
    parts.push(`Haussier ${Math.round(Number(bias.bullishProbability))}%`);
  }
  if (Number.isFinite(Number(bias?.bearishProbability))) {
    parts.push(`Baissier ${Math.round(Number(bias.bearishProbability))}%`);
  }
  return parts.length ? parts.join(' • ') : 'Lecture probabiliste indisponible.';
}

function formatBreakoutStatusQualifier(status) {
  switch (String(status || '').toUpperCase()) {
    case 'PRE_BREAKOUT':
      return 'pré-cassure';
    case 'CANDIDATE_BREAKOUT':
      return 'en approche';
    case 'CONFIRMED_BREAKOUT':
      return 'confirmée';
    case 'RETEST_PHASE':
      return 'en retest';
    default:
      return '';
  }
}

function formatCanonicalBreakoutValue(breakout) {
  if (!breakout) {
    return 'n/a';
  }
  const typeLabel = String(breakout.type || '').toUpperCase() === 'BREAKDOWN'
    ? 'Cassure baissière'
    : String(breakout.type || '').toUpperCase() === 'BREAKOUT'
      ? 'Cassure haussière'
      : 'Cassure';
  const qualifier = formatBreakoutStatusQualifier(breakout.status);
  return qualifier ? `${typeLabel} · ${qualifier}` : typeLabel;
}

function formatCanonicalBreakoutHelper(breakout) {
  if (!breakout) {
    return 'Niveaux de cassure indisponibles.';
  }
  const parts = [];
  if (Number.isFinite(Number(breakout.level))) {
    parts.push(`Niveau ${Number(breakout.level)}`);
  }
  if (Number.isFinite(Number(breakout.targetPrice))) {
    parts.push(`Target ${Number(breakout.targetPrice)}`);
  }
  if (Number.isFinite(Number(breakout.stopInvalidation))) {
    parts.push(`Invalidation ${Number(breakout.stopInvalidation)}`);
  }
  return parts.length ? parts.join(' • ') : 'Niveaux de cassure indisponibles.';
}

function formatCanonicalCoherenceValue(coherence) {
  switch (String(coherence?.overallStatus || '').toUpperCase()) {
    case 'CONSISTENT':
      return 'Cohérente';
    case 'MIXED':
      return 'Mixte';
    case 'INCONSISTENT':
      return 'Incohérente';
    default:
      return 'n/a';
  }
}

function formatV3ScoreLabel(value, fallback = 'n/a') {
  switch (String(value || '').toUpperCase()) {
    case 'HIGH':
      return 'Fort';
    case 'GOOD':
      return 'Solide';
    case 'MODERATE':
      return 'Modéré';
    case 'FRAGILE':
      return 'Fragile';
    default:
      return fallback;
  }
}

function formatV3ComponentLabel(key) {
  switch (String(key || '')) {
    case 'structureScore':
      return 'Structure';
    case 'patternConfidence':
      return 'Pattern';
    case 'structureQuality':
      return 'Propreté';
    case 'breakoutQuality':
      return 'Cassure';
    case 'volumeConfirmationScore':
      return 'Volume';
    case 'breakoutState':
      return 'État';
    case 'tradeConfidence':
      return 'Trade';
    case 'signalConviction':
      return 'Conviction';
    case 'targetConfidence':
      return 'Cible';
    case 'confidence':
      return 'Confiance';
    case 'fakeoutSafety':
      return 'Anti-fakeout';
    default:
      return formatValue(key);
  }
}

function formatV3ComponentValue(key, value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (String(key) === 'breakoutState') {
    return formatValue(value);
  }
  const numeric = toNumber(value);
  if (numeric !== null) {
    return formatScore(numeric);
  }
  return formatValue(value, null);
}

function formatV3ScoreHelper(snapshot, fallback = 'Score V3 indisponible.') {
  if (!snapshot) {
    return fallback;
  }
  const parts = [];
  const scoreLabel = formatV3ScoreLabel(snapshot.label, '');
  if (scoreLabel) {
    parts.push(`Lecture ${scoreLabel}`);
  }
  Object.entries(snapshot.components || {}).forEach(([key, value]) => {
    const formattedValue = formatV3ComponentValue(key, value);
    if (formattedValue) {
      parts.push(`${formatV3ComponentLabel(key)} ${formattedValue}`);
    }
  });
  return parts.length ? parts.join(' • ') : fallback;
}

function formatV3DirectionalBiasHelper(bias) {
  if (!bias) {
    return 'Lecture directionnelle V3 indisponible.';
  }
  const parts = [];
  const probabilitySummary = formatCanonicalBiasHelper(bias);
  if (probabilitySummary !== 'Lecture probabiliste indisponible.') {
    parts.push(probabilitySummary);
  }
  if (bias.structureBias) {
    parts.push(`Structure ${formatDirectionLabel(bias.structureBias, bias.structureBias)}`);
  }
  if (bias.patternDirection) {
    parts.push(`Pattern ${formatDirectionLabel(bias.patternDirection, bias.patternDirection)}`);
  }
  if (Number.isFinite(Number(bias.spread))) {
    parts.push(`Spread ${Math.round(Number(bias.spread))} pts`);
  }
  if (bias.source) {
    parts.push(formatValue(bias.source));
  }
  return parts.length ? parts.join(' • ') : 'Lecture directionnelle V3 indisponible.';
}

function formatV3RenderingHelper(rendering) {
  if (!rendering) {
    return 'Métadonnées de rendu V3 indisponibles.';
  }
  const parts = [];
  if (rendering.annotationLocked) {
    parts.push('Rendu verrouillé sur le pattern sélectionné');
  }
  if (rendering.annotationDirection) {
    parts.push(`Direction ${formatDirectionLabel(rendering.annotationDirection, rendering.annotationDirection)}`);
  }
  if (rendering.overlaySource) {
    parts.push(`Source ${formatValue(rendering.overlaySource)}`);
  }
  return parts.length ? parts.join(' • ') : 'Métadonnées de rendu V3 indisponibles.';
}

function formatProbabilityValue(value, fallback = 'n/a') {
  const numeric = toNumber(value);
  if (numeric === null) {
    return fallback;
  }
  return `${numeric.toFixed(1)}%`;
}

function formatV3AggregateBiasValue(metrics, fallback = 'n/a') {
  const code = String(metrics?.dominantBias || '').toUpperCase();
  switch (code) {
    case 'BULLISH':
      return 'Haussier';
    case 'BEARISH':
      return 'Baissier';
    case 'NEUTRAL':
      return 'Neutre';
    case 'MIXED':
      return 'Mixte';
    default:
      return fallback;
  }
}

function formatV3CoverageHelper(metrics, detections, fallback = 'Aucun snapshot V3 historisé.') {
  if (!metrics) {
    return fallback;
  }
  const snapshotCount = toNumber(metrics.snapshotCount);
  if (snapshotCount === null || snapshotCount <= 0) {
    return fallback;
  }
  const parts = [`${Math.round(snapshotCount)} snapshot(s) V3`];
  const detectionCount = toNumber(detections ?? metrics.detections);
  if (detectionCount !== null) {
    parts.push(`${Math.round(detectionCount)} détection(s)`);
  }
  if (metrics.lockedRenderingRate !== null && metrics.lockedRenderingRate !== undefined) {
    parts.push(`Rendu verrouillé ${formatPercent(metrics.lockedRenderingRate)}`);
  }
  return parts.join(' • ');
}

function formatV3AggregateBiasHelper(metrics) {
  if (!metrics || !toNumber(metrics.snapshotCount)) {
    return 'Lecture directionnelle V3 indisponible.';
  }
  const parts = [];
  if (toNumber(metrics.averageBullishProbability) !== null) {
    parts.push(`Haussier moyen ${formatProbabilityValue(metrics.averageBullishProbability)}`);
  }
  if (toNumber(metrics.averageBearishProbability) !== null) {
    parts.push(`Baissier moyen ${formatProbabilityValue(metrics.averageBearishProbability)}`);
  }
  const bullishCount = Math.round(toNumber(metrics.bullishCount) || 0);
  const bearishCount = Math.round(toNumber(metrics.bearishCount) || 0);
  const neutralCount = Math.round(toNumber(metrics.neutralCount) || 0);
  const labeledCounts = [];
  if (bullishCount > 0) {
    labeledCounts.push(`${bullishCount} haussier(s)`);
  }
  if (bearishCount > 0) {
    labeledCounts.push(`${bearishCount} baissier(s)`);
  }
  if (neutralCount > 0) {
    labeledCounts.push(`${neutralCount} neutre(s)`);
  }
  if (labeledCounts.length) {
    parts.push(labeledCounts.join(' • '));
  }
  return parts.length ? parts.join(' • ') : 'Lecture directionnelle V3 indisponible.';
}

function buildAdminCanonicalSource(detection, initialAnalysis) {
  if (!detection && !initialAnalysis) {
    return {};
  }
  return {
    ...(initialAnalysis || {}),
    direction: initialAnalysis?.direction || detection?.direction,
    breakoutLevel: initialAnalysis?.breakoutLevel ?? detection?.breakoutPrice,
    targetPrice: initialAnalysis?.targetPrice ?? detection?.targetFinalPrice,
    stopInvalidation: initialAnalysis?.stopInvalidation ?? detection?.invalidationPrice ?? detection?.stopPrice,
    rawJson: detection?.analysisSnapshot || initialAnalysis?.rawJson || '',
  };
}

function OutcomeFlag({ active, trueLabel, falseLabel, tone = 'success' }) {
  const color = tone === 'danger' ? PAGE_COLORS.danger : PAGE_COLORS.success;

  if (!active) {
    return (
      <Typography
        component="span"
        role="img"
        aria-label={falseLabel}
        sx={{ color: alpha(PAGE_COLORS.muted, 0.38), fontWeight: 700, lineHeight: 1 }}
      >
        -
      </Typography>
    );
  }

  const Icon = tone === 'danger' ? CloseRoundedIcon : CheckRoundedIcon;
  return <Icon fontSize="small" titleAccess={trueLabel} sx={{ color }} />;
}

function normalizeFilters(filters = {}) {
  return Object.fromEntries(
    Object.entries({ ...DEFAULT_FILTERS, ...filters }).map(([key, value]) => {
      if (typeof value !== 'string') {
        return [key, value];
      }
      const trimmed = value.trim();
      return [key, key === 'version' ? parseVersionsInput(trimmed) : trimmed];
    }),
  );
}

function areFilterValuesEquivalent(left, right) {
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false;
    }
    return left.every((value, index) => value === right[index]);
  }
  return left === right;
}

function areFiltersEquivalent(left = {}, right = {}) {
  const normalizedLeft = normalizeFilters(left);
  const normalizedRight = normalizeFilters(right);
  return Object.keys({ ...normalizedLeft, ...normalizedRight }).every((key) => (
    areFilterValuesEquivalent(normalizedLeft[key], normalizedRight[key])
  ));
}

function hasBenchmarkPurgeScope(filters = {}) {
  return Boolean(String(filters.benchmarkRunId || '').trim() || String(filters.cohortId || '').trim());
}

function hasAdditionalBenchmarkPurgeFilters(filters = {}) {
  return BENCHMARK_PURGE_RESTRICTED_KEYS.some((key) => {
    const value = filters[key];
    if (value === null || value === undefined) {
      return false;
    }
    return typeof value === 'string' ? value.trim() !== '' : true;
  });
}

function countDeletedPurgeRows(result) {
  return ['deletedDetections', 'deletedEvents', 'deletedAnalyses', 'deletedBenchmarkExecutions', 'deletedBenchmarkRuns']
    .reduce((sum, key) => sum + (toNumber(result?.[key]) || 0), 0);
}

function findPurgeTable(result, tableName) {
  return (result?.tables || []).find((table) => table?.tableName === tableName) || null;
}

function countMatchedPurgeRows(result) {
  return (result?.tables || []).reduce((sum, table) => sum + (toNumber(table?.matchedRows) || 0), 0);
}

function resolvePurgeResultSeverity(result) {
  if (result?.dryRun) {
    return countMatchedPurgeRows(result) > 0 ? 'warning' : 'info';
  }
  return countDeletedPurgeRows(result) > 0 ? 'success' : 'info';
}

function buildDuplicateCleanupResultMessage(result) {
  if (!result) {
    return '';
  }

  const deletedDetections = toNumber(result.deletedDetections) || 0;
  const deletedAnalyses = toNumber(result.deletedAnalyses) || 0;
  const duplicateGroups = toNumber(result.duplicateGroups) || 0;
  const analysisDuplicateGroups = toNumber(result.analysisDuplicateGroups) || 0;
  const deletedEvents = toNumber(result.deletedEvents) || 0;
  const reattachedDetections = toNumber(result.reattachedDetections) || 0;
  const reattachedBenchmarkExecutions = toNumber(result.reattachedBenchmarkExecutions) || 0;
  const scannedDetections = toNumber(result.scannedDetections) || 0;
  const scannedAnalyses = toNumber(result.scannedAnalyses) || 0;

  if (deletedAnalyses > 0) {
    const parts = [];
    if (deletedDetections > 0 || duplicateGroups > 0) {
      parts.push(`${deletedDetections} doublon(s) de détection supprimé(s) sur ${duplicateGroups} groupe(s)`);
    }
    parts.push(`${deletedAnalyses} analyse(s) doublon supprimée(s) sur ${analysisDuplicateGroups} groupe(s)`);
    if (reattachedDetections > 0) {
      parts.push(`${reattachedDetections} détection(s) réaffectée(s)`);
    }
    if (reattachedBenchmarkExecutions > 0) {
      parts.push(`${reattachedBenchmarkExecutions} exécution(s) benchmark réaffectée(s)`);
    }
    return `${parts.join(', ')}, avec ${deletedEvents} événement(s) lié(s) supprimé(s).`;
  }

  if (deletedDetections > 0) {
    return `${deletedDetections} doublon(s) supprimé(s) sur ${duplicateGroups} groupe(s), avec ${deletedEvents} événement(s) lié(s) supprimé(s).`;
  }

  return `Aucun doublon à supprimer sur la cohorte courante (${scannedDetections} détection(s) et ${scannedAnalyses} analyse(s) analysée(s)).`;
}

function buildPurgeResultMessage(result) {
  if (!result) {
    return '';
  }

  const matchedDetections = toNumber(findPurgeTable(result, 'pattern_detection')?.matchedRows) || 0;
  const matchedEvents = toNumber(findPurgeTable(result, 'pattern_detection_event')?.matchedRows) || 0;
  const matchedAnalyses = toNumber(findPurgeTable(result, 'chart_pattern_analysis')?.matchedRows) || 0;
  const matchedBenchmarkExecutions = toNumber(findPurgeTable(result, 'pattern_scanner_benchmark_execution')?.matchedRows) || 0;
  const matchedBenchmarkRuns = toNumber(findPurgeTable(result, 'pattern_scanner_benchmark_run')?.matchedRows) || 0;
  const deletedDetections = toNumber(result.deletedDetections) || 0;
  const deletedEvents = toNumber(result.deletedEvents) || 0;
  const deletedAnalyses = toNumber(result.deletedAnalyses) || 0;
  const deletedBenchmarkExecutions = toNumber(result.deletedBenchmarkExecutions) || 0;
  const deletedBenchmarkRuns = toNumber(result.deletedBenchmarkRuns) || 0;

  if (result.dryRun) {
    if (result.completePurge) {
      if (countMatchedPurgeRows(result) === 0) {
        return 'Prévisualisation benchmark: aucune donnée benchmark à purger pour le run sélectionné.';
      }
      return `Prévisualisation benchmark: ${matchedBenchmarkRuns} run(s), ${matchedBenchmarkExecutions} exécution(s), ${matchedAnalyses} analyse(s), ${matchedDetections} détection(s) et ${matchedEvents} événement(s) seraient supprimé(s).`;
    }

    if (matchedDetections > 0 || matchedEvents > 0 || matchedAnalyses > 0) {
      return `Prévisualisation historique: ${matchedDetections} détection(s), ${matchedEvents} événement(s) et ${matchedAnalyses} analyse(s) seraient supprimé(s) sur la cohorte filtrée.`;
    }

    return `Prévisualisation historique: aucune donnée à purger sur la cohorte filtrée (${toNumber(result.scannedDetections) || 0} détection(s) trouvée(s)).`;
  }

  if (result.completePurge) {
    if (countDeletedPurgeRows(result) === 0) {
      return 'Aucune donnée benchmark à purger pour le run sélectionné.';
    }
    return `Purge benchmark complète: ${deletedBenchmarkRuns} run(s), ${deletedBenchmarkExecutions} exécution(s), ${deletedAnalyses} analyse(s), ${deletedDetections} détection(s) et ${deletedEvents} événement(s) supprimé(s).`;
  }

  if (deletedDetections > 0 || deletedEvents > 0 || deletedAnalyses > 0) {
    return `${deletedDetections} détection(s), ${deletedEvents} événement(s) et ${deletedAnalyses} analyse(s) supprimé(s) sur la cohorte filtrée.`;
  }

  return `Aucune donnée historique à purger sur la cohorte filtrée (${toNumber(result.scannedDetections) || 0} détection(s) trouvée(s)).`;
}

function buildHistoryCleanupPreviewNote(result) {
  const preview = result?.historyCleanupPreview;
  if (!preview || result?.completePurge) {
    return '';
  }

  const candidateAnalyses = toNumber(preview.candidateAnalyses) || 0;
  const deletableAnalyses = toNumber(preview.deletableAnalyses) || 0;
  const blockedAnalyses = toNumber(preview.blockedAnalyses) || 0;
  const deletedAnalyses = toNumber(result.deletedAnalyses);
  const effectiveDeletedAnalyses = Number.isFinite(deletedAnalyses) ? deletedAnalyses : deletableAnalyses;

  if (candidateAnalyses === 0) {
    return '';
  }

  if (result?.dryRun) {
    if (blockedAnalyses > 0) {
      return `Prévisualisation conservative: ${deletableAnalyses} analyse(s) suppressible(s) sur ${candidateAnalyses}; ${blockedAnalyses} resteraient conservées avec leur lineage actuel.`;
    }
    return `Prévisualisation conservative: ${deletableAnalyses} analyse(s) suppressible(s) sur ${candidateAnalyses}.`;
  }

  if (blockedAnalyses > 0) {
    return `Purge historique conservative: ${effectiveDeletedAnalyses} analyse(s) source supprimée(s) sur ${candidateAnalyses} candidate(s); ${blockedAnalyses} analyse(s) restent volontairement hors purge. Des détections peuvent donc rester visibles après rafraîchissement.`;
  }

  return `Purge historique conservative: ${effectiveDeletedAnalyses} analyse(s) source supprimée(s) sur ${candidateAnalyses} candidate(s); aucune analyse supplémentaire n a été conservée par le contrôle de lineage.`;
}

function formatHistoryCleanupClassificationLabel(row) {
  return `${formatValue(row?.code, 'UNKNOWN')}: ${toNumber(row?.count) || 0}`;
}

function countDeletedBenchmarkCleanupRows(result) {
  return ['deletedDetections', 'deletedEvents', 'deletedAnalyses', 'deletedExecutions', 'deletedRuns', 'deletedSnapshots']
    .reduce((sum, key) => sum + (toNumber(result?.summary?.[key]) || 0), 0);
}

function countMatchedBenchmarkCleanupRows(result) {
  return (result?.tables || []).reduce((sum, table) => sum + (toNumber(table?.matchedRows) || 0), 0);
}

function resolveBenchmarkCleanupSeverity(result) {
  if (result?.status === 'BLOCKED') {
    return 'warning';
  }
  if (result?.dryRun) {
    return countMatchedBenchmarkCleanupRows(result) > 0 ? 'warning' : 'info';
  }
  return countDeletedBenchmarkCleanupRows(result) > 0 ? 'success' : 'info';
}

function buildBenchmarkCleanupResultMessage(result) {
  if (!result) {
    return '';
  }

  const summary = result.summary || {};
  const deletedDetections = toNumber(summary.deletedDetections) || 0;
  const deletedEvents = toNumber(summary.deletedEvents) || 0;
  const deletedAnalyses = toNumber(summary.deletedAnalyses) || 0;
  const deletedExecutions = toNumber(summary.deletedExecutions) || 0;
  const deletedRuns = toNumber(summary.deletedRuns) || 0;
  const deletedSnapshots = toNumber(summary.deletedSnapshots) || 0;
  const detectionCandidates = toNumber(summary.detectionCandidates) || 0;
  const analysisCandidates = toNumber(summary.analysisCandidates) || 0;
  const benchmarkArtifactCandidates = toNumber(summary.benchmarkArtifactCandidates) || 0;
  const estimatedDeletions = toNumber(summary.estimatedDeletions) || 0;
  const blockedDeletes = toNumber(summary.blockedDeletes) || 0;
  const protectedRows = toNumber(summary.protectedRows) || 0;

  if (result.status === 'ALREADY_CLEAN') {
    return 'Cleanup benchmark: aucune donnée benchmark résiduelle à supprimer pour ce scope.';
  }

  if (result.status === 'BLOCKED') {
    return `Cleanup benchmark bloqué: ${blockedDeletes} suppression(s) restent bloquée(s) par ${protectedRows} ligne(s) protégée(s).`;
  }

  if (result.dryRun) {
    if (estimatedDeletions === 0) {
      return 'Dry-run cleanup benchmark: aucune suppression éligible sur ce run benchmark.';
    }
    return `Dry-run cleanup benchmark: ${detectionCandidates} détection(s), ${analysisCandidates} analyse(s) et ${benchmarkArtifactCandidates} artefact(s) benchmark, soit ${estimatedDeletions} suppression(s) estimée(s), seraient traités.`;
  }

  if (countDeletedBenchmarkCleanupRows(result) === 0) {
    return 'Cleanup benchmark exécuté: aucune donnée supplémentaire supprimée.';
  }

  return `Cleanup benchmark exécuté: ${deletedRuns} run(s), ${deletedExecutions} exécution(s), ${deletedAnalyses} analyse(s), ${deletedDetections} détection(s), ${deletedEvents} événement(s) et ${deletedSnapshots} snapshot(s) supprimé(s).`;
}

function buildBenchmarkCleanupSummaryNote(result) {
  const summary = result?.summary;
  if (!summary) {
    return '';
  }

  const parts = [];
  const duplicateRowsCandidates = toNumber(summary.duplicateRowsCandidates) || 0;
  const safeOrphanSnapshots = toNumber(summary.safeOrphanSnapshots) || 0;
  const protectedRows = toNumber(summary.protectedRows) || 0;
  const nonBenchmarkAnalysesProtected = toNumber(summary.nonBenchmarkAnalysesProtected) || 0;
  const ambiguousLineageProtected = toNumber(summary.ambiguousLineageProtected) || 0;
  const blockedDeletes = toNumber(summary.blockedDeletes) || 0;

  if (duplicateRowsCandidates > 0) {
    parts.push(`${duplicateRowsCandidates} doublon(s) benchmark déjà identifiés dans le périmètre`);
  }
  if (safeOrphanSnapshots > 0) {
    parts.push(`${safeOrphanSnapshots} snapshot(s) orphelin(s) supprimable(s) sans risque métier`);
  }
  if (protectedRows > 0) {
    parts.push(`${protectedRows} ligne(s) restent protégée(s)`);
  }
  if (nonBenchmarkAnalysesProtected > 0) {
    parts.push(`${nonBenchmarkAnalysesProtected} analyse(s) non-benchmark restent protégée(s)`);
  }
  if (ambiguousLineageProtected > 0) {
    parts.push(`${ambiguousLineageProtected} analyse(s) benchmark-owned restent protégée(s) par lineage ambigu`);
  }
  if (blockedDeletes > 0) {
    parts.push(`${blockedDeletes} suppression(s) restent bloquée(s)`);
  }
  if (!parts.length) {
    return result?.dryRun
      ? 'Prévisualisation uniquement: aucune suppression n a été exécutée et les compteurs proviennent du comptage transactionnel backend.'
      : 'Vérification post-cleanup: les compteurs restants proviennent d un recompte backend immédiatement après suppression.';
  }
  return parts.join(' | ');
}

function buildBenchmarkRebuildResultMessage(result) {
  if (!result) {
    return '';
  }
  const refreshedAnalyses = toNumber(result.refreshedAnalyses) || 0;
  if (result.status === 'SKIPPED') {
    return result.message || 'Reconstruction benchmark ignorée.';
  }
  return `${refreshedAnalyses} analyse(s) relancée(s) pour la reconstruction benchmark (${result.exchange || 'n/a'} ${result.timeframe || 'n/a'}).`;
}

function formatPurgeTableSummary(table, dryRun = false) {
  const matchedRows = toNumber(table?.matchedRows) || 0;
  const deletedRows = toNumber(table?.deletedRows) || 0;
  const remainingRows = toNumber(table?.remainingRows) || 0;
  if (dryRun) {
    if (matchedRows === 0 && remainingRows === 0) {
      return `${table?.tableName}: aucune ligne ciblée`;
    }
    return `${table?.tableName}: ${matchedRows} ciblé(s), ${remainingRows} présent(s)`;
  }
  if (remainingRows > 0) {
    return `${table?.tableName}: ${deletedRows}/${matchedRows} supprimé(s), ${remainingRows} restant(s)`;
  }
  return `${table?.tableName}: ${deletedRows}/${matchedRows} supprimé(s)`;
}

function bucketOrder(label) {
  if (!label) {
    return 10_000;
  }
  if (label === 'UNKNOWN') {
    return 9_999;
  }
  if (label === '<50') {
    return 0;
  }
  const match = String(label).match(/(\d+)/);
  return match ? Number(match[1]) : 5_000;
}

function orderBucketLabels(labels = []) {
  return [...new Set((labels || []).filter(Boolean))].sort((left, right) => {
    const diff = bucketOrder(left) - bucketOrder(right);
    return diff !== 0 ? diff : String(left).localeCompare(String(right));
  });
}

function calibrationFallbackFromBuckets(rows = []) {
  return (rows || []).map((row) => ({
    bucket: row.bucket || row.label,
    minValue: row.minValue ?? row.minConfidence ?? null,
    maxValue: row.maxValue ?? row.maxConfidence ?? null,
    sampleSize: row.sampleSize ?? row.detections ?? 0,
    breakoutRate: row.breakoutRate ?? 0,
    targetRate: row.targetRate ?? row.targetFinalRate ?? 0,
    invalidationRate: row.invalidationRate ?? 0,
    medianTimeToTargetSeconds: row.medianTimeToTargetSeconds ?? row.timeToTargetFinal?.medianSeconds ?? null,
    lowSample: Boolean(row.lowSample),
  }));
}

function matrixCellBackground(targetRate, lowSample) {
  const normalized = normalizeRatio(targetRate);
  if (normalized === null) {
    return alpha(PAGE_COLORS.ink, 0.05);
  }
  return alpha(lowSample ? PAGE_COLORS.warning : PAGE_COLORS.inkSoft, 0.14 + clamp(normalized, 0, 1) * 0.56);
}

function LowSampleChip({ lowSample, threshold }) {
  if (!lowSample) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={`Echantillon < ${threshold}`}
      sx={{
        height: 24,
        bgcolor: alpha(PAGE_COLORS.warning, 0.12),
        color: PAGE_COLORS.warning,
        fontWeight: 700,
      }}
    />
  );
}

function MetricCard({ label, value, helper, tone = 'default' }) {
  const toneMap = {
    default: { bg: '#fff', fg: PAGE_COLORS.ink },
    accent: { bg: alpha(PAGE_COLORS.accent, 0.08), fg: PAGE_COLORS.accent },
    success: { bg: alpha(PAGE_COLORS.success, 0.1), fg: PAGE_COLORS.success },
    warning: { bg: alpha(PAGE_COLORS.warning, 0.1), fg: PAGE_COLORS.warning },
    danger: { bg: alpha(PAGE_COLORS.danger, 0.1), fg: PAGE_COLORS.danger },
  };
  const palette = toneMap[tone] || toneMap.default;

  return (
    <Paper
      sx={{
        p: 2.2,
        borderRadius: 4,
        background: palette.bg,
        border: `1px solid ${alpha(palette.fg, 0.12)}`,
      }}
    >
      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, color: palette.fg, mt: 0.6 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 0.8 }}>
          {helper}
        </Typography>
      ) : null}
    </Paper>
  );
}

function SectionCard({ icon, title, description, children, action }) {
  return (
    <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: `1px solid ${alpha(PAGE_COLORS.ink, 0.08)}` }}>
      <Stack spacing={2.1}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Stack direction="row" spacing={1.1} alignItems="center">
            {icon}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                {title}
              </Typography>
              {description ? (
                <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 0.35 }}>
                  {description}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          {action || null}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

function BenchmarkOrchestrationNotice() {
  return (
    <Alert severity="info" sx={{ borderRadius: 3 }}>
      V4.4 n est pas une ligne moteur dans ce benchmark cible. Les lignes ci-dessous comparent uniquement les versions de detection; V4.4 designe la couche d orchestration benchmark.
    </Alert>
  );
}

function RateBar({ label, value, color }) {
  const normalized = clamp(normalizeRatio(value) || 0, 0, 1);

  return (
    <Stack spacing={0.6}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 800, color }}>{formatPercent(value)}</Typography>
      </Stack>
      <Box sx={{ height: 10, borderRadius: 999, overflow: 'hidden', bgcolor: alpha(color, 0.12) }}>
        <LinearProgress
          variant="determinate"
          value={normalized * 100}
          sx={{
            height: '100%',
            bgcolor: alpha(color, 0.08),
            '& .MuiLinearProgress-bar': {
              bgcolor: color,
            },
          }}
        />
      </Box>
    </Stack>
  );
}

function BucketMetricsTable({ rows, lowSampleThreshold, firstLabel = 'Bucket' }) {
  if (!rows?.length) {
    return <Alert severity="info">Aucune ligne ne correspond aux filtres actifs.</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>{firstLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.bucket || row.label}-${row.minValue ?? row.minConfidence ?? 'na'}`} hover>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {row.bucket || row.label}
                  </Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
              <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
              <TableCell>{formatDuration(row.timeToTargetFinal?.medianSeconds || row.medianTimeToTargetSeconds)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function GroupedMetricsTable({ rows, groupLabel, lowSampleThreshold }) {
  if (!rows?.length) {
    return <Alert severity="info">Aucune agrégation n est disponible pour cette dimension.</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>{groupLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Bucket</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.group}-${row.bucket}`} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {row.group}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2">{row.bucket}</Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
              <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
              <TableCell>{formatDuration(row.timeToTargetFinal?.medianSeconds)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function V3AggregateTable({ rows, groupLabel, lowSampleThreshold, emptyMessage }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>{groupLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Snapshots V3</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Couverture</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Qualité pattern</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Qualité breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Biais directionnel</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Confiance setup</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Rendu verrouillé</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.group} hover>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {row.group}
                  </Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{row.snapshotCount}</TableCell>
              <TableCell>{formatPercent(row.coverageRate)}</TableCell>
              <TableCell>{formatScore(row.averagePatternQualityScore, 1)}</TableCell>
              <TableCell>{formatScore(row.averageBreakoutQualityScore, 1)}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {formatV3AggregateBiasValue(row)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                  {formatV3AggregateBiasHelper(row)}
                </Typography>
              </TableCell>
              <TableCell>{formatScore(row.averageSetupConfidenceScore, 1)}</TableCell>
              <TableCell>{formatPercent(row.lockedRenderingRate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RecentDetectionsTable({ rows, onOpen, emptyMessage }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Actif</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Pattern</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Score</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Score MTF</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Alignement</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Statut</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="center">Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="center">Invalidation</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="center">Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Détection</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const breakout = BREAKOUT_OUTCOME_STATUSES.has(row.status);
            const invalidation = row.status === 'INVALIDATED';
            const target = TARGET_OUTCOME_STATUSES.has(row.status);

            return (
              <TableRow key={row.id} hover>
                <TableCell>{row.symbol} / {row.exchange} / {row.timeframe}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.pattern}</Typography>
                  <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{formatValue(row.patternFamily)}</Typography>
                  <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                    Biais {formatDirectionLabel(row.direction)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatValue(row.engineVersion)}</Typography>
                  <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{formatValue(row.engineBuild)}</Typography>
                </TableCell>
                <TableCell>{formatScore(row.patternScore)}</TableCell>
                <TableCell>{formatScore(row.mtfScore)}</TableCell>
                <TableCell>
                  <Typography variant="body2">{formatValue(row.mtfAlignment, 'UNKNOWN')}</Typography>
                  <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>Conviction {formatScore(row.convictionScore)}</Typography>
                </TableCell>
                <TableCell>{formatValue(row.status)}</TableCell>
                <TableCell align="center">
                  <OutcomeFlag active={breakout} trueLabel="Breakout: oui" falseLabel="Breakout: non" />
                </TableCell>
                <TableCell align="center">
                  <OutcomeFlag active={invalidation} trueLabel="Invalidation: oui" falseLabel="Invalidation: non" tone="danger" />
                </TableCell>
                <TableCell align="center">
                  <OutcomeFlag active={target} trueLabel="Target: oui" falseLabel="Target: non" />
                </TableCell>
                <TableCell>{formatDateTime(row.detectionCandleAt)}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" onClick={() => onOpen(row.id)}>
                    Timeline
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function formatDiagnosticRatePair(allRate, resolvedRate, available = true) {
  if (!available) {
    return 'n/a';
  }
  return `${formatPercent(allRate)} all / ${formatPercent(resolvedRate)} resolved`;
}

function formatDiagnosticPercent(value, available = true) {
  if (!available) {
    return 'n/a';
  }
  return formatPercent(value);
}

function resolveBenchmarkSeverity(status) {
  switch (String(status || '').toUpperCase()) {
    case 'VALID_COHORT':
    case 'VALID':
    case 'COMPLETE_COHORT':
    case 'COMPARABLE':
      return 'success';
    case 'UNAVAILABLE':
    case 'PARTIAL_COHORT':
      return 'warning';
    case 'COHORT_INCONSISTENT':
    case 'HISTORICAL_CONTAMINATION':
    case 'INVALID_COHORT':
    case 'INSUFFICIENT_DATA':
      return 'error';
    default:
      return 'info';
  }
}

function resolveBenchmarkTone(status) {
  switch (String(status || '').toUpperCase()) {
    case 'VALID_COHORT':
    case 'VALID':
    case 'COMPLETE_COHORT':
    case 'COMPARABLE':
      return 'success';
    case 'UNAVAILABLE':
    case 'PARTIAL_COHORT':
      return 'warning';
    case 'COHORT_INCONSISTENT':
    case 'HISTORICAL_CONTAMINATION':
    case 'INVALID_COHORT':
    case 'INSUFFICIENT_DATA':
      return 'danger';
    default:
      return 'accent';
  }
}

function resolveCoverageTone(value) {
  const normalized = normalizeRatio(value);
  if (normalized === null) {
    return 'default';
  }
  if (normalized >= 1) {
    return 'success';
  }
  if (normalized <= 0) {
    return 'danger';
  }
  return 'warning';
}

function formatSnapshotClassification(value, versionLabels = DEFAULT_DIAGNOSTIC_VERSION_LABELS) {
  const normalized = String(value || '').toUpperCase();
  switch (normalized) {
    case 'BOTH_AGREE':
      return 'Accord';
    case 'BOTH_DIFFER':
      return 'Différence';
    case 'LEFT_ONLY':
    case 'V3_ONLY':
      return versionLabels.leftOnlyLabel;
    case 'RIGHT_ONLY':
    case 'V4_ONLY':
      return versionLabels.rightOnlyLabel;
    default:
      if (normalized === `${String(versionLabels.leftTag || '').toUpperCase()}_ONLY`) {
        return versionLabels.leftOnlyLabel;
      }
      if (normalized === `${String(versionLabels.rightTag || '').toUpperCase()}_ONLY`) {
        return versionLabels.rightOnlyLabel;
      }
      return formatValue(value);
  }
}

function resolveIntegrityTone(invariant) {
  if (!invariant) {
    return 'default';
  }
  if (invariant.passed) {
    return 'success';
  }
  return invariant.blocking ? 'danger' : 'warning';
}

function formatBenchmarkScopeHelper(integrity) {
  if (!integrity) {
    return 'n/a';
  }
  const parts = [];
  if (integrity.benchmarkRunId) {
    parts.push(`Run ${integrity.benchmarkRunId}`);
  }
  if (integrity.cohortId) {
    parts.push(`Cohort ${integrity.cohortId}`);
  }
  return parts.length ? parts.join(' | ') : formatValue(integrity.populationDefinition, 'n/a');
}

function formatExecutionPopulationHelper(summary, fallback = 'n/a') {
  if (!summary) {
    return fallback;
  }
  return `Analyses ${summary.analysesPersistedCount || 0} | Zero ${summary.zeroDetectionCount || 0} | Persisted ${summary.detectionPersistedCount || 0}`;
}

function formatIntegrityReasons(reasons) {
  return Array.isArray(reasons) && reasons.length ? reasons.join(', ') : 'None';
}

function formatVersionCountMap(counts, fallback = 'n/a') {
  if (!counts || typeof counts !== 'object') {
    return fallback;
  }
  const entries = Object.entries(counts);
  if (!entries.length) {
    return fallback;
  }
  return entries.map(([version, count]) => `${formatVersionTag(version)} ${count || 0}`).join(' | ');
}

function formatEligibilityReasonSummary(reasons) {
  return Array.isArray(reasons) && reasons.length
    ? reasons.map((row) => `${formatValue(row.reason, 'n/a')} ${row.count || 0}`).join(' | ')
    : 'None';
}

function formatEligibilityReasons(reasons) {
  return Array.isArray(reasons) && reasons.length ? reasons.join(', ') : 'None';
}

function DiagnosticVersionTable({ rowsByVersion, versions, emptyMessage, comparisonAvailable = true }) {
  if (!versions?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Resolved</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Unresolved</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Score moyen</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>MTF moyen</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Confiance moyenne</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {versions.map((version) => {
            const row = rowsByVersion?.[version];
            if (!row) {
              return null;
            }
            return (
              <TableRow key={version} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {String(version).toUpperCase()}
                  </Typography>
                </TableCell>
                <TableCell>{row.detections}</TableCell>
                <TableCell>{row.resolvedDetections}</TableCell>
                <TableCell>{row.unresolvedDetections}</TableCell>
                <TableCell>{formatDiagnosticRatePair(row.breakoutRateAll, row.breakoutRateResolved, comparisonAvailable)}</TableCell>
                <TableCell>{formatDiagnosticRatePair(row.targetRateAll, row.targetRateResolved, comparisonAvailable)}</TableCell>
                <TableCell>{formatDiagnosticRatePair(row.invalidationRateAll, row.invalidationRateResolved, comparisonAvailable)}</TableCell>
                <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
                <TableCell>{formatScore(row.averageMtfScore, 1)}</TableCell>
                <TableCell>{formatPercent(row.averageConfidence)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DiagnosticSegmentTable({ rows, versions, emptyMessage, comparisonAvailable = true }) {
  if (!rows?.length || !versions?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  const leftVersion = versions[0];
  const rightVersion = versions[1];

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Segment</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>{String(leftVersion).toUpperCase()} détections</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>{String(rightVersion).toUpperCase()} détections</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout {String(leftVersion).toUpperCase()}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout {String(rightVersion).toUpperCase()}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target {String(leftVersion).toUpperCase()}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target {String(rightVersion).toUpperCase()}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation {String(leftVersion).toUpperCase()}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation {String(rightVersion).toUpperCase()}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Unresolved {String(leftVersion).toUpperCase()}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Unresolved {String(rightVersion).toUpperCase()}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const left = row.versions?.[leftVersion];
            const right = row.versions?.[rightVersion];
            return (
              <TableRow key={row.segment} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {row.segment}
                  </Typography>
                </TableCell>
                <TableCell>{left?.detections ?? 0}</TableCell>
                <TableCell>{right?.detections ?? 0}</TableCell>
                <TableCell>{formatDiagnosticPercent(left?.breakoutRateAll, comparisonAvailable)}</TableCell>
                <TableCell>{formatDiagnosticPercent(right?.breakoutRateAll, comparisonAvailable)}</TableCell>
                <TableCell>{formatDiagnosticPercent(left?.targetRateAll, comparisonAvailable)}</TableCell>
                <TableCell>{formatDiagnosticPercent(right?.targetRateAll, comparisonAvailable)}</TableCell>
                <TableCell>{formatDiagnosticPercent(left?.invalidationRateAll, comparisonAvailable)}</TableCell>
                <TableCell>{formatDiagnosticPercent(right?.invalidationRateAll, comparisonAvailable)}</TableCell>
                <TableCell>{formatDiagnosticPercent(left?.unresolvedRateAll, comparisonAvailable)}</TableCell>
                <TableCell>{formatDiagnosticPercent(right?.unresolvedRateAll, comparisonAvailable)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DiagnosticSnapshotDiffTable({ rows, emptyMessage, versionLabels = DEFAULT_DIAGNOSTIC_VERSION_LABELS }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Classification</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Market snapshot</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>{versionLabels.leftTag}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>{versionLabels.rightTag}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.symbol}-${row.exchange}-${row.timeframe}-${row.detectionCandleAt}`} hover>
              <TableCell>
                <Chip
                  size="small"
                  label={formatSnapshotClassification(row.classification, versionLabels)}
                  sx={{
                    fontWeight: 700,
                    bgcolor: row.classification === 'BOTH_DIFFER'
                      ? alpha(PAGE_COLORS.warning, 0.14)
                      : row.classification === 'BOTH_AGREE'
                        ? alpha(PAGE_COLORS.success, 0.14)
                        : alpha(PAGE_COLORS.ink, 0.08),
                    color: row.classification === 'BOTH_DIFFER' ? PAGE_COLORS.warning : row.classification === 'BOTH_AGREE' ? PAGE_COLORS.success : PAGE_COLORS.ink,
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.symbol} / {row.exchange} / {row.timeframe}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{formatDateTime(row.detectionCandleAt)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatValue(row.leftPattern)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Score {formatScore(row.leftPatternScore)} • MTF {formatScore(row.leftMtfScore)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Conf {formatPercentValue(row.leftConfidence, 0)} • Geo {formatPercentValue(row.leftGeometryConfidence, 0)} • Struct {formatPercentValue(row.leftStructureQuality, 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Fakeout {formatValue(row.leftFakeoutRisk, 'n/a')} • Target conf. {formatPercentValue(row.leftTargetConfidence, 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Qualif. {formatValue(row.leftQualificationReason, 'unavailable')}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  {formatValue(row.leftStatus)} • B/O {formatDecimal(row.leftBreakout, 4)} • T {formatDecimal(row.leftTarget, 4)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatValue(row.rightPattern)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Score {formatScore(row.rightPatternScore)} • MTF {formatScore(row.rightMtfScore)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Conf {formatPercentValue(row.rightConfidence, 0)} • Geo {formatPercentValue(row.rightGeometryConfidence, 0)} • Struct {formatPercentValue(row.rightStructureQuality, 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Fakeout {formatValue(row.rightFakeoutRisk, 'n/a')} • Target conf. {formatPercentValue(row.rightTargetConfidence, 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Qualif. {formatValue(row.rightQualificationReason, 'unavailable')}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  {formatValue(row.rightStatus)} • B/O {formatDecimal(row.rightBreakout, 4)} • T {formatDecimal(row.rightTarget, 4)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LevelAuditTable({ rows, emptyMessage }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Détection</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Référence</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR stocké</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR calculé</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Distance breakout %</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Fakeout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Sources</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.engineVersion}-${row.symbol}-${row.timeframe}-${row.detectionCandleAt}`} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.symbol} / {row.exchange} / {row.timeframe}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  {formatValue(row.pattern)} • {formatValue(row.status)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  {formatDateTime(row.detectionCandleAt)}
                </Typography>
              </TableCell>
              <TableCell>{String(row.engineVersion || '').toUpperCase()}</TableCell>
              <TableCell>{formatDecimal(row.referencePrice, 4)}</TableCell>
              <TableCell>{formatDecimal(row.breakoutPrice, 4)}</TableCell>
              <TableCell>{formatDecimal(row.targetPrice, 4)}</TableCell>
              <TableCell>{formatDecimal(row.invalidationPrice, 4)}</TableCell>
              <TableCell>{formatDecimal(row.storedRiskRewardRatio, 4)}</TableCell>
              <TableCell>{formatDecimal(row.computedRiskRewardRatio, 4)}</TableCell>
              <TableCell>{formatPercentValue(row.breakoutDistancePercent, 2)}</TableCell>
              <TableCell>{formatValue(row.fakeoutRisk, 'n/a')}</TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>B/O {formatValue(row.breakoutSource)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>T {formatValue(row.targetSource)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Inv {formatValue(row.invalidationSource)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Ref {formatValue(row.referenceSource)}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CategoryCountTable({ rows, emptyMessage }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Segment</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Cas</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.segment} hover>
              <TableCell>{row.segment}</TableCell>
              <TableCell>{row.detections}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DuplicateCohortTable({ rows, emptyMessage }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Cohorte</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.engineVersion}-${row.symbol}-${row.exchange}-${row.timeframe}-${row.detectionCandleAt}`} hover>
              <TableCell>{String(row.engineVersion || '').toUpperCase()}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {row.symbol} / {row.exchange} / {row.timeframe}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                  {formatDateTime(row.detectionCandleAt)}
                </Typography>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DuplicateAnalysisKeyTable({ rows, emptyMessage }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Clé canonique</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Execution</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Analyses</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Hashes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.engineVersion}-${row.symbol}-${row.exchange}-${row.timeframe}-${row.closedCandleTimestamp}`} hover>
              <TableCell>{String(row.engineVersion || '').toUpperCase()}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {row.cohortKey || `${row.symbol} / ${row.exchange} / ${row.timeframe}`}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Closed candle: {formatDateTime(row.closedCandleTimestamp)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Run: {formatValue(row.benchmarkRunId, 'n/a')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {formatValue(row.executionStatus)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Execution ID: {formatValue(row.executionId, 'n/a')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {row.analyses || 0} total / {row.extraAnalyses || 0} extra
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Cause: {formatValue(row.cause, 'n/a')}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Reference: {formatValue(row.referenceAnalysisId, 'n/a')}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Extra IDs: {(row.extraAnalysisIds || []).join(', ') || 'n/a'}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  IDs: {(row.analysisIds || []).join(', ') || 'n/a'}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Created: {(row.createdAtValues || []).map((value) => formatDateTime(value)).join(' | ') || 'n/a'}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Updated: {(row.updatedAtValues || []).map((value) => formatDateTime(value)).join(' | ') || 'n/a'}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Sources: {(row.analysisSources || []).join(', ') || 'n/a'}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Patterns: {(row.patterns || []).join(', ') || 'n/a'}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Signals: {(row.signals || []).join(', ') || 'n/a'}
                </Typography>
                {(row.rows || []).map((analysisRow, index) => (
                  <Typography
                    key={`${analysisRow.analysisId || 'duplicate-row'}-${index}`}
                    variant="caption"
                    sx={{ color: PAGE_COLORS.muted, display: 'block' }}
                  >
                    {analysisRow.rowRole || 'ROW'} / {analysisRow.scopeMembership || 'UNKNOWN'} / {analysisRow.pattern || 'n/a'} / {formatDateTime(analysisRow.createdAt)} / {formatValue(analysisRow.analysisId, 'n/a')}
                  </Typography>
                ))}
              </TableCell>
              <TableCell>{(row.inputSnapshotHashes || []).join(', ') || 'n/a'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ExecutionIssueTable({ rows, emptyMessage }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Version</TableCell>
            <TableCell>Cohorte</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Timings</TableCell>
            <TableCell>Analysis</TableCell>
            <TableCell>Reason</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.executionId || `${row.engineVersion || 'unknown'}-${row.cohortKey || index}`} hover>
              <TableCell sx={{ fontWeight: 700 }}>{formatValue(row.engineVersion, 'n/a').toUpperCase()}</TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {row.cohortKey || `${formatValue(row.symbol)} / ${formatValue(row.exchange)} / ${formatValue(row.timeframe)}`}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Closed candle: {formatDateTime(row.closedCandleTimestamp)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Hash: {formatValue(row.inputSnapshotHash)}
                </Typography>
              </TableCell>
              <TableCell>{formatValue(row.status)}</TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Attempts: {formatValue(row.attemptCount)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Queued: {formatDateTime(row.queuedAt)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Dispatched: {formatDateTime(row.dispatchedAt)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Started: {formatDateTime(row.startedAt)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Finished: {formatDateTime(row.finishedAt)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Wait: {formatValue(row.queueWaitMs)} ms / Duration: {formatValue(row.lastDurationMs)} ms
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Engine: {formatMilliseconds(row.engineDurationMs)} / Persistence: {formatMilliseconds(row.persistenceDurationMs)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Run: {formatValue(row.benchmarkRunId, 'n/a')}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Execution ID: {formatValue(row.executionId, 'n/a')}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  Analysis ID: {formatValue(row.analysisId, 'n/a')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: PAGE_COLORS.ink }}>
                  {formatValue(row.failureMessage || row.notExecutedReason)}
                </Typography>
                {(row.stateUpdateConflictCount || row.stateUpdateRetryCount || row.stateUpdateFailureCount || row.stateUpdateFailureMessage) ? (
                  <>
                    <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                      State update: conflicts {row.stateUpdateConflictCount || 0} / retries {row.stateUpdateRetryCount || 0} / failures {row.stateUpdateFailureCount || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                      Update error: {formatValue(row.stateUpdateFailureMessage)}
                    </Typography>
                  </>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function formatPopulationLabel(value, versionLabels = DEFAULT_DIAGNOSTIC_VERSION_LABELS) {
  switch (String(value || '').toUpperCase()) {
    case 'LEFT_ALL':
      return versionLabels.allLeftLabel;
    case 'LEFT_BOTH':
      return versionLabels.leftBothLabel;
    case 'LEFT_ONLY':
      return versionLabels.leftOnlyLabel;
    case 'COMMON':
      return versionLabels.commonLabel;
    case 'RIGHT_BOTH':
      return versionLabels.rightBothLabel;
    case 'RIGHT_ALL':
      return versionLabels.allRightLabel;
    case 'RIGHT_ONLY':
      return versionLabels.rightOnlyLabel;
    case 'V3_ONLY':
      return versionLabels.leftOnlyLabel;
    case 'V4_ONLY':
      return versionLabels.rightOnlyLabel;
    case 'ALL_V4':
      return versionLabels.allRightLabel;
    case 'RECTANGLE':
      return 'Rectangle';
    default:
      return formatValue(value);
  }
}

function formatBooleanLabel(value, trueLabel = 'Oui', falseLabel = 'Non', fallback = 'n/a') {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return value ? trueLabel : falseLabel;
}

function summarizeCategoryCounts(rows, limit = 3) {
  if (!rows?.length) {
    return 'n/a';
  }
  return rows.slice(0, limit).map((row) => `${row.segment} (${row.detections})`).join(' • ');
}

function PatternExpansionTable({ rows, lowSampleThreshold, versionLabels = DEFAULT_DIAGNOSTIC_VERSION_LABELS, rightFamilyVersions = [] }) {
  if (!rows?.length) {
    return <Alert severity="info">Aucune expansion par pattern n est disponible.</Alert>;
  }

  const familyVersions = Array.isArray(rightFamilyVersions) && rightFamilyVersions.length
    ? rightFamilyVersions
    : [versionLabels.rightVersion];

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Pattern</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>{versionLabels.leftTag}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Common</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>{versionLabels.rightOnlyLabel}</TableCell>
            {familyVersions.map((familyVersion) => (
              <TableCell key={familyVersion} sx={{ fontWeight: 800 }}>{formatVersionTag(familyVersion)}</TableCell>
            ))}
            <TableCell sx={{ fontWeight: 800 }}>{versionLabels.rightOnlyLabel} %</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Score {versionLabels.rightOnlyLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>MTF {versionLabels.rightOnlyLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Confidence {versionLabels.rightOnlyLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout {versionLabels.rightOnlyLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target {versionLabels.rightOnlyLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation {versionLabels.rightOnlyLabel}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.pattern} hover>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {row.pattern}
                  </Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                  {row.highVolumeLowBreakout ? (
                    <Chip size="small" label="Low breakout" sx={{ fontWeight: 700, bgcolor: alpha(PAGE_COLORS.warning, 0.12), color: PAGE_COLORS.warning }} />
                  ) : null}
                </Stack>
              </TableCell>
              <TableCell>{row.v3Detections}</TableCell>
              <TableCell>{row.commonDetections}</TableCell>
              <TableCell>{row.v4OnlyDetections}</TableCell>
              {familyVersions.map((familyVersion) => (
                <TableCell key={`${row.pattern}-${familyVersion}`}>{row.rightFamilyDetections?.[familyVersion] ?? 0}</TableCell>
              ))}
              <TableCell>{formatPercent(row.v4OnlyShare)}</TableCell>
              <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
              <TableCell>{formatScore(row.averageMtfScore, 1)}</TableCell>
              <TableCell>{formatPercent(row.averageConfidence)}</TableCell>
              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
              <TableCell>{formatPercent(row.targetRate)}</TableCell>
              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PopulationSegmentPerformanceTable({ rows, emptyMessage, firstLabel = 'Segment', lowSampleThreshold }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>{firstLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>N</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Score</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>MTF</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Confidence</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Unresolved</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR median</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR mean</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR ~ 0.9259</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.segment} hover>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {row.segment}
                  </Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
              <TableCell>{formatScore(row.averageMtfScore, 1)}</TableCell>
              <TableCell>{formatPercent(row.averageConfidence)}</TableCell>
              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
              <TableCell>{formatPercent(row.targetRate)}</TableCell>
              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
              <TableCell>{formatPercent(row.unresolvedRate)}</TableCell>
              <TableCell>{formatDecimal(row.medianComputedRiskReward, 2)}</TableCell>
              <TableCell>{formatDecimal(row.averageComputedRiskReward, 2)}</TableCell>
              <TableCell>{formatPercent(row.rrAnomalyRate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PopulationExchangeTable({ rows, emptyMessage, lowSampleThreshold }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Exchange</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>N</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Pattern distribution</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.exchange} hover>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {row.exchange}
                  </Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{summarizeCategoryCounts(row.patternDistribution)}</TableCell>
              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
              <TableCell>{formatPercent(row.targetRate)}</TableCell>
              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PopulationSymbolTable({ rows, emptyMessage, lowSampleThreshold }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Symbol</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>N</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Dominant pattern</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Score</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>MTF</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Confidence</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.symbol} hover>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {row.symbol}
                  </Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{formatValue(row.dominantPattern)}</TableCell>
              <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
              <TableCell>{formatScore(row.averageMtfScore, 1)}</TableCell>
              <TableCell>{formatPercent(row.averageConfidence)}</TableCell>
              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BucketPerformanceTable({ rows, emptyMessage, lowSampleThreshold }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Bucket</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>N</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Unresolved</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.bucket} hover>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {row.bucket}
                  </Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
              <TableCell>{formatPercent(row.targetRate)}</TableCell>
              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
              <TableCell>{formatPercent(row.unresolvedRate)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DiagnosticScoreMtfMatrix({ cells, lowSampleThreshold, emptyMessage }) {
  const scoreBuckets = useMemo(() => orderBucketLabels((cells || []).map((cell) => cell.scoreBucket)), [cells]);
  const mtfBuckets = useMemo(() => orderBucketLabels((cells || []).map((cell) => cell.mtfBucket)), [cells]);
  const lookup = useMemo(
    () => new Map((cells || []).map((cell) => [`${cell.scoreBucket}__${cell.mtfBucket}`, cell])),
    [cells],
  );

  if (!cells?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 720, display: 'grid', gridTemplateColumns: `180px repeat(${mtfBuckets.length}, minmax(132px, 1fr))`, gap: 1 }}>
        <Box sx={{ p: 1.2 }} />
        {mtfBuckets.map((bucket) => (
          <Paper key={bucket} sx={{ p: 1.2, borderRadius: 3, bgcolor: PAGE_COLORS.sand, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              MTF
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{bucket}</Typography>
          </Paper>
        ))}
        {scoreBuckets.map((scoreBucket) => (
          <React.Fragment key={scoreBucket}>
            <Paper sx={{ p: 1.4, borderRadius: 3, bgcolor: PAGE_COLORS.sand }}>
              <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Score
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{scoreBucket}</Typography>
            </Paper>
            {mtfBuckets.map((mtfBucket) => {
              const cell = lookup.get(`${scoreBucket}__${mtfBucket}`);
              return (
                <Paper
                  key={`${scoreBucket}-${mtfBucket}`}
                  sx={{
                    p: 1.35,
                    borderRadius: 3,
                    minHeight: 118,
                    bgcolor: matrixCellBackground(cell?.targetRate, cell?.lowSample),
                    border: `1px solid ${alpha(cell?.lowSample ? PAGE_COLORS.warning : PAGE_COLORS.ink, 0.12)}`,
                  }}
                >
                  {!cell ? (
                    <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>n/a</Typography>
                  ) : (
                    <Stack spacing={0.6}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                          {formatPercent(cell.targetRate)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                          n={cell.detections}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                        Breakout {formatPercent(cell.breakoutRate)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                        Invalid. {formatPercent(cell.invalidationRate)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                        Unresolved {formatPercent(cell.unresolvedRate)}
                      </Typography>
                      <LowSampleChip lowSample={cell.lowSample} threshold={lowSampleThreshold} />
                    </Stack>
                  )}
                </Paper>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

function PopulationRrSummaryTable({ rows, emptyMessage, lowSampleThreshold, versionLabels = DEFAULT_DIAGNOSTIC_VERSION_LABELS }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Segment</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>N</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR calculable</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR ~ 0.9259</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR anomaly %</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR median</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR mean</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR min</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR max</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.segment} hover>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {formatPopulationLabel(row.segment, versionLabels)}
                  </Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{row.calculableRrCount}</TableCell>
              <TableCell>{row.rrAnomalyCount}</TableCell>
              <TableCell>{formatPercent(row.rrAnomalyRate)}</TableCell>
              <TableCell>{formatDecimal(row.medianComputedRiskReward, 2)}</TableCell>
              <TableCell>{formatDecimal(row.averageComputedRiskReward, 2)}</TableCell>
              <TableCell>{formatDecimal(row.minComputedRiskReward, 2)}</TableCell>
              <TableCell>{formatDecimal(row.maxComputedRiskReward, 2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PotentialCalibrationIssueTable({ rows, emptyMessage, showPopulation = false, versionLabels = DEFAULT_DIAGNOSTIC_VERSION_LABELS }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {showPopulation ? <TableCell sx={{ fontWeight: 800 }}>Population</TableCell> : null}
            <TableCell sx={{ fontWeight: 800 }}>Détection</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Pattern</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Score</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>MTF</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Confidence</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target conf.</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Geometry</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Structure</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Volume</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Fakeout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR stocké</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>RR calculé</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.population}-${row.symbol}-${row.exchange}-${row.timeframe}-${row.detectionCandleAt}-${row.pattern}`} hover>
              {showPopulation ? <TableCell>{formatPopulationLabel(row.population, versionLabels)}</TableCell> : null}
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {row.symbol} / {row.exchange} / {row.timeframe}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  {formatDateTime(row.detectionCandleAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatValue(row.pattern)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{formatValue(row.status)}</Typography>
              </TableCell>
              <TableCell>{formatValue(row.patternScore, 'n/a')}</TableCell>
              <TableCell>{formatValue(row.mtfScore, 'n/a')}</TableCell>
              <TableCell>{formatValue(row.confidence, 'n/a')}</TableCell>
              <TableCell>{formatValue(row.targetConfidence, 'n/a')}</TableCell>
              <TableCell>{formatValue(row.geometryConfidence, 'n/a')}</TableCell>
              <TableCell>{formatValue(row.structureQuality, 'n/a')}</TableCell>
              <TableCell>{formatBooleanLabel(row.volumeConfirmation)}</TableCell>
              <TableCell>{formatValue(row.fakeoutRisk, 'n/a')}</TableCell>
              <TableCell>{formatDecimal(row.storedRiskRewardRatio, 4)}</TableCell>
              <TableCell>{formatDecimal(row.computedRiskRewardRatio, 4)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RectangleStructureTable({ rows, emptyMessage }) {
  if (!rows?.length) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Détection</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Touches</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Niveaux</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Géométrie</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Volume / fakeout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Scores</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.symbol}-${row.exchange}-${row.timeframe}-${row.detectionCandleAt}`} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {row.symbol} / {row.exchange} / {row.timeframe}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  {formatDateTime(row.detectionCandleAt)}
                </Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                  {formatValue(row.status)} • {formatValue(row.geometryType)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Pivot highs {formatValue(row.pivotHighCount, 'n/a')}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Pivot lows {formatValue(row.pivotLowCount, 'n/a')}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Resistance {formatValue(row.resistanceTouches, 'n/a')}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Support {formatValue(row.supportTouches, 'n/a')}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Attempts {formatValue(row.breakoutAttempts, 'n/a')} / Failed {formatValue(row.failedBreakouts, 'n/a')}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Support {formatDecimal(row.supportLevel, 4)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Resistance {formatDecimal(row.resistanceLevel, 4)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Duration {formatValue(row.structureDuration, 'n/a')}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Boundary {formatValue(row.boundaryMode)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Parallel {formatDecimal(row.parallelism, 2)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Horizontal {formatDecimal(row.horizontality, 2)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Contact {formatDecimal(row.contactQuality, 2)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Compression {formatDecimal(row.compression, 2)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Slopes S {formatDecimal(row.supportSlope, 4)} / R {formatDecimal(row.resistanceSlope, 4)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Volume {formatBooleanLabel(row.volumeConfirmation)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Vol. ratio {formatDecimal(row.breakoutVolumeRatio, 2)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Trend {formatValue(row.volumeTrend)}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Fakeout {formatValue(row.fakeoutRisk, 'n/a')}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Pattern {formatValue(row.patternScore, 'n/a')}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>MTF {formatValue(row.mtfScore, 'n/a')}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Confidence {formatValue(row.confidence, 'n/a')}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Geometry {formatValue(row.geometryConfidence, 'n/a')}</Typography>
                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>Structure {formatValue(row.structureQuality, 'n/a')} / Target {formatValue(row.targetConfidence, 'n/a')}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CalibrationPanel({ title, description, rows, lowSampleThreshold }) {
  return (
    <SectionCard
      icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
      title={title}
      description={description}
    >
      {!rows?.length ? (
        <Alert severity="info">Aucune calibration n est encore disponible.</Alert>
      ) : (
        <Stack spacing={1.2}>
          {rows.map((row) => (
            <Paper
              key={row.bucket}
              variant="outlined"
              sx={{
                p: 1.6,
                borderRadius: 3,
                borderColor: alpha(row.lowSample ? PAGE_COLORS.warning : PAGE_COLORS.inkSoft, 0.16),
                bgcolor: row.lowSample ? alpha(PAGE_COLORS.warning, 0.05) : PAGE_COLORS.sand,
              }}
            >
              <Stack spacing={1.1}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                      {row.bucket}
                    </Typography>
                    <Chip size="small" label={`${row.sampleSize} détection(s)`} sx={{ fontWeight: 700 }} />
                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                  </Stack>
                  <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>
                    Median target {formatDuration(row.medianTimeToTargetSeconds)}
                  </Typography>
                </Stack>
                <RateBar label="Breakout" value={row.breakoutRate} color={PAGE_COLORS.inkSoft} />
                <RateBar label="Target final" value={row.targetRate} color={PAGE_COLORS.success} />
                <RateBar label="Invalidation" value={row.invalidationRate} color={PAGE_COLORS.danger} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}

function ScoreMtfMatrix({ cells, lowSampleThreshold }) {
  const scoreBuckets = useMemo(() => orderBucketLabels((cells || []).map((cell) => cell.scoreBucket)), [cells]);
  const mtfBuckets = useMemo(() => orderBucketLabels((cells || []).map((cell) => cell.mtfBucket)), [cells]);
  const lookup = useMemo(
    () => new Map((cells || []).map((cell) => [`${cell.scoreBucket}__${cell.mtfBucket}`, cell])),
    [cells],
  );

  if (!cells?.length) {
    return <Alert severity="info">Aucune matrice score × MTF n est disponible pour la cohorte courante.</Alert>;
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 720, display: 'grid', gridTemplateColumns: `180px repeat(${mtfBuckets.length}, minmax(132px, 1fr))`, gap: 1 }}>
        <Box sx={{ p: 1.2 }} />
        {mtfBuckets.map((bucket) => (
          <Paper key={bucket} sx={{ p: 1.2, borderRadius: 3, bgcolor: PAGE_COLORS.sand, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              MTF
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{bucket}</Typography>
          </Paper>
        ))}
        {scoreBuckets.map((scoreBucket) => (
          <React.Fragment key={scoreBucket}>
            <Paper sx={{ p: 1.4, borderRadius: 3, bgcolor: PAGE_COLORS.sand }}>
              <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Score
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{scoreBucket}</Typography>
            </Paper>
            {mtfBuckets.map((mtfBucket) => {
              const cell = lookup.get(`${scoreBucket}__${mtfBucket}`);
              return (
                <Paper
                  key={`${scoreBucket}-${mtfBucket}`}
                  sx={{
                    p: 1.35,
                    borderRadius: 3,
                    minHeight: 110,
                    bgcolor: matrixCellBackground(cell?.targetFinalRate, cell?.lowSample),
                    border: `1px solid ${alpha(cell?.lowSample ? PAGE_COLORS.warning : PAGE_COLORS.ink, 0.12)}`,
                  }}
                >
                  {!cell ? (
                    <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>n/a</Typography>
                  ) : (
                    <Stack spacing={0.6}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                          {formatPercent(cell.targetFinalRate)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                          n={cell.detections}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                        Breakout {formatPercent(cell.breakoutRate)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                        Invalid. {formatPercent(cell.invalidationRate)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                        Median {formatDuration(cell.timeToTargetFinal?.medianSeconds)}
                      </Typography>
                      <LowSampleChip lowSample={cell.lowSample} threshold={lowSampleThreshold} />
                    </Stack>
                  )}
                </Paper>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

const PatternPerformanceAdminPage = () => {
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState({});
  const [calibration, setCalibration] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [diagnosticError, setDiagnosticError] = useState('');
  const [diagnosticPopulation, setDiagnosticPopulation] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calibrationError, setCalibrationError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupError, setCleanupError] = useState('');
  const [cleanupResult, setCleanupResult] = useState(null);
  const [benchmarkCleanupLoading, setBenchmarkCleanupLoading] = useState(false);
  const [benchmarkCleanupMode, setBenchmarkCleanupMode] = useState(null);
  const [benchmarkCleanupError, setBenchmarkCleanupError] = useState('');
  const [benchmarkCleanupResult, setBenchmarkCleanupResult] = useState(null);
  const [benchmarkRebuildLoading, setBenchmarkRebuildLoading] = useState(false);
  const [benchmarkRebuildError, setBenchmarkRebuildError] = useState('');
  const [benchmarkRebuildResult, setBenchmarkRebuildResult] = useState(null);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeMode, setPurgeMode] = useState(null);
  const [purgeError, setPurgeError] = useState('');
  const [purgeResult, setPurgeResult] = useState(null);
  const [stablecoinPurgeLoading, setStablecoinPurgeLoading] = useState(false);
  const [stablecoinPurgeError, setStablecoinPurgeError] = useState('');
  const [stablecoinPurgeResult, setStablecoinPurgeResult] = useState(null);
  const [legacyPurgeLoading, setLegacyPurgeLoading] = useState(false);
  const [legacyPurgeError, setLegacyPurgeError] = useState('');
  const [legacyPurgeResult, setLegacyPurgeResult] = useState(null);
  const [resetNotice, setResetNotice] = useState('');

  const loadData = useCallback(async (nextFilters) => {
    setLoading(true);
    setError('');
    setCalibrationError('');
    try {
      const normalizedFilters = normalizeFilters(nextFilters);
      const [summaryPayload, comparisonPayload] = await Promise.all([
        fetchPatternPerformanceSummary(normalizedFilters),
        fetchPatternPerformanceComparison({
          ...normalizedFilters,
          versions: parseVersionsInput(normalizedFilters.version),
        }),
      ]);
      const calibrationPayload = await fetchPatternPerformanceCalibration(normalizedFilters).catch((loadError) => {
        setCalibrationError(loadError.message || 'Impossible de charger la calibration score / MTF.');
        return null;
      });
      setSummary(summaryPayload);
      setComparison(comparisonPayload || {});
      setCalibration(calibrationPayload);
    } catch (loadError) {
      setSummary(null);
      setComparison({});
      setCalibration(null);
      setError(loadError.message || 'Impossible de charger les statistiques de performance patterns.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(appliedFilters);
  }, [appliedFilters, loadData]);

  const loadDiagnostic = useCallback(async (nextFilters) => {
    setDiagnosticLoading(true);
    setDiagnosticError('');
    try {
      const normalizedFilters = normalizeFilters(nextFilters);
      const selectedVersions = parseVersionsInput(normalizedFilters.version);
      if (!isTwoVersionDiagnosticSelection(selectedVersions)) {
        setDiagnostic(null);
        setDiagnosticError('Le diagnostic exige exactement deux versions distinctes. Laisse Version(s) vide pour utiliser v4,v4.3, ou saisis par exemple v3,v4 ou v4.1,v4.1.3.');
        return;
      }
      const payload = await fetchPatternPerformanceDiagnostic({
        ...normalizedFilters,
        versions: countParsedVersions(selectedVersions) === 2 ? selectedVersions : 'v4,v4.3',
      });
      setDiagnostic(payload || null);
    } catch (loadError) {
      setDiagnostic(null);
      setDiagnosticError(loadError.message || 'Impossible de charger le diagnostic de comparaison.');
    } finally {
      setDiagnosticLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'diagnostic') {
      return;
    }
    loadDiagnostic(appliedFilters);
  }, [activeTab, appliedFilters, loadDiagnostic]);

  const refreshCurrentData = useCallback(async (nextFilters) => {
    await loadData(nextFilters);
    if (activeTab === 'diagnostic') {
      await loadDiagnostic(nextFilters);
    }
  }, [activeTab, loadData, loadDiagnostic]);

  const handleFilterChange = (key) => (event) => {
    setResetNotice('');
    setFilters((current) => ({
      ...current,
      [key]: event.target.value,
    }));
  };

  const handleApplyFilters = () => {
    setResetNotice('');
    setCleanupError('');
    setCleanupResult(null);
    setBenchmarkCleanupError('');
    setBenchmarkCleanupResult(null);
    setBenchmarkRebuildError('');
    setBenchmarkRebuildResult(null);
    setPurgeError('');
    setPurgeResult(null);
    setStablecoinPurgeError('');
    setStablecoinPurgeResult(null);
    setLegacyPurgeError('');
    setLegacyPurgeResult(null);
    setDiagnosticError('');
    setAppliedFilters(normalizeFilters(filters));
  };

  const handleResetFilters = () => {
    const resetFilters = { ...DEFAULT_FILTERS };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setResetNotice('Filtres réinitialisés. Retour à la cohorte par défaut (hors stablecoins).');
    setCleanupError('');
    setCleanupResult(null);
    setBenchmarkCleanupError('');
    setBenchmarkCleanupResult(null);
    setBenchmarkRebuildError('');
    setBenchmarkRebuildResult(null);
    setPurgeError('');
    setPurgeResult(null);
    setStablecoinPurgeError('');
    setStablecoinPurgeResult(null);
    setLegacyPurgeError('');
    setLegacyPurgeResult(null);
    setDiagnosticError('');
  };

  const handleCleanupDuplicates = useCallback(async () => {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const confirmed = window.confirm('Nettoyer les doublons de la cohorte actuellement affichée ? Les détections dupliquées et leurs événements liés seront supprimés.');
      if (!confirmed) {
        return;
      }
    }

    setCleanupLoading(true);
    setResetNotice('');
    setCleanupError('');
    setCleanupResult(null);
    try {
      const normalizedFilters = normalizeFilters(appliedFilters);
      const payload = await cleanupPatternPerformanceDuplicates(normalizedFilters);
      setCleanupResult(payload);
      await refreshCurrentData(normalizedFilters);
    } catch (loadError) {
      setCleanupError(loadError.message || 'Impossible de nettoyer les doublons de cette cohorte.');
    } finally {
      setCleanupLoading(false);
    }
  }, [appliedFilters, refreshCurrentData]);

  const runBenchmarkCleanup = useCallback(async (dryRun) => {
    const normalizedFilters = normalizeFilters(filters);
    if (!hasBenchmarkPurgeScope(normalizedFilters)) {
      setBenchmarkCleanupError('Renseigne benchmarkRunId ou cohortId avant de lancer le cleanup benchmark explicite.');
      setBenchmarkCleanupResult(null);
      return;
    }
    if (hasAdditionalBenchmarkPurgeFilters(normalizedFilters)) {
      setBenchmarkCleanupError('Le cleanup benchmark explicite exige uniquement benchmarkRunId ou cohortId. Retire les autres filtres avant de lancer le dry-run ou le cleanup.');
      setBenchmarkCleanupResult(null);
      return;
    }

    if (!dryRun && typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const confirmed = window.confirm('Exécuter le cleanup benchmark explicite ? Cette action supprimera uniquement le run benchmark sélectionné, son ledger, ses analyses source benchmark, ses détections, ses événements et ses snapshots benchmark devenus sûrs à supprimer.');
      if (!confirmed) {
        return;
      }
    }

    setBenchmarkCleanupLoading(true);
    setBenchmarkCleanupMode(dryRun ? 'preview' : 'execute');
    setResetNotice('');
    setBenchmarkCleanupError('');
    setBenchmarkCleanupResult(null);
    setBenchmarkRebuildError('');
    setBenchmarkRebuildResult(null);
    try {
      const cleanupScope = {
        benchmarkRunId: normalizedFilters.benchmarkRunId,
        cohortId: normalizedFilters.cohortId,
        actor: 'admin-ui',
        reason: dryRun ? 'PATTERN_SCANNER_UI_DRY_RUN' : 'PATTERN_SCANNER_UI_EXECUTE',
      };
      const payload = dryRun
        ? await previewPatternScannerCleanup(cleanupScope)
        : await executePatternScannerCleanup({ ...cleanupScope, confirm: true });
      setBenchmarkCleanupResult(payload);
      if (!dryRun && payload?.status === 'SUCCESS') {
        setResetNotice('Cleanup benchmark exécuté. Les compteurs affichés proviennent de la vérification post-cleanup backend; retire benchmarkRunId/cohortId avant de recharger une autre cohorte.');
      }
    } catch (loadError) {
      setBenchmarkCleanupError(loadError.message || (dryRun
        ? 'Impossible de prévisualiser le cleanup benchmark explicite.'
        : 'Impossible d exécuter le cleanup benchmark explicite.'));
    } finally {
      setBenchmarkCleanupLoading(false);
      setBenchmarkCleanupMode(null);
    }
  }, [filters]);

  const handlePreviewBenchmarkCleanup = useCallback(async () => {
    await runBenchmarkCleanup(true);
  }, [runBenchmarkCleanup]);

  const handleExecuteBenchmarkCleanup = useCallback(async () => {
    await runBenchmarkCleanup(false);
  }, [runBenchmarkCleanup]);

  const handleBenchmarkRebuild = useCallback(async () => {
    const normalizedFilters = normalizeFilters(filters);
    if (!hasBenchmarkPurgeScope(normalizedFilters)) {
      setBenchmarkRebuildError('Renseigne benchmarkRunId ou cohortId avant de reconstruire un benchmark.');
      setBenchmarkRebuildResult(null);
      return;
    }
    if (hasAdditionalBenchmarkPurgeFilters(normalizedFilters)) {
      setBenchmarkRebuildError('La reconstruction benchmark exige uniquement benchmarkRunId ou cohortId. Retire les autres filtres avant de relancer la baseline.');
      setBenchmarkRebuildResult(null);
      return;
    }

    setBenchmarkRebuildLoading(true);
    setResetNotice('');
    setBenchmarkRebuildError('');
    setBenchmarkRebuildResult(null);
    try {
      const payload = await rebuildPatternScannerBenchmark({
        benchmarkRunId: normalizedFilters.benchmarkRunId,
        cohortId: normalizedFilters.cohortId,
      });
      setBenchmarkRebuildResult(payload);
    } catch (loadError) {
      setBenchmarkRebuildError(loadError.message || 'Impossible de reconstruire le benchmark sélectionné.');
    } finally {
      setBenchmarkRebuildLoading(false);
    }
  }, [filters]);

  const runPurgeHistory = useCallback(async (dryRun) => {
    const normalizedFilters = normalizeFilters(filters);
    const sameAppliedFilters = areFiltersEquivalent(appliedFilters, normalizedFilters);
    const benchmarkScopedPurge = hasBenchmarkPurgeScope(normalizedFilters);
    if (benchmarkScopedPurge && hasAdditionalBenchmarkPurgeFilters(normalizedFilters)) {
      setPurgeError('La purge benchmark complète exige uniquement benchmarkRunId ou cohortId. Retire les autres filtres avant de lancer la purge.');
      setPurgeResult(null);
      return;
    }
    if (!benchmarkScopedPurge && (!normalizedFilters.dateFrom || !normalizedFilters.dateTo)) {
      setPurgeError('Renseigne Date début et Date fin, ou bien benchmarkRunId/cohortId, avant de lancer la purge historique.');
      setPurgeResult(null);
      return;
    }

    if (!dryRun && typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const confirmed = window.confirm(
        benchmarkScopedPurge
          ? 'Purger définitivement le benchmark sélectionné ? Le run benchmark, ses exécutions, les analyses source, les détections et leurs événements liés seront supprimés.'
          : 'Purger définitivement l historique Pattern Performance correspondant aux filtres saisis ? La purge historique reste conservative: seules les détections rattachées à des analyses source suppressibles seront supprimées, et les lignes à lineage complet resteront conservées.',
      );
      if (!confirmed) {
        return;
      }
    }

    setPurgeLoading(true);
    setPurgeMode(dryRun ? 'preview' : 'delete');
    setResetNotice('');
    setPurgeError('');
    setPurgeResult(null);
    try {
      const payload = await purgePatternPerformanceHistory({ ...normalizedFilters, dryRun });
      setFilters(normalizedFilters);
      if (!dryRun) {
        if (sameAppliedFilters) {
          await refreshCurrentData(normalizedFilters);
        } else {
          setAppliedFilters(normalizedFilters);
        }
      }
      setPurgeResult(payload);
    } catch (loadError) {
      setPurgeError(loadError.message || (dryRun
        ? 'Impossible de prévisualiser la purge historique de cette période.'
        : 'Impossible de purger l historique de cette période.'));
    } finally {
      setPurgeLoading(false);
      setPurgeMode(null);
    }
  }, [appliedFilters, filters, refreshCurrentData]);

  const handlePurgeHistory = useCallback(async () => {
    await runPurgeHistory(false);
  }, [runPurgeHistory]);

  const handlePreviewPurgeHistory = useCallback(async () => {
    await runPurgeHistory(true);
  }, [runPurgeHistory]);

  const handlePurgeLegacyV1 = useCallback(async () => {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const confirmed = window.confirm('Purger définitivement toutes les données legacy v1 du Pattern Scanner ? Les détections, leurs événements et les analyses source v1 seront supprimés.');
      if (!confirmed) {
        return;
      }
    }

    setLegacyPurgeLoading(true);
    setResetNotice('');
    setLegacyPurgeError('');
    setLegacyPurgeResult(null);
    try {
      const payload = await purgeLegacyPatternPerformanceV1();
      setLegacyPurgeResult(payload);
      await refreshCurrentData(appliedFilters);
    } catch (loadError) {
      setLegacyPurgeError(loadError.message || 'Impossible de purger les données legacy v1.');
    } finally {
      setLegacyPurgeLoading(false);
    }
  }, [appliedFilters, refreshCurrentData]);

  const handlePurgeStablecoins = useCallback(async () => {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const confirmed = window.confirm('Purger définitivement toutes les données stablecoin du Pattern Scanner ? Les détections, leurs événements et les analyses source stablecoin seront supprimés.');
      if (!confirmed) {
        return;
      }
    }

    setStablecoinPurgeLoading(true);
    setResetNotice('');
    setStablecoinPurgeError('');
    setStablecoinPurgeResult(null);
    try {
      const payload = await purgeStablecoinPatternPerformanceData();
      setStablecoinPurgeResult(payload);
      await refreshCurrentData(appliedFilters);
    } catch (loadError) {
      setStablecoinPurgeError(loadError.message || 'Impossible de purger les données stablecoin historisées.');
    } finally {
      setStablecoinPurgeLoading(false);
    }
  }, [appliedFilters, refreshCurrentData]);

  const actionsLoading = loading
    || diagnosticLoading
    || cleanupLoading
    || benchmarkCleanupLoading
    || benchmarkRebuildLoading
    || purgeLoading
    || stablecoinPurgeLoading
    || legacyPurgeLoading;

  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters).filter((value) => String(value || '').trim() !== '').length,
    [appliedFilters],
  );

  const compareRows = useMemo(
    () => Object.entries(comparison || {})
      .map(([key, value]) => ({ engineVersion: value?.engineVersion || key, ...value }))
      .sort((left, right) => compareEngineVersionLabels(left.engineVersion, right.engineVersion)),
    [comparison],
  );
  const comparisonV3Rows = useMemo(
    () => compareRows.map((row) => ({
      group: row.engineVersion,
      detections: row.detections || 0,
      snapshotCount: row.v3?.snapshotCount || 0,
      coverageRate: row.v3?.coverageRate || 0,
      averagePatternQualityScore: row.v3?.averagePatternQualityScore ?? null,
      averageBreakoutQualityScore: row.v3?.averageBreakoutQualityScore ?? null,
      averageSetupConfidenceScore: row.v3?.averageSetupConfidenceScore ?? null,
      averageBullishProbability: row.v3?.averageBullishProbability ?? null,
      averageBearishProbability: row.v3?.averageBearishProbability ?? null,
      dominantBias: row.v3?.dominantBias ?? null,
      bullishCount: row.v3?.bullishCount || 0,
      bearishCount: row.v3?.bearishCount || 0,
      neutralCount: row.v3?.neutralCount || 0,
      lockedRenderingRate: row.v3?.lockedRenderingRate ?? null,
      lowSample: Boolean(row.lowSample),
    })),
    [compareRows],
  );

  const openDetail = useCallback(async (detectionId) => {
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const payload = await fetchPatternPerformanceDetail(detectionId);
      setDetail(payload);
    } catch (loadError) {
      setDetailError(loadError.message || 'Impossible de charger le détail de la détection.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = () => {
    setDetail(null);
    setDetailError('');
  };

  const global = summary?.global;
  const v3Global = summary?.v3Global;
  const v3VersionRows = summary?.v3Versions || [];
  const v3TimeframeRows = summary?.v3Timeframes || [];
  const lowSampleThreshold = summary?.lowSampleThreshold || calibration?.lowSampleThreshold || 30;
  const scoreCalibration = calibration?.score?.length ? calibration.score : calibrationFallbackFromBuckets(summary?.scoreBuckets);
  const mtfCalibration = calibration?.mtfScore?.length ? calibration.mtfScore : calibrationFallbackFromBuckets(summary?.mtfBuckets);
  const confidenceCalibration = calibration?.confidence?.length ? calibration.confidence : calibrationFallbackFromBuckets(summary?.confidenceBuckets);
  const latestDetections = summary?.recentDetections || [];
  const recentOutcomeDetections = summary?.recentOutcomeDetections?.length
    ? summary.recentOutcomeDetections
    : latestDetections.filter((row) => OUTCOME_DETECTION_STATUSES.has(row.status));
  const detailCanonicalSource = useMemo(() => buildAdminCanonicalSource(detail?.detection, detail?.initialAnalysis), [detail]);
  const detailCanonicalBias = useMemo(() => resolveCanonicalBias(detailCanonicalSource), [detailCanonicalSource]);
  const detailCanonicalBreakout = useMemo(() => resolveCanonicalBreakout(detailCanonicalSource), [detailCanonicalSource]);
  const detailCoherence = useMemo(() => resolveCoherenceSnapshot(detailCanonicalSource), [detailCanonicalSource]);
  const detailV3Metadata = useMemo(() => resolvePatternEngineV3Metadata(detailCanonicalSource), [detailCanonicalSource]);
  const detailV3Bias = detailV3Metadata?.directionalBias || detailCanonicalBias;
  const diagnosticVersions = diagnostic?.versions || [];
  const diagnosticVersionLabels = useMemo(() => resolveDiagnosticVersionLabels(diagnosticVersions), [diagnosticVersions]);
  const diagnosticSameFamily = useMemo(
    () => shareDiagnosticVersionFamily(diagnosticVersionLabels.leftVersion, diagnosticVersionLabels.rightVersion),
    [diagnosticVersionLabels],
  );
  const diagnosticBenchmark = diagnostic?.benchmark || null;
  const diagnosticSnapshot = diagnostic?.snapshot || null;
  const diagnosticSnapshotIssues = diagnosticSnapshot?.issues || [];
  const diagnosticEligibility = diagnostic?.eligibility || null;
  const diagnosticLineage = diagnostic?.lineage || null;
  const diagnosticEligibilityReasons = diagnosticEligibility?.reasons || [];
  const diagnosticExcludedAnalyses = diagnosticEligibility?.excludedAnalyses || [];
  const diagnosticIntegrity = diagnostic?.integrity || null;
  const diagnosticBenchmarkScoped = diagnosticIntegrity?.scopeType === 'BENCHMARK_LEDGER' || diagnosticEligibility?.scopeType === 'BENCHMARK_LEDGER';
  const diagnosticAnalysisScopeLabel = diagnosticEligibility?.scopeType === 'HISTORICAL_RECONSTRUCTION' ? 'Historical analyses' : 'Analyses in scope';
  const diagnosticDuplicates = diagnostic?.duplicates || null;
  const diagnosticComparisonAvailable = Boolean(diagnosticBenchmark?.comparisonAvailable);
  const diagnosticComparisonWarning = diagnosticBenchmark?.message || (diagnosticBenchmarkScoped
    ? 'aucune métrique comparative n est interpretable tant que la cohorte benchmark reste partielle ou invalide.'
    : 'aucun benchmark explicite n est selectionne ; les recouvrements historiques affiches restent purement diagnostiques.');
  const diagnosticExecution = diagnostic?.execution || null;
  const diagnosticExecutionTotals = diagnosticExecution?.totals || null;
  const diagnosticExecutionWarnings = diagnosticExecution?.warnings || [];
  const diagnosticExecutionRows = useMemo(() => {
    if (!diagnosticExecution?.byVersion) {
      return [];
    }
    const ordered = diagnosticVersions
      .map((version) => diagnosticExecution.byVersion?.[version])
      .filter(Boolean);
    return ordered.length ? ordered : Object.values(diagnosticExecution.byVersion || {});
  }, [diagnosticExecution, diagnosticVersions]);
  const diagnosticLeftExecution = diagnosticExecution?.byVersion?.[diagnosticVersionLabels.leftVersion] || null;
  const diagnosticRightExecution = diagnosticExecution?.byVersion?.[diagnosticVersionLabels.rightVersion] || null;
  const diagnosticInvalidSnapshots = diagnosticExecution?.invalidSnapshots || [];
  const diagnosticExecutionIssues = diagnosticExecution?.executionIssues || [];
  const diagnosticIntegrityInvariants = diagnosticIntegrity?.invariants || [];
  const diagnosticIntegrityContamination = diagnosticIntegrity?.contamination || null;
  const diagnosticIntegrityDuplicateKeys = (diagnosticIntegrity?.duplicateAnalysisKeyCount || 0) + (diagnosticIntegrity?.duplicateDetectionKeyCount || 0);
  const diagnosticDuplicateAnalysisKeys = diagnosticIntegrity?.duplicateAnalysisKeys || [];
  const diagnosticExpansion = diagnostic?.expansion || null;
  const diagnosticExpansionPopulation = diagnosticExpansion?.population || null;
  const diagnosticPopulationOptions = useMemo(
    () => resolveDiagnosticPopulationOptions(diagnosticExpansion, diagnosticVersionLabels),
    [diagnosticExpansion, diagnosticVersionLabels],
  );
  const activeDiagnosticPopulation = diagnosticPopulationOptions.some((option) => option.value === diagnosticPopulation)
    ? diagnosticPopulation
    : resolveDefaultDiagnosticPopulation(diagnosticPopulationOptions, diagnosticVersionLabels);
  const selectedPopulationDiagnostic = diagnosticExpansion?.populations?.[activeDiagnosticPopulation] || null;
  const selectedPopulationSummary = selectedPopulationDiagnostic?.summary || null;
  const activeDiagnosticPopulationLabel = formatPopulationLabel(activeDiagnosticPopulation, diagnosticVersionLabels);
  const diagnosticExpansionAlerts = diagnosticExpansion?.alerts || [];
  const diagnosticPatternExpansion = diagnosticExpansion?.patternExpansion || [];
  const diagnosticRrByPopulation = diagnosticExpansion?.rrByPopulation || [];
  const diagnosticRectangle = diagnosticExpansion?.rectangle || null;
  const diagnosticSections = diagnostic?.sections || {};
  const diagnosticPatterns = diagnosticSections.patterns || [];
  const diagnosticTimeframes = diagnosticSections.timeframes || [];
  const diagnosticAssets = diagnosticSections.assets || [];
  const diagnosticScoreBuckets = diagnosticSections.scoreBuckets || [];
  const diagnosticMtfBuckets = diagnosticSections.mtfBuckets || [];
  const diagnosticVolumeStates = diagnosticSections.volumeStates || [];
  const diagnosticFakeoutBuckets = diagnosticSections.fakeoutRiskBuckets || [];
  const diagnosticMaturityBuckets = diagnosticSections.maturityBuckets || [];
  const diagnosticLevels = diagnosticSections.levels || [];
  const diagnosticRrAudit = diagnosticSections.rrAudit || null;

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #efe8dc 0%, #edf3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 820 }}>
              <Chip
                icon={<AnalyticsRoundedIcon sx={{ color: '#f7f3ea !important' }} />}
                label="Admin Pattern Performance"
                sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }}
              />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Performance empirique du Pattern Scanner
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Cette page suit chaque détection depuis sa bougie de validation jusqu à sa résolution pour comparer objectivement les versions du moteur, mesurer la qualité historique du score pattern, du score MTF et de la confiance, sans altérer les statistiques brutes du tracking.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <Chip label={`${global?.detections || 0} détection(s)`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
              <Chip label={`${activeFilterCount} filtre(s) actif(s)`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
              <Chip label={`Low sample < ${lowSampleThreshold}`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
              <Chip
                label={summary?.cohortStart && summary?.cohortEnd ? `Cohorte ${formatDateTime(summary.cohortStart)} → ${formatDateTime(summary.cohortEnd)}` : 'Cohorte n/a'}
                sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fffaf2' }}>
          <Stack spacing={2.2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.5 }}>
                  Filtres d analyse
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Filtrez la cohorte par version, build, famille, type d actif, score, score MTF, alignement, volume, période de détection ou identifiant benchmark isolé.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
                <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={handleApplyFilters} sx={{ bgcolor: '#17323c', '&:hover': { bgcolor: '#0f2329' } }}>
                  Rechercher
                </Button>
                <Button variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={handleResetFilters} sx={{ borderColor: '#17323c', color: '#17323c' }}>
                  Réinitialiser
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweepRoundedIcon />}
                  onClick={handleCleanupDuplicates}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#9c4f4f', color: '#9c4f4f' }}
                >
                  {cleanupLoading ? 'Nettoyage…' : 'Nettoyer doublons'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SearchRoundedIcon />}
                  onClick={handlePreviewBenchmarkCleanup}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#6a4b1d', color: '#6a4b1d' }}
                >
                  {benchmarkCleanupLoading && benchmarkCleanupMode === 'preview' ? 'Dry-run cleanup…' : 'Dry-run cleanup benchmark'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweepRoundedIcon />}
                  onClick={handleExecuteBenchmarkCleanup}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#7d2f2f', color: '#7d2f2f' }}
                >
                  {benchmarkCleanupLoading && benchmarkCleanupMode === 'execute' ? 'Cleanup benchmark…' : 'Exécuter cleanup benchmark'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={handleBenchmarkRebuild}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#2f6f7d', color: '#2f6f7d' }}
                >
                  {benchmarkRebuildLoading ? 'Reconstruction…' : 'Reconstruire benchmark'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SearchRoundedIcon />}
                  onClick={handlePreviewPurgeHistory}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#9b6a17', color: '#9b6a17' }}
                >
                  {purgeLoading && purgeMode === 'preview' ? 'Prévisualisation…' : 'Prévisualiser purge'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweepRoundedIcon />}
                  onClick={handlePurgeHistory}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#7d2f2f', color: '#7d2f2f' }}
                >
                  {purgeLoading && purgeMode === 'delete' ? 'Purge…' : 'Purger historique'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweepRoundedIcon />}
                  onClick={handlePurgeStablecoins}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#8a4d16', color: '#8a4d16' }}
                >
                  {stablecoinPurgeLoading ? 'Purge stablecoins…' : 'Purger stablecoins'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweepRoundedIcon />}
                  onClick={handlePurgeLegacyV1}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#5f2120', color: '#5f2120' }}
                >
                  {legacyPurgeLoading ? 'Purge v1…' : 'Purger legacy v1'}
                </Button>
                <Button variant="text" startIcon={<RefreshRoundedIcon />} onClick={() => refreshCurrentData(appliedFilters)} disabled={loading || diagnosticLoading} sx={{ color: '#17323c' }}>
                  Actualiser
                </Button>
              </Stack>
            </Stack>

            {cleanupError ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {cleanupError}
              </Alert>
            ) : null}

            {purgeError ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {purgeError}
              </Alert>
            ) : null}

            {benchmarkCleanupError ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {benchmarkCleanupError}
              </Alert>
            ) : null}

            {benchmarkRebuildError ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {benchmarkRebuildError}
              </Alert>
            ) : null}

            {legacyPurgeError ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {legacyPurgeError}
              </Alert>
            ) : null}

            {stablecoinPurgeError ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {stablecoinPurgeError}
              </Alert>
            ) : null}

            {cleanupResult ? (
              <Alert severity={cleanupResult.deletedDetections > 0 || cleanupResult.deletedAnalyses > 0 ? 'success' : 'info'} sx={{ borderRadius: 3 }}>
                {buildDuplicateCleanupResultMessage(cleanupResult)}
              </Alert>
            ) : null}

            {benchmarkCleanupResult ? (
              <Alert severity={resolveBenchmarkCleanupSeverity(benchmarkCleanupResult)} sx={{ borderRadius: 3 }}>
                <Stack spacing={1.2}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {buildBenchmarkCleanupResultMessage(benchmarkCleanupResult)}
                  </Typography>
                  {benchmarkCleanupResult.summary ? (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                      {[
                        `Analyses candidates: ${toNumber(benchmarkCleanupResult.summary.analysisCandidates) || 0}`,
                        `Analyses benchmark-owned: ${toNumber(benchmarkCleanupResult.summary.benchmarkOwnedAnalyses) || 0}`,
                        `Artefacts benchmark: ${toNumber(benchmarkCleanupResult.summary.benchmarkArtifactCandidates) || 0}`,
                        `Doublons candidats: ${toNumber(benchmarkCleanupResult.summary.duplicateRowsCandidates) || 0}`,
                        `Snapshots orphelins sûrs: ${toNumber(benchmarkCleanupResult.summary.safeOrphanSnapshots) || 0}`,
                        `Lignes protégées: ${toNumber(benchmarkCleanupResult.summary.protectedRows) || 0}`,
                        `Analyses non-benchmark protégées: ${toNumber(benchmarkCleanupResult.summary.nonBenchmarkAnalysesProtected) || 0}`,
                        `Lineage ambigu protégé: ${toNumber(benchmarkCleanupResult.summary.ambiguousLineageProtected) || 0}`,
                        `Suppressions bloquées: ${toNumber(benchmarkCleanupResult.summary.blockedDeletes) || 0}`,
                        `Suppressions estimées: ${toNumber(benchmarkCleanupResult.summary.estimatedDeletions) || 0}`,
                      ].map((label) => (
                        <Chip
                          key={label}
                          size="small"
                          label={label}
                          sx={{
                            bgcolor: alpha(PAGE_COLORS.ink, 0.06),
                            color: PAGE_COLORS.ink,
                            fontWeight: 700,
                          }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                  {buildBenchmarkCleanupSummaryNote(benchmarkCleanupResult) ? (
                    <Typography variant="caption" sx={{ color: '#4f646f' }}>
                      {buildBenchmarkCleanupSummaryNote(benchmarkCleanupResult)}
                    </Typography>
                  ) : null}
                  {benchmarkCleanupResult.tables?.length ? (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                      {benchmarkCleanupResult.tables.map((table) => (
                        <Chip
                          key={`benchmark-cleanup-${table.tableName}`}
                          size="small"
                          label={formatPurgeTableSummary(table, Boolean(benchmarkCleanupResult.dryRun))}
                          sx={{
                            bgcolor: alpha(PAGE_COLORS.accent, 0.12),
                            color: PAGE_COLORS.ink,
                            fontWeight: 700,
                          }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </Alert>
            ) : null}

            {benchmarkRebuildResult ? (
              <Alert severity={benchmarkRebuildResult.status === 'SUCCESS' ? 'success' : 'info'} sx={{ borderRadius: 3 }}>
                {buildBenchmarkRebuildResultMessage(benchmarkRebuildResult)}
              </Alert>
            ) : null}

            {purgeResult ? (
              <Alert severity={resolvePurgeResultSeverity(purgeResult)} sx={{ borderRadius: 3 }}>
                <Stack spacing={1.2}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {buildPurgeResultMessage(purgeResult)}
                  </Typography>
                  {purgeResult.dryRun ? (
                    <Typography variant="caption" sx={{ color: '#4f646f' }}>
                      Prévisualisation uniquement: aucune suppression n a été exécutée. Les compteurs restants reflètent encore les lignes présentes au moment du calcul.
                    </Typography>
                  ) : !purgeResult.completePurge ? (
                    <Typography variant="caption" sx={{ color: '#4f646f' }}>
                      Pour supprimer aussi le ledger benchmark, lance une purge avec benchmarkRunId ou cohortId seul.
                    </Typography>
                  ) : null}
                  {buildHistoryCleanupPreviewNote(purgeResult) ? (
                    <Typography variant="caption" sx={{ color: '#4f646f' }}>
                      {buildHistoryCleanupPreviewNote(purgeResult)}
                    </Typography>
                  ) : null}
                  {purgeResult.historyCleanupPreview?.statusCounts?.length || purgeResult.historyCleanupPreview?.repairabilityCounts?.length ? (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                      {(purgeResult.historyCleanupPreview?.statusCounts || []).map((row) => (
                        <Chip
                          key={`purge-status-${row.code}`}
                          size="small"
                          label={formatHistoryCleanupClassificationLabel(row)}
                          sx={{
                            bgcolor: alpha(PAGE_COLORS.warning, 0.12),
                            color: PAGE_COLORS.ink,
                            fontWeight: 700,
                          }}
                        />
                      ))}
                      {(purgeResult.historyCleanupPreview?.repairabilityCounts || []).map((row) => (
                        <Chip
                          key={`purge-repairability-${row.code}`}
                          size="small"
                          label={formatHistoryCleanupClassificationLabel(row)}
                          sx={{
                            bgcolor: alpha(PAGE_COLORS.accent, 0.12),
                            color: PAGE_COLORS.ink,
                            fontWeight: 700,
                          }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                  {purgeResult.tables?.length ? (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                      {purgeResult.tables.map((table) => (
                        <Chip
                          key={table.tableName}
                          size="small"
                          label={formatPurgeTableSummary(table, Boolean(purgeResult.dryRun))}
                          sx={{
                            bgcolor: alpha(PAGE_COLORS.ink, 0.06),
                            color: PAGE_COLORS.ink,
                            fontWeight: 700,
                          }}
                        />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </Alert>
            ) : null}

            {legacyPurgeResult ? (
              <Alert severity={legacyPurgeResult.deletedDetections > 0 || legacyPurgeResult.deletedAnalyses > 0 ? 'success' : 'info'} sx={{ borderRadius: 3 }}>
                {legacyPurgeResult.deletedDetections > 0 || legacyPurgeResult.deletedAnalyses > 0
                  ? `${legacyPurgeResult.deletedDetections} détection(s) v1, ${legacyPurgeResult.deletedEvents} événement(s) et ${legacyPurgeResult.deletedAnalyses} analyse(s) source supprimé(s).`
                  : 'Aucune donnée legacy v1 à purger.'}
              </Alert>
            ) : null}

            {stablecoinPurgeResult ? (
              <Alert severity={stablecoinPurgeResult.deletedDetections > 0 || stablecoinPurgeResult.deletedAnalyses > 0 ? 'success' : 'info'} sx={{ borderRadius: 3 }}>
                {stablecoinPurgeResult.deletedDetections > 0 || stablecoinPurgeResult.deletedAnalyses > 0
                  ? `${stablecoinPurgeResult.deletedDetections} détection(s) stablecoin, ${stablecoinPurgeResult.deletedEvents} événement(s) et ${stablecoinPurgeResult.deletedAnalyses} analyse(s) source supprimé(s).`
                  : 'Aucune donnée stablecoin à purger.'}
              </Alert>
            ) : null}

            {resetNotice ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                {resetNotice}
              </Alert>
            ) : null}

            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Les statistiques excluent les stablecoins historisés. Le filtre Direction suit le biais canonique déduit des niveaux et du snapshot historisé, pas seulement le PatternType legacy. Le score et le score MTF servent uniquement aux filtres, aux buckets et aux calibrations. Les métriques globales restent brutes. Les scores MTF absents restent en bucket UNKNOWN et ne sont jamais forcés à 0. Le nettoyage des doublons agit sur la cohorte affichée. La purge historique hors benchmark agit de façon conservative sur les filtres saisis si Date début et Date fin sont renseignées: seules les détections et événements ciblés sont supprimés automatiquement hors scope benchmark explicite, et les analyses métier restent protégées. Le dry-run cleanup benchmark et son exécution exigent benchmarkRunId ou cohortId seuls afin de supprimer explicitement le ledger benchmark, les analyses source benchmark, les détections, leurs événements et les snapshots benchmark devenus sûrs à supprimer. Le bouton Reconstruire benchmark relance ensuite une baseline propre sur ce snapshot canonique. Le bouton stablecoins purge globalement les détections stablecoin, leurs événements et les analyses source associées. Le bouton legacy v1 purge globalement les détections v1, leurs événements et les analyses source associées.
            </Alert>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 1.2 }}>
              <TextField label="Version(s)" size="small" value={filters.version} onChange={handleFilterChange('version')} placeholder="v3,v4 ou v4,v4.2 ou v4.1,v4.1.3" />
              <TextField label="Engine build" size="small" value={filters.engineBuild} onChange={handleFilterChange('engineBuild')} placeholder="v4-2026-08-20.0000" />
              <TextField label="Pattern" size="small" value={filters.pattern} onChange={handleFilterChange('pattern')} placeholder="DOUBLE_TOP" />
              <TextField label="Famille pattern" size="small" value={filters.patternFamily} onChange={handleFilterChange('patternFamily')} placeholder="REVERSAL" />
              <TextField label="Crypto" size="small" value={filters.crypto} onChange={handleFilterChange('crypto')} placeholder="BTC" />
              <TextField label="Exchange" size="small" value={filters.exchange} onChange={handleFilterChange('exchange')} placeholder="BINANCE" />
              <FormControl size="small">
                <InputLabel>Timeframe</InputLabel>
                <Select label="Timeframe" value={filters.timeframe} onChange={handleFilterChange('timeframe')}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="1H">1H</MenuItem>
                  <MenuItem value="4H">4H</MenuItem>
                  <MenuItem value="1D">1D</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Direction</InputLabel>
                <Select label="Direction" value={filters.direction} onChange={handleFilterChange('direction')}>
                  <MenuItem value="">Toutes</MenuItem>
                  <MenuItem value="BULLISH">LONG</MenuItem>
                  <MenuItem value="BEARISH">SHORT</MenuItem>
                  <MenuItem value="NEUTRAL">NEUTRAL</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Statut</InputLabel>
                <Select label="Statut" value={filters.status} onChange={handleFilterChange('status')}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="BROKEN_OUT">BROKEN_OUT</MenuItem>
                  <MenuItem value="TARGET_HIT">TARGET_HIT</MenuItem>
                  <MenuItem value="INVALIDATED">INVALIDATED</MenuItem>
                  <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                  <MenuItem value="AMBIGUOUS">AMBIGUOUS</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Bucket score</InputLabel>
                <Select label="Bucket score" value={filters.scoreBucket} onChange={handleFilterChange('scoreBucket')}>
                  <MenuItem value="">Tous</MenuItem>
                  {STANDARD_BUCKET_OPTIONS.map((bucket) => <MenuItem key={bucket} value={bucket}>{bucket}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Score min" size="small" type="number" value={filters.scoreMin} onChange={handleFilterChange('scoreMin')} inputProps={{ min: 0, max: 100 }} />
              <TextField label="Score max" size="small" type="number" value={filters.scoreMax} onChange={handleFilterChange('scoreMax')} inputProps={{ min: 0, max: 100 }} />
              <FormControl size="small">
                <InputLabel>Bucket score MTF</InputLabel>
                <Select label="Bucket score MTF" value={filters.mtfBucket} onChange={handleFilterChange('mtfBucket')}>
                  <MenuItem value="">Tous</MenuItem>
                  {MTF_BUCKET_OPTIONS.map((bucket) => <MenuItem key={bucket} value={bucket}>{bucket}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Score MTF min" size="small" type="number" value={filters.mtfScoreMin} onChange={handleFilterChange('mtfScoreMin')} inputProps={{ min: 0, max: 100 }} />
              <TextField label="Score MTF max" size="small" type="number" value={filters.mtfScoreMax} onChange={handleFilterChange('mtfScoreMax')} inputProps={{ min: 0, max: 100 }} />
              <FormControl size="small">
                <InputLabel>Alignement MTF</InputLabel>
                <Select label="Alignement MTF" value={filters.mtfAlignment} onChange={handleFilterChange('mtfAlignment')}>
                  <MenuItem value="">Tous</MenuItem>
                  {MTF_ALIGNMENT_OPTIONS.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Bucket confiance</InputLabel>
                <Select label="Bucket confiance" value={filters.confidenceBucket} onChange={handleFilterChange('confidenceBucket')}>
                  <MenuItem value="">Tous</MenuItem>
                  {STANDARD_BUCKET_OPTIONS.map((bucket) => <MenuItem key={bucket} value={bucket}>{bucket}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Type d actif</InputLabel>
                <Select label="Type d actif" value={filters.assetType} onChange={handleFilterChange('assetType')}>
                  {ASSET_TYPE_OPTIONS.map((option) => <MenuItem key={option.value || 'default'} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Volume confirmé</InputLabel>
                <Select label="Volume confirmé" value={filters.volumeConfirmed} onChange={handleFilterChange('volumeConfirmed')}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="true">Oui</MenuItem>
                  <MenuItem value="false">Non</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Benchmark run ID" size="small" value={filters.benchmarkRunId} onChange={handleFilterChange('benchmarkRunId')} placeholder="aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa" />
              <TextField label="Cohort ID" size="small" value={filters.cohortId} onChange={handleFilterChange('cohortId')} placeholder="bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbbbb" />
              <TextField label="Date début" size="small" type="datetime-local" value={filters.dateFrom} onChange={handleFilterChange('dateFrom')} InputLabelProps={{ shrink: true }} />
              <TextField label="Date fin" size="small" type="datetime-local" value={filters.dateTo} onChange={handleFilterChange('dateTo')} InputLabelProps={{ shrink: true }} />
            </Box>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Paper sx={{ p: 6, borderRadius: 5, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#19323c', mb: 1.5 }} />
            <Typography variant="body2" sx={{ color: '#4f646f' }}>
              Chargement des métriques de détection...
            </Typography>
          </Paper>
        ) : !summary ? (
          <Alert severity="info">Aucune statistique de performance n est disponible pour les filtres courants.</Alert>
        ) : (
          <>
            <Paper sx={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${alpha(PAGE_COLORS.ink, 0.08)}` }}>
              <Tabs
                value={activeTab}
                onChange={(_, nextValue) => setActiveTab(nextValue)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  px: 1,
                  bgcolor: '#fff',
                  '& .MuiTabs-indicator': { backgroundColor: PAGE_COLORS.accent, height: 3 },
                }}
              >
                {TAB_OPTIONS.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    label={tab.label}
                    sx={{
                      minHeight: 68,
                      color: PAGE_COLORS.muted,
                      fontWeight: 700,
                      '&.Mui-selected': { color: PAGE_COLORS.ink },
                    }}
                  />
                ))}
              </Tabs>
            </Paper>

            {activeTab === 'overview' ? (
              <Stack spacing={1.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
                  {[
                    { label: 'Détections', value: global?.detections || 0 },
                    { label: 'Breakouts', value: global?.breakoutCount || 0 },
                    { label: 'Targets finales', value: global?.targetFinalCount || 0, tone: 'success' },
                    { label: 'Invalidations', value: global?.invalidationCount || 0, tone: 'danger' },
                    { label: 'Expirés', value: global?.expiredCount || 0, tone: 'warning' },
                    { label: 'Ambigus', value: global?.ambiguousCount || 0, tone: 'warning' },
                    { label: 'Target rate', value: formatPercent(global?.targetFinalRate), tone: 'success' },
                    { label: 'Quality score', value: formatPercent(global?.qualityScore), tone: 'accent' },
                  ].map((item) => (
                    <MetricCard key={item.label} label={item.label} value={item.value} tone={item.tone} />
                  ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.05fr 0.95fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Lecture score / MTF"
                    description="Mesures descriptives globales pour lire la cohorte sans changer les statistiques de base."
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.2 }}>
                      <MetricCard label="Score pattern moyen" value={formatScore(global?.averagePatternScore, 1)} helper={`Median ${formatScore(global?.medianPatternScore, 1)}`} />
                      <MetricCard label="Score MTF moyen" value={formatScore(global?.averageMtfScore, 1)} helper={`Median ${formatScore(global?.medianMtfScore, 1)}`} tone="accent" />
                      <MetricCard label="Confiance moyenne" value={formatPercent(global?.averageConfidence)} helper={`Median ${formatPercent(global?.medianConfidence)}`} />
                    </Box>
                    <Divider />
                    <Stack spacing={1.2}>
                      <RateBar label="Breakout rate" value={global?.breakoutRate} color={PAGE_COLORS.inkSoft} />
                      <RateBar label="Target final rate" value={global?.targetFinalRate} color={PAGE_COLORS.success} />
                      <RateBar label="Invalidation rate" value={global?.invalidationRate} color={PAGE_COLORS.danger} />
                    </Stack>
                  </SectionCard>

                  <SectionCard
                    icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Statistiques temporelles"
                    description="Durées médianes et extrêmes utiles pour qualifier la vitesse de résolution."
                  >
                    {[
                      { label: 'Breakout median', value: formatDuration(global?.timeToBreakout?.medianSeconds) },
                      { label: 'Breakout moyenne', value: formatDuration(global?.timeToBreakout?.averageSeconds) },
                      { label: 'Target median', value: formatDuration(global?.timeToTargetFinal?.medianSeconds) },
                      { label: 'Target moyenne', value: formatDuration(global?.timeToTargetFinal?.averageSeconds) },
                      { label: 'Invalidation median', value: formatDuration(global?.timeToInvalidation?.medianSeconds) },
                      { label: 'P90 target', value: formatDuration(global?.timeToTargetFinal?.p90Seconds) },
                    ].map((item) => (
                      <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.75, borderBottom: `1px solid ${PAGE_COLORS.border}` }}>
                        <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>{item.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </SectionCard>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Calibration de confiance"
                    description="Lecture rapide de la stabilité historique par bucket de confiance."
                  >
                    <BucketMetricsTable rows={summary.confidenceBuckets} lowSampleThreshold={lowSampleThreshold} firstLabel="Bucket confiance" />
                  </SectionCard>

                  <SectionCard
                    icon={<CompareArrowsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Comparaison des versions"
                    description="Benchmark ciblé des versions du moteur sur la cohorte filtrée."
                  >
                    <Stack spacing={1.2}>
                      <BenchmarkOrchestrationNotice />
                      {compareRows.length === 0 ? (
                        <Alert severity="info">Aucune version comparable pour la cohorte actuelle.</Alert>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Score moyen</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>MTF moyen</TableCell>
                                <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {compareRows.map((row) => (
                                <TableRow key={row.engineVersion} hover>
                                  <TableCell>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.engineVersion}</Typography>
                                      <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                    </Stack>
                                  </TableCell>
                                  <TableCell>{row.detections}</TableCell>
                                  <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
                                  <TableCell>{formatPercent(row.targetRate)}</TableCell>
                                  <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
                                  <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
                                  <TableCell>{formatScore(row.averageMtfScore, 1)}</TableCell>
                                  <TableCell>{formatDuration(row.medianTimeToTargetSeconds)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Stack>
                  </SectionCard>
                </Box>

                {v3Global ? (
                  <SectionCard
                    icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Lecture V3 historisée"
                    description="Axes V3 séparés calculés depuis diagnostics.v3, sans réécriture des snapshots legacy."
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 1.2 }}>
                      <MetricCard label="Couverture V3" value={formatPercent(v3Global.coverageRate)} helper={formatV3CoverageHelper(v3Global, global?.detections)} tone="accent" />
                      <MetricCard label="Qualité pattern V3" value={formatScore(v3Global.averagePatternQualityScore, 1)} helper={formatV3CoverageHelper(v3Global, global?.detections)} tone="accent" />
                      <MetricCard label="Qualité breakout V3" value={formatScore(v3Global.averageBreakoutQualityScore, 1)} helper={formatV3CoverageHelper(v3Global, global?.detections)} tone="success" />
                      <MetricCard label="Biais directionnel V3" value={formatV3AggregateBiasValue(v3Global)} helper={formatV3AggregateBiasHelper(v3Global)} tone="accent" />
                      <MetricCard label="Confiance setup V3" value={formatScore(v3Global.averageSetupConfidenceScore, 1)} helper={formatV3CoverageHelper(v3Global, global?.detections)} />
                    </Box>
                  </SectionCard>
                ) : null}

                <SectionCard
                  icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Derniers breakouts / invalidations / targets"
                  description="Sous-cohorte des détections récentes déjà sorties de l état ACTIVE via breakout, invalidation ou objectif atteint."
                >
                  <RecentDetectionsTable
                    rows={recentOutcomeDetections}
                    onOpen={openDetail}
                    emptyMessage="Aucune détection récente avec breakout, invalidation ou target atteint n est disponible."
                  />
                </SectionCard>
              </Stack>
            ) : null}

            {activeTab === 'diagnostic' ? (
              diagnosticLoading ? (
                <Paper sx={{ p: 5, borderRadius: 5, textAlign: 'center' }}>
                  <CircularProgress sx={{ color: PAGE_COLORS.ink, mb: 1.5 }} />
                  <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>
                    Chargement du diagnostic de comparaison...
                  </Typography>
                </Paper>
              ) : diagnosticError ? (
                <Alert severity="error">{diagnosticError}</Alert>
              ) : !diagnostic ? (
                <Alert severity="info">Aucun diagnostic de comparaison n est disponible pour les filtres courants.</Alert>
              ) : (
                <Stack spacing={1.5}>
                  <SectionCard
                    icon={<CompareArrowsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title={diagnosticSameFamily
                      ? `${diagnosticVersionLabels.leftTag} vs ${diagnosticVersionLabels.rightTag} - Diagnostic final`
                      : `Diagnostic ${diagnosticVersionLabels.leftTag} vs ${diagnosticVersionLabels.rightTag}`}
                    description="Diagnostic READ ONLY. Les taux affichés dans les tableaux utilisent explicitement le dénominateur indiqué dans le rapport, sans relancer le scanner, sans nouvelle version moteur et sans recalcul retroactif."
                  >
                    <Stack spacing={1.5}>
                      <Alert severity={resolveBenchmarkSeverity(diagnosticBenchmark?.status)} sx={{ borderRadius: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          Benchmark status: {formatValue(diagnosticBenchmark?.status, 'UNKNOWN')}
                        </Typography>
                        <Typography variant="body2">
                          {diagnosticBenchmark?.message || diagnostic.report?.conclusion || `Diagnostic ${diagnosticVersionLabels.leftTag} vs ${diagnosticVersionLabels.rightTag} indisponible.`}
                        </Typography>
                      </Alert>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
                        <MetricCard label="Benchmark" value={formatValue(diagnosticBenchmark?.status, 'UNKNOWN')} tone={resolveBenchmarkTone(diagnosticBenchmark?.status)} helper={formatValue(diagnosticBenchmark?.reason, 'n/a')} />
                        <MetricCard label="Common cohort" value={diagnosticBenchmark?.commonCohortCount || 0} tone="accent" helper={`Expected ${diagnosticBenchmark?.expectedCohortCount || 0}`} />
                        <MetricCard label="Disagreements" value={diagnostic.diff?.bothDifferCount || 0} tone="warning" helper={`Accords ${diagnostic.diff?.bothAgreeCount || 0}`} />
                        <MetricCard label={diagnosticVersionLabels.leftOnlyLabel} value={diagnostic.diff?.leftOnlyCount || 0} helper={`Executed ${diagnosticLeftExecution?.executedCount || 0} | Persisted ${diagnosticLeftExecution?.detectionPersistedCount || 0}`} />
                        <MetricCard label={diagnosticVersionLabels.rightOnlyLabel} value={diagnostic.diff?.rightOnlyCount || 0} helper={`Executed ${diagnosticRightExecution?.executedCount || 0} | Persisted ${diagnosticRightExecution?.detectionPersistedCount || 0}`} tone="accent" />
                        <MetricCard label="Duplicate cohorts" value={diagnosticDuplicates?.candidateCount || 0} helper={`Analysis keys ${diagnosticIntegrity?.duplicateAnalysisKeyCount || 0} | Detection keys ${diagnosticIntegrity?.duplicateDetectionKeyCount || 0}`} tone={diagnosticIntegrityDuplicateKeys ? 'danger' : 'success'} />
                        <MetricCard label="Historical contamination" value={diagnosticIntegrityContamination?.count || 0} helper={formatIntegrityReasons(diagnosticIntegrityContamination?.reasons)} tone={diagnosticIntegrityContamination?.count ? 'danger' : 'success'} />
                        <MetricCard label="RR ~ 0.9259" value={diagnosticRrAudit?.anomalyCount || 0} helper={formatPercent(diagnosticRrAudit?.anomalyRate)} tone="warning" />
                      </Box>
                      {!diagnosticComparisonAvailable ? (
                        <Alert severity="warning" sx={{ borderRadius: 3 }}>
                          Comparaison {diagnosticVersionLabels.leftTag}/{diagnosticVersionLabels.rightTag} indisponible: {diagnosticComparisonWarning}
                        </Alert>
                      ) : null}
                      <Stack spacing={0.8}>
                        {(diagnostic.report?.highlights || []).map((line) => (
                          <Typography key={line} variant="body2" sx={{ color: PAGE_COLORS.muted }}>
                            {line}
                          </Typography>
                        ))}
                      </Stack>
                      <Alert severity="info" sx={{ borderRadius: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>
                          Plan du diagnostic final
                        </Typography>
                        <Typography variant="body2">
                          {diagnosticBenchmarkScoped
                            ? 'A. Benchmark integrity • B. Same cohort coverage • C. Exact populations • D. Pattern expansion • E. Selected population summary • F. Pattern and timeframe breakdowns • G. Exchange and symbol breakdowns • H. Score and MTF calibration • I. RR analysis • J. Rectangle diagnostic • K. Raw population • L. Same cohort tables • M. Common snapshots • N. Level and RR audits.'
                            : 'A. Historical reconstruction integrity • B. Same cohort coverage • C. Exact populations • D. Pattern expansion • E. Selected population summary • F. Pattern and timeframe breakdowns • G. Exchange and symbol breakdowns • H. Score and MTF calibration • I. RR analysis • J. Rectangle diagnostic • K. Raw population • L. Same cohort tables • M. Common snapshots • N. Level and RR audits.'}
                        </Typography>
                      </Alert>
                    </Stack>
                  </SectionCard>

                  {diagnosticSnapshot ? (
                    <SectionCard
                      icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title="Benchmark snapshot"
                      description="Snapshot benchmark immuable utilise comme population de reference commune pour tous les engines compares."
                    >
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
                          <MetricCard label="Snapshot ID" value={formatValue(diagnosticSnapshot?.snapshotId, 'n/a')} helper={`Created ${formatDateTime(diagnosticSnapshot?.createdAt)}`} />
                          <MetricCard label="Snapshot status" value={formatValue(diagnosticSnapshot?.status, 'n/a')} tone={diagnosticSnapshotIssues.length ? 'danger' : 'success'} helper={`Immutable ${diagnosticSnapshot?.immutable ? 'YES' : 'NO'}`} />
                          <MetricCard label="Expected cohort" value={diagnosticSnapshot?.expectedCohortCount || 0} tone="accent" helper={`Actual ${diagnosticSnapshot?.actualCohortCount || 0}`} />
                          <MetricCard label="Cohort hash" value={formatValue(diagnosticSnapshot?.cohortKeyHash, 'n/a')} helper={`Duplicate ${diagnosticSnapshot?.duplicateCount || 0} | Missing ${diagnosticSnapshot?.missingCount || 0} | Extra ${diagnosticSnapshot?.extraCount || 0}`} />
                        </Box>

                        {diagnosticSnapshotIssues.length ? (
                          <>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                              Invalid snapshot details
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Snapshot</TableCell>
                                    <TableCell>Reason</TableCell>
                                    <TableCell align="right">Expected</TableCell>
                                    <TableCell align="right">Actual</TableCell>
                                    <TableCell align="right">Duplicate</TableCell>
                                    <TableCell align="right">Missing</TableCell>
                                    <TableCell align="right">Extra</TableCell>
                                    <TableCell>Hash</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {diagnosticSnapshotIssues.map((row, index) => (
                                    <TableRow key={`${row.snapshotId || 'snapshot'}-${row.reason || index}`}>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                                          {formatValue(row.snapshotId, 'n/a')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Created: {formatDateTime(row.createdAt)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>{formatValue(row.reason, 'n/a')}</TableCell>
                                      <TableCell align="right">{row.expectedSize || 0}</TableCell>
                                      <TableCell align="right">{row.actualSize || 0}</TableCell>
                                      <TableCell align="right">{row.duplicateCount || 0}</TableCell>
                                      <TableCell align="right">{row.missingCount || 0}</TableCell>
                                      <TableCell align="right">{row.extraCount || 0}</TableCell>
                                      <TableCell>{formatValue(row.cohortKeyHash, 'n/a')}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        ) : (
                          <Alert severity="success" sx={{ borderRadius: 3 }}>Aucune anomalie structurelle detectee sur le snapshot benchmark courant.</Alert>
                        )}
                      </Stack>
                    </SectionCard>
                  ) : null}

                  {diagnosticExecution ? (
                    <SectionCard
                      icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title={diagnosticBenchmarkScoped ? 'Benchmark integrity' : 'Historical reconstruction integrity'}
                      description={diagnosticBenchmarkScoped
                        ? 'Cohorte benchmark explicite, avec separation stricte entre executions ledger, analyses persistees, zero detection et detections persistees.'
                        : 'Reconstruction historique en lecture seule, utile pour l audit mais non publiable comme benchmark sans benchmarkRunId/cohortId explicite.'}
                    >
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1.2 }}>
                          <MetricCard
                            label="Scope"
                            value={formatValue(diagnosticIntegrity?.scopeType, 'n/a')}
                            tone="accent"
                            helper={formatBenchmarkScopeHelper(diagnosticIntegrity)}
                          />
                          <MetricCard
                            label="Expected cohort"
                            value={diagnosticExecution?.expectedCohortCount || 0}
                            tone="accent"
                            helper={formatValue(diagnosticIntegrity?.populationDefinition || diagnosticExecution?.cohortKeyDefinition, 'n/a')}
                          />
                          <MetricCard
                            label="Common cohort"
                            value={diagnosticExecution?.commonCohortCount || 0}
                            tone={diagnosticComparisonAvailable ? 'success' : 'warning'}
                            helper={`Both detected ${diagnosticIntegrity?.bothDetectedCount || 0} | ${diagnosticVersionLabels.leftTag} only ${diagnosticIntegrity?.leftDetectedOnlyCount || 0} | ${diagnosticVersionLabels.rightTag} only ${diagnosticIntegrity?.rightDetectedOnlyCount || 0} | Both zero ${diagnosticIntegrity?.bothZeroDetectionCount || 0}`}
                          />
                          <MetricCard
                            label={`${diagnosticVersionLabels.leftTag} executed`}
                            value={diagnosticLeftExecution?.executedCount || 0}
                            tone={resolveCoverageTone(diagnosticBenchmark?.leftCoverage)}
                            helper={`Coverage ${formatPercent(diagnosticBenchmark?.leftCoverage)} | Missing ${diagnosticLeftExecution?.missingCount || 0} | Extra ${diagnosticLeftExecution?.extraCount || 0}`}
                          />
                          <MetricCard
                            label={`${diagnosticVersionLabels.rightTag} executed`}
                            value={diagnosticRightExecution?.executedCount || 0}
                            tone={resolveCoverageTone(diagnosticBenchmark?.rightCoverage)}
                            helper={`Coverage ${formatPercent(diagnosticBenchmark?.rightCoverage)} | Missing ${diagnosticRightExecution?.missingCount || 0} | Extra ${diagnosticRightExecution?.extraCount || 0}`}
                          />
                          <MetricCard
                            label={diagnosticBenchmarkScoped ? 'Invalid benchmark snapshots' : 'Invalid historical snapshots'}
                            value={diagnosticExecution?.invalidCohortCount || 0}
                            tone={diagnosticExecution?.invalidCohortCount ? 'danger' : 'success'}
                            helper={`${diagnosticBenchmarkScoped ? 'Benchmarked cohort only' : 'Historical reconstruction only'} | Missing ${diagnosticIntegrity?.missingInputSnapshotTimestampCount || 0} | Hash mismatch ${diagnosticIntegrity?.mismatchedInputSnapshotHashCount || 0}`}
                          />
                          <MetricCard
                            label={diagnosticAnalysisScopeLabel}
                            value={diagnosticEligibility?.analysisCount || 0}
                            tone="accent"
                            helper={formatValue(diagnosticEligibility?.status, 'n/a')}
                          />
                          <MetricCard
                            label="Benchmark eligible"
                            value={diagnosticEligibility?.benchmarkEligibleAnalysisCount || 0}
                            tone={diagnosticEligibility?.benchmarkEligibleAnalysisCount ? 'success' : 'warning'}
                            helper={`Cohorts ${diagnosticEligibility?.benchmarkEligibleCohortCount || 0} | ${formatVersionCountMap(diagnosticEligibility?.benchmarkEligibleByVersion)}`}
                          />
                          <MetricCard
                            label="Not benchmark eligible"
                            value={diagnosticEligibility?.notBenchmarkEligibleAnalysisCount || 0}
                            tone={diagnosticEligibility?.notBenchmarkEligibleAnalysisCount ? 'warning' : 'success'}
                            helper={formatEligibilityReasonSummary(diagnosticEligibilityReasons)}
                          />
                          <MetricCard
                            label="Duplicate keys"
                            value={diagnosticIntegrityDuplicateKeys}
                            tone={diagnosticIntegrityDuplicateKeys ? 'danger' : 'success'}
                            helper={`Analysis ${diagnosticIntegrity?.duplicateAnalysisKeyCount || 0} | Detection ${diagnosticIntegrity?.duplicateDetectionKeyCount || 0}`}
                          />
                          <MetricCard
                            label="Historical contamination"
                            value={diagnosticIntegrityContamination?.count || 0}
                            tone={diagnosticIntegrityContamination?.count ? 'danger' : 'success'}
                            helper={formatIntegrityReasons(diagnosticIntegrityContamination?.reasons)}
                          />
                        </Box>

                        {diagnosticIntegrityContamination?.count ? (
                          <Alert severity="error" sx={{ borderRadius: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              Historical contamination detected: {diagnosticIntegrityContamination.count}
                            </Typography>
                            <Typography variant="body2">
                              Reasons: {formatIntegrityReasons(diagnosticIntegrityContamination.reasons)}. Window: {formatDateTime(diagnosticIntegrityContamination.oldestTimestamp)} - {formatDateTime(diagnosticIntegrityContamination.newestTimestamp)}.
                            </Typography>
                          </Alert>
                        ) : null}

                        {diagnosticExecutionWarnings.length ? diagnosticExecutionWarnings.map((warning) => (
                          <Alert key={warning} severity="warning" sx={{ borderRadius: 3 }}>{warning}</Alert>
                        )) : (
                          <Alert severity="info" sx={{ borderRadius: 3 }}>Aucune alerte de cohorte supplementaire sur l execution historisee.</Alert>
                        )}

                        {diagnosticEligibility ? (
                          <>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                              {diagnosticBenchmarkScoped ? 'Benchmark eligibility' : 'Historical eligibility audit'}
                            </Typography>
                            <Alert severity={diagnosticEligibility?.notBenchmarkEligibleAnalysisCount ? 'warning' : 'success'} sx={{ borderRadius: 3 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {diagnosticEligibility.message}
                              </Typography>
                              <Typography variant="body2">
                                {diagnosticAnalysisScopeLabel}: {diagnosticEligibility.analysisCount || 0}. Benchmark eligible: {diagnosticEligibility.benchmarkEligibleAnalysisCount || 0}. Not benchmark eligible: {diagnosticEligibility.notBenchmarkEligibleAnalysisCount || 0}. Eligible cohorts: {diagnosticEligibility.benchmarkEligibleCohortCount || 0}.
                              </Typography>
                            </Alert>

                            {diagnosticEligibilityReasons.length ? (
                              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Reason</TableCell>
                                      <TableCell align="right">Count</TableCell>
                                      <TableCell>By version</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {diagnosticEligibilityReasons.map((row, index) => (
                                      <TableRow key={`${row.reason || 'reason'}-${index}`}>
                                        <TableCell sx={{ fontWeight: 700 }}>{formatValue(row.reason)}</TableCell>
                                        <TableCell align="right">{row.count || 0}</TableCell>
                                        <TableCell>{formatVersionCountMap(row.byVersion)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : null}

                            {diagnosticExcludedAnalyses.length ? (
                              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Version</TableCell>
                                      <TableCell>Cohorte</TableCell>
                                      <TableCell>Analysis</TableCell>
                                      <TableCell>Snapshot</TableCell>
                                      <TableCell>Reasons</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {diagnosticExcludedAnalyses.map((row, index) => (
                                      <TableRow key={`${row.analysisId || row.engineVersion || 'excluded'}-${index}`}>
                                        <TableCell sx={{ fontWeight: 700 }}>{formatValue(row.engineVersion, 'n/a').toUpperCase()}</TableCell>
                                        <TableCell>
                                          <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                                            {row.cohortKey || `${formatValue(row.symbol)} / ${formatValue(row.exchange)} / ${formatValue(row.timeframe)}`}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Closed candle: {formatDateTime(row.closedCandleTimestamp)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Analysis ID: {formatValue(row.analysisId, 'n/a')}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Execution ID: {formatValue(row.executionId, 'n/a')}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Execution status: {formatValue(row.executionStatus, 'n/a')}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Run: {formatValue(row.benchmarkRunId, 'n/a')}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Created: {formatDateTime(row.createdAt)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Source: {formatValue(row.analysisSource)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Input snapshot: {formatDateTime(row.inputSnapshotCandleTimestamp)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Hash: {formatValue(row.inputSnapshotHash)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Snapshot ID: {formatValue(row.inputSnapshotId)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Expected hash: {formatValue(row.expectedInputSnapshotHash)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Analysis timestamp: {formatDateTime(row.analysisTimestamp)}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                            Ledger link: {row.executionLinked ? 'YES' : 'NO'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>{formatEligibilityReasons(row.reasons)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : null}
                          </>
                        ) : null}

                        {diagnosticLineage ? (
                          <>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                              Lineage audit
                            </Typography>
                            <Alert
                              severity={diagnosticLineage.status === 'COMPLETE_LINEAGE' ? 'success' : diagnosticLineage.status === 'INVALID_COHORT' ? 'error' : 'warning'}
                              sx={{ borderRadius: 3 }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {diagnosticLineage.message}
                              </Typography>
                              <Typography variant="body2">
                                Scope: {formatValue(diagnosticLineage.scopeType, 'n/a')}. Historical analyses: {diagnosticLineage.historicalAnalysisCount || 0}. Complete lineage: {diagnosticLineage.completeLineageAnalysisCount || 0}. Eligible: {diagnosticLineage.benchmarkEligibleAnalysisCount || 0}. Ineligible: {diagnosticLineage.benchmarkIneligibleAnalysisCount || 0}.
                              </Typography>
                            </Alert>

                            <Box
                              sx={{
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                              }}
                            >
                              <MetricCard
                                label="Complete lineage"
                                value={diagnosticLineage.completeLineageAnalysisCount || 0}
                                tone={diagnosticLineage.completeLineageAnalysisCount ? 'success' : 'warning'}
                                helper={`Already coherent ${diagnosticLineage.alreadyCoherentAnalysisCount || 0}`}
                              />
                              <MetricCard
                                label="Missing snapshot hash"
                                value={diagnosticLineage.missingInputSnapshotHashCount || 0}
                                tone={diagnosticLineage.missingInputSnapshotHashCount ? 'warning' : 'success'}
                                helper={`Timestamp without hash ${diagnosticLineage.timestampWithoutHashCount || 0}`}
                              />
                              <MetricCard
                                label="Missing snapshot timestamp"
                                value={diagnosticLineage.missingInputSnapshotTimestampCount || 0}
                                tone={diagnosticLineage.missingInputSnapshotTimestampCount ? 'warning' : 'success'}
                                helper={`Snapshot not found ${diagnosticLineage.snapshotNotFoundCount || 0}`}
                              />
                              <MetricCard
                                label="Missing snapshot ID"
                                value={diagnosticLineage.missingInputSnapshotIdCount || 0}
                                tone={diagnosticLineage.missingInputSnapshotIdCount ? 'warning' : 'success'}
                                helper={`Hash without ID ${diagnosticLineage.hashWithoutSnapshotIdCount || 0}`}
                              />
                              <MetricCard
                                label="Repairable analyses"
                                value={diagnosticLineage.deterministicallyRepairableAnalysisCount || 0}
                                tone={diagnosticLineage.deterministicallyRepairableAnalysisCount ? 'accent' : 'default'}
                                helper={`Potential updates ${diagnosticLineage.potentialUpdateCount || 0}`}
                              />
                              <MetricCard
                                label="Not repairable"
                                value={diagnosticLineage.nonRepairableAnalysisCount || 0}
                                tone={diagnosticLineage.nonRepairableAnalysisCount ? 'warning' : 'success'}
                                helper={`Snapshot mismatches ${diagnosticLineage.snapshotHashMismatchCount || 0}`}
                              />
                              <MetricCard
                                label="Invalid snapshots"
                                value={diagnosticLineage.invalidSnapshotCount || 0}
                                tone={diagnosticLineage.invalidSnapshotCount ? 'danger' : 'success'}
                                helper={`Timestamp mismatches ${diagnosticLineage.snapshotTimestampMismatchCount || 0}`}
                              />
                              <MetricCard
                                label="Safe orphan snapshots"
                                value={diagnosticLineage.conservativeSafeToDeleteSnapshotCount || 0}
                                tone={diagnosticLineage.conservativeSafeToDeleteSnapshotCount ? 'accent' : 'default'}
                                helper={`Orphans ${diagnosticLineage.orphanSnapshotCount || 0} | Potential deletes ${diagnosticLineage.potentialDeleteCount || 0}`}
                              />
                            </Box>
                          </>
                        ) : null}

                        {diagnosticIntegrityInvariants.length ? (
                          <>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                              Integrity invariants
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Invariant</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Blocking</TableCell>
                                    <TableCell>Expected</TableCell>
                                    <TableCell>Actual</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {diagnosticIntegrityInvariants.map((row) => {
                                    const tone = resolveIntegrityTone(row);
                                    const color = tone === 'success'
                                      ? PAGE_COLORS.success
                                      : tone === 'danger'
                                        ? PAGE_COLORS.danger
                                        : tone === 'warning'
                                          ? PAGE_COLORS.warning
                                          : PAGE_COLORS.ink;
                                    return (
                                      <TableRow key={row.code}>
                                        <TableCell>
                                          <Stack spacing={0.25}>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                              {row.label}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                                              {row.code}
                                            </Typography>
                                          </Stack>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            size="small"
                                            icon={row.passed ? <CheckRoundedIcon /> : <CloseRoundedIcon />}
                                            label={row.passed ? 'PASS' : 'FAILED'}
                                            variant="outlined"
                                            sx={{
                                              borderColor: alpha(color, 0.28),
                                              bgcolor: alpha(color, 0.1),
                                              color,
                                              fontWeight: 700,
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>{row.blocking ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{formatValue(row.expected)}</TableCell>
                                        <TableCell>{formatValue(row.actual)}</TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        ) : null}

                        {diagnosticDuplicateAnalysisKeys.length ? (
                          <>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                              Duplicate analysis keys
                            </Typography>
                            <DuplicateAnalysisKeyTable
                              rows={diagnosticDuplicateAnalysisKeys}
                              emptyMessage="Aucune clé d analyse dupliquée n a ete détectée sur le benchmark courant."
                            />
                          </>
                        ) : null}

                        {diagnosticExecutionTotals ? (
                          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                            <MetricCard
                              label="State update anomalies"
                              value={`${diagnosticExecutionTotals.stateUpdateConflictCount || 0} / ${diagnosticExecutionTotals.stateUpdateRetryCount || 0} / ${diagnosticExecutionTotals.stateUpdateFailureCount || 0}`}
                              helper="Conflicts / retries / failures"
                              tone={diagnosticExecutionTotals.stateUpdateFailureCount ? 'danger' : diagnosticExecutionTotals.stateUpdateConflictCount ? 'warning' : 'success'}
                            />
                            <MetricCard
                              label="True timeouts / failures"
                              value={`${diagnosticExecutionTotals.trueEngineTimeoutCount || 0} / ${diagnosticExecutionTotals.engineFailureCount || 0}`}
                              helper="Engine timeouts / engine failures"
                              tone={diagnosticExecutionTotals.trueEngineTimeoutCount || diagnosticExecutionTotals.engineFailureCount ? 'warning' : 'success'}
                            />
                            <MetricCard
                              label="Queue wait"
                              value={formatPercentileTriplet(diagnosticExecutionTotals.queueWait)}
                              helper="p50 / p95 / p99"
                              tone="accent"
                            />
                            <MetricCard
                              label="Engine duration"
                              value={formatPercentileTriplet(diagnosticExecutionTotals.engineDuration)}
                              helper="p50 / p95 / p99"
                              tone="accent"
                            />
                            <MetricCard
                              label="Persistence duration"
                              value={formatPercentileTriplet(diagnosticExecutionTotals.persistenceDuration)}
                              helper="p50 / p95 / p99"
                              tone="default"
                            />
                          </Box>
                        ) : null}

                        {diagnosticExecutionRows.length ? (
                          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Version</TableCell>
                                  <TableCell align="right">Expected</TableCell>
                                  <TableCell align="right">Dispatched</TableCell>
                                  <TableCell align="right">Started</TableCell>
                                  <TableCell align="right">Executed</TableCell>
                                  <TableCell align="right">Missing</TableCell>
                                  <TableCell align="right">Extra</TableCell>
                                  <TableCell align="right">Completed</TableCell>
                                  <TableCell align="right">Failed</TableCell>
                                  <TableCell align="right">Timeout</TableCell>
                                  <TableCell align="right">Not executed</TableCell>
                                  <TableCell align="right">Detected analyses</TableCell>
                                  <TableCell align="right">Zero detection</TableCell>
                                  <TableCell align="right">Filtered</TableCell>
                                  <TableCell align="right">Persisted analyses</TableCell>
                                  <TableCell align="right">Persisted detections</TableCell>
                                  <TableCell align="right">Coverage</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {diagnosticExecutionRows.map((row) => (
                                  <TableRow key={row.engineVersion}>
                                    <TableCell sx={{ fontWeight: 800 }}>{formatValue(row.engineVersion, 'n/a').toUpperCase()}</TableCell>
                                    <TableCell align="right">{row.expectedCount || 0}</TableCell>
                                    <TableCell align="right">{row.dispatchedCount || 0}</TableCell>
                                    <TableCell align="right">{row.startedCount || 0}</TableCell>
                                    <TableCell align="right">{row.executedCount || 0}</TableCell>
                                    <TableCell align="right">{row.missingCount || 0}</TableCell>
                                    <TableCell align="right">{row.extraCount || 0}</TableCell>
                                    <TableCell align="right">{row.completedCount || 0}</TableCell>
                                    <TableCell align="right">{row.failedCount || 0}</TableCell>
                                    <TableCell align="right">{row.timeoutCount || 0}</TableCell>
                                    <TableCell align="right">{row.notExecutedCount || 0}</TableCell>
                                    <TableCell align="right">{row.detectedCount || 0}</TableCell>
                                    <TableCell align="right">{row.zeroDetectionCount || 0}</TableCell>
                                    <TableCell align="right">{row.filteredCount || 0}</TableCell>
                                    <TableCell align="right">{row.analysesPersistedCount || 0}</TableCell>
                                    <TableCell align="right">{row.detectionPersistedCount || 0}</TableCell>
                                    <TableCell align="right">{formatPercent(row.coverage)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : null}

                        {diagnosticExecutionIssues.length ? (
                          <>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                              Execution issues
                            </Typography>
                            <ExecutionIssueTable
                              rows={diagnosticExecutionIssues}
                              emptyMessage="Aucune anomalie d execution benchmark n a ete détectée sur le run courant."
                            />
                          </>
                        ) : null}

                        {diagnosticInvalidSnapshots.length ? (
                          <>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                              Invalid benchmark snapshots
                            </Typography>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Version</TableCell>
                                    <TableCell>Cohorte</TableCell>
                                    <TableCell>Analysis</TableCell>
                                    <TableCell>Snapshot</TableCell>
                                    <TableCell>Reason</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {diagnosticInvalidSnapshots.map((row, index) => (
                                    <TableRow key={`${row.engineVersion || 'unknown'}-${row.analysisTimestamp || index}`}>
                                      <TableCell sx={{ fontWeight: 700 }}>{formatValue(row.engineVersion, 'n/a').toUpperCase()}</TableCell>
                                      <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                                          {row.cohortKey || `${formatValue(row.symbol)} / ${formatValue(row.exchange)} / ${formatValue(row.timeframe)}`}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Closed candle: {formatDateTime(row.closedCandleTimestamp)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Analysis ID: {formatValue(row.analysisId, 'n/a')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Execution ID: {formatValue(row.executionId, 'n/a')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Execution status: {formatValue(row.executionStatus, 'n/a')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Run: {formatValue(row.benchmarkRunId, 'n/a')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Created: {formatDateTime(row.createdAt)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Source: {formatValue(row.analysisSource)}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Input snapshot: {formatDateTime(row.inputSnapshotCandleTimestamp)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Hash: {formatValue(row.inputSnapshotHash)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Snapshot ID: {formatValue(row.inputSnapshotId)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Expected hash: {formatValue(row.expectedInputSnapshotHash)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Analysis timestamp: {formatDateTime(row.analysisTimestamp)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, display: 'block' }}>
                                          Ledger link: {row.executionLinked ? 'YES' : 'NO'}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>{formatValue(row.reason)}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </>
                        ) : null}
                      </Stack>
                    </SectionCard>
                  ) : null}

                  {diagnosticExpansion ? (
                    <>
                      <SectionCard
                        icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                        title="Exact populations"
                        description={`Populations LEFT_ONLY / COMMON / RIGHT_ONLY et cohortes complètes LEFT_ALL / RIGHT_ALL calculées sur les snapshots historisés pour ${diagnosticVersionLabels.leftTag} vs ${diagnosticVersionLabels.rightTag}, sans relancer le scanner ni modifier le moteur.`}
                        action={diagnosticPopulationOptions.length ? (
                          <FormControl size="small" sx={{ minWidth: 170 }}>
                            <InputLabel>Population</InputLabel>
                            <Select label="Population" value={activeDiagnosticPopulation} onChange={(event) => setDiagnosticPopulation(event.target.value)}>
                              {diagnosticPopulationOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : null}
                      >
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(9, minmax(0, 1fr))' }, gap: 1.2 }}>
                            <MetricCard label={diagnosticVersionLabels.leftTag} value={diagnosticExpansionPopulation?.leftDetections || 0} />
                            <MetricCard label={diagnosticVersionLabels.rightTag} value={diagnosticExpansionPopulation?.rightDetections || 0} tone="accent" />
                            <MetricCard label={diagnosticVersionLabels.commonLabel} value={diagnosticExpansionPopulation?.commonCount || 0} tone="success" />
                            <MetricCard label={diagnosticVersionLabels.leftOnlyLabel} value={diagnosticExpansionPopulation?.leftOnlyCount || 0} tone="warning" />
                            <MetricCard label={diagnosticVersionLabels.rightOnlyLabel} value={diagnosticExpansionPopulation?.rightOnlyCount || 0} tone="warning" />
                            <MetricCard label={`${diagnosticVersionLabels.leftTag} coverage`} value={formatPercent(diagnosticExpansionPopulation?.leftCoverageOfRight)} helper={`common / ${diagnosticVersionLabels.rightTag}`} />
                            <MetricCard label={`${diagnosticVersionLabels.rightTag} coverage`} value={formatPercent(diagnosticExpansionPopulation?.rightCoverageOfLeft)} helper={`common / ${diagnosticVersionLabels.leftTag}`} />
                            <MetricCard label={`${diagnosticVersionLabels.leftTag}-only share`} value={formatPercent(diagnosticExpansionPopulation?.leftOnlyShare)} helper={`${diagnosticVersionLabels.leftOnlyLabel} / ${diagnosticVersionLabels.leftTag}`} tone="warning" />
                            <MetricCard label={`${diagnosticVersionLabels.rightTag}-only share`} value={formatPercent(diagnosticExpansionPopulation?.rightOnlyShare)} helper={`${diagnosticVersionLabels.rightOnlyLabel} / ${diagnosticVersionLabels.rightTag}`} tone="warning" />
                            <MetricCard label={`${diagnosticVersionLabels.rightTag} expansion`} value={`${formatDecimal(diagnosticExpansionPopulation?.rightExpansion, 1)}x`} helper={`${diagnosticVersionLabels.rightOnlyLabel} / ${diagnosticVersionLabels.leftTag}`} tone="accent" />
                          </Box>
                          {diagnosticExpansionAlerts.length ? diagnosticExpansionAlerts.map((alert) => (
                            <Alert key={alert} severity="warning" sx={{ borderRadius: 3 }}>{alert}</Alert>
                          )) : (
                            <Alert severity="info" sx={{ borderRadius: 3 }}>Aucune alerte analytique supplémentaire sur la cohorte courante.</Alert>
                          )}
                          {selectedPopulationSummary?.lowSample ? (
                            <Alert severity="warning" sx={{ borderRadius: 3 }}>
                              Echantillon &lt; {lowSampleThreshold}: les métriques de {formatPopulationLabel(activeDiagnosticPopulation, diagnosticVersionLabels)} restent descriptives et non robustes statistiquement.
                            </Alert>
                          ) : null}
                        </Stack>
                      </SectionCard>

                      <SectionCard
                        icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                        title="Pattern expansion"
                        description={`Pour chaque pattern: volume ${diagnosticVersionLabels.leftTag}, volume commun, détections unilatérales, score/MTF/confiance et résultats historiques des détections supplémentaires observées dans la cohorte ${diagnosticVersionLabels.leftTag} vs ${diagnosticVersionLabels.rightTag}.`}
                      >
                        <PatternExpansionTable
                          rows={diagnosticPatternExpansion}
                          lowSampleThreshold={lowSampleThreshold}
                          versionLabels={diagnosticVersionLabels}
                          rightFamilyVersions={diagnosticExpansion.rightFamilyVersions}
                        />
                      </SectionCard>

                      <SectionCard
                        icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                        title={`Population ${activeDiagnosticPopulationLabel}`}
                        description="Lecture rapide de la population sélectionnée avant d ouvrir les segmentations score / MTF / confiance / RR."
                      >
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(7, minmax(0, 1fr))' }, gap: 1.2 }}>
                          <MetricCard label="Détections" value={selectedPopulationSummary?.detections || 0} />
                          <MetricCard label="Snapshots" value={selectedPopulationSummary?.snapshotCount || 0} helper={`Resolved ${selectedPopulationSummary?.resolvedDetections || 0}`} />
                          <MetricCard label="Breakout" value={formatPercent(selectedPopulationSummary?.breakoutRate)} />
                          <MetricCard label="Target" value={formatPercent(selectedPopulationSummary?.targetRate)} tone="success" />
                          <MetricCard label="Invalidation" value={formatPercent(selectedPopulationSummary?.invalidationRate)} tone="danger" />
                          <MetricCard label="Unresolved" value={formatPercent(selectedPopulationSummary?.unresolvedRate)} tone="warning" />
                          <MetricCard label="Score" value={formatScore(selectedPopulationSummary?.averagePatternScore, 1)} />
                          <MetricCard label="MTF" value={formatScore(selectedPopulationSummary?.averageMtfScore, 1)} />
                          <MetricCard label="Confidence" value={formatPercent(selectedPopulationSummary?.averageConfidence)} />
                          <MetricCard label="Fakeout median" value={formatDecimal(selectedPopulationSummary?.medianFakeoutRisk, 1)} helper={`Mean ${formatDecimal(selectedPopulationSummary?.averageFakeoutRisk, 1)}`} tone="warning" />
                          <MetricCard label="Median breakout" value={formatDuration(selectedPopulationSummary?.medianTimeToBreakoutSeconds)} />
                          <MetricCard label="Median target" value={formatDuration(selectedPopulationSummary?.medianTimeToTargetSeconds)} tone="success" />
                          <MetricCard label="Median invalidation" value={formatDuration(selectedPopulationSummary?.medianTimeToInvalidationSeconds)} tone="danger" />
                          <MetricCard label="RR anomaly" value={formatPercent(selectedPopulationSummary?.rrAnomalyRate)} helper={`${selectedPopulationSummary?.rrAnomalyCount || 0} / ${selectedPopulationSummary?.calculableRrCount || 0}`} tone="warning" />
                        </Box>
                      </SectionCard>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                        <SectionCard
                          icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title={`${activeDiagnosticPopulationLabel} by pattern`}
                          description="Pattern performance sur la population sélectionnée, toujours accompagnée de la sample size."
                        >
                          <PopulationSegmentPerformanceTable rows={selectedPopulationDiagnostic?.byPattern} firstLabel="Pattern" lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucune agrégation pattern n est disponible sur cette population." />
                        </SectionCard>

                        <SectionCard
                          icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title={`${activeDiagnosticPopulationLabel} by timeframe`}
                          description="Répartition 1H / 4H / 1D avec breakout, target, invalidation, score, MTF et RR."
                        >
                          <PopulationSegmentPerformanceTable rows={selectedPopulationDiagnostic?.byTimeframe} firstLabel="Timeframe" lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucune agrégation timeframe n est disponible sur cette population." />
                        </SectionCard>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                        <SectionCard
                          icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title={`${activeDiagnosticPopulationLabel} by exchange`}
                          description="Détection d une éventuelle concentration du bruit par exchange et distribution des patterns dominants."
                        >
                          <PopulationExchangeTable rows={selectedPopulationDiagnostic?.byExchange} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucune agrégation exchange n est disponible sur cette population." />
                        </SectionCard>

                        <SectionCard
                          icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title={`${activeDiagnosticPopulationLabel} by symbol`}
                          description={`Volume ${activeDiagnosticPopulationLabel.toLowerCase()} par actif avec pattern dominant, score, MTF, confiance et breakout historique.`}
                        >
                          <PopulationSymbolTable rows={selectedPopulationDiagnostic?.bySymbol} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun symbol n est disponible sur cette population." />
                        </SectionCard>
                      </Box>

                      <SectionCard
                        icon={<CompareArrowsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                        title="Pattern + timeframe"
                        description="Combinaisons RECTANGLE / 4H, CHANNEL / 4H, RECTANGLE / 1H, etc. pour localiser l expansion V4-only."
                      >
                        <PopulationSegmentPerformanceTable rows={selectedPopulationDiagnostic?.byPatternTimeframe} firstLabel="Pattern / timeframe" lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucune combinaison pattern / timeframe n est disponible sur cette population." />
                      </SectionCard>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                        <SectionCard
                          icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title="Score Analysis"
                          description="Buckets officiels 0-49 / 50-59 / 60-69 / 70-79 / 80-89 / 90-100, sans nouveau seuil métier."
                        >
                          <BucketPerformanceTable rows={selectedPopulationDiagnostic?.scoreBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket score n est disponible sur cette population." />
                        </SectionCard>

                        <SectionCard
                          icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title="MTF Analysis"
                          description="Segmentation indépendante du score pattern, toujours avec sample size et taux historiques."
                        >
                          <BucketPerformanceTable rows={selectedPopulationDiagnostic?.mtfBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket MTF n est disponible sur cette population." />
                        </SectionCard>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                        <SectionCard
                          icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title="Confidence Calibration"
                          description="La confidence reste un score interne; cette vue mesure seulement sa cohérence historique par bucket."
                        >
                          <BucketPerformanceTable rows={selectedPopulationDiagnostic?.confidenceBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket confidence n est disponible sur cette population." />
                        </SectionCard>

                        <SectionCard
                          icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title="Pattern Score x MTF"
                          description="Matrice descriptive pour tester si score élevé + MTF élevé correspond réellement à de meilleurs résultats."
                        >
                          <DiagnosticScoreMtfMatrix cells={selectedPopulationDiagnostic?.scoreMtfMatrix} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucune matrice score x MTF n est disponible sur cette population." />
                        </SectionCard>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' }, gap: 1.5 }}>
                        <SectionCard
                          icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title="RR Analysis"
                          description="RR par population, distribution, concentrations d anomalies et attribution sur la population sélectionnée. La formule affichée reste la formule métier historisée."
                        >
                          <Stack spacing={1.5}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
                              <MetricCard label="RR calculable" value={selectedPopulationDiagnostic?.rrAudit?.calculableCount || 0} helper={`Unavailable ${selectedPopulationDiagnostic?.rrAudit?.unavailableCount || 0}`} />
                              <MetricCard label="RR anomaly" value={selectedPopulationDiagnostic?.rrAudit?.anomalyCount || 0} helper={formatPercent(selectedPopulationDiagnostic?.rrAudit?.anomalyRate)} tone="warning" />
                              <MetricCard label="RR median" value={formatDecimal(selectedPopulationSummary?.medianComputedRiskReward, 2)} />
                              <MetricCard label="RR mean" value={formatDecimal(selectedPopulationSummary?.averageComputedRiskReward, 2)} />
                            </Box>
                            <Alert severity="info" sx={{ borderRadius: 3 }}>
                              {selectedPopulationDiagnostic?.rrAudit?.formula || diagnosticRrAudit?.formula || 'Formule RR indisponible.'}
                            </Alert>
                            <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                              RR par population
                            </Typography>
                            <PopulationRrSummaryTable rows={diagnosticRrByPopulation} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucune attribution RR par population n est disponible." versionLabels={diagnosticVersionLabels} />
                            <Divider />
                            <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                              Distribution du RR calculé
                            </Typography>
                            <CategoryCountTable rows={selectedPopulationDiagnostic?.rrAudit?.distribution} emptyMessage="Aucune distribution RR n est disponible sur cette population." />
                            <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                              RR anomaly attribution
                            </Typography>
                            <CategoryCountTable rows={selectedPopulationDiagnostic?.rrAudit?.byPattern} emptyMessage="Aucune attribution RR par pattern." />
                            <CategoryCountTable rows={selectedPopulationDiagnostic?.rrAudit?.byTimeframe} emptyMessage="Aucune attribution RR par timeframe." />
                            <CategoryCountTable rows={selectedPopulationDiagnostic?.rrAudit?.bySymbol} emptyMessage="Aucune attribution RR par symbol." />
                          </Stack>
                        </SectionCard>

                        <SectionCard
                          icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                          title="Potential calibration issues"
                          description="Score < 60 et confidence >= 80 restent des cas suspects analytiques, jamais un nouveau seuil métier."
                        >
                          <PotentialCalibrationIssueTable rows={selectedPopulationDiagnostic?.potentialCalibrationIssues} showPopulation emptyMessage="Aucun cas score faible / confidence élevée n est ressorti sur cette population." versionLabels={diagnosticVersionLabels} />
                        </SectionCard>
                      </Box>

                      {diagnosticRectangle ? (
                        <>
                          <SectionCard
                            icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                            title="RECTANGLE DIAGNOSTIC"
                            description="Inspection dédiée des Rectangles V4 pour distinguer bruit potentiel, signal valide mal confirmé, ou problème de calibration du score / de la confiance."
                          >
                            <Stack spacing={1.5}>
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 1.2 }}>
                                <MetricCard label="N" value={diagnosticRectangle.summary?.detections || 0} />
                                <MetricCard label="Breakout" value={formatPercent(diagnosticRectangle.summary?.breakoutRate)} tone="warning" />
                                <MetricCard label="Target" value={formatPercent(diagnosticRectangle.summary?.targetRate)} tone="success" />
                                <MetricCard label="Score" value={formatScore(diagnosticRectangle.qualityProfile?.averagePatternScore, 1)} />
                                <MetricCard label="MTF" value={formatScore(diagnosticRectangle.qualityProfile?.averageMtfScore, 1)} />
                                <MetricCard label="Confidence" value={formatPercent(diagnosticRectangle.qualityProfile?.averageConfidence)} />
                                <MetricCard label="Geometry" value={formatPercent(diagnosticRectangle.qualityProfile?.averageGeometryConfidence)} />
                                <MetricCard label="Structure" value={formatPercent(diagnosticRectangle.qualityProfile?.averageStructureQuality)} />
                                <MetricCard label="Volume conf." value={formatPercent(diagnosticRectangle.qualityProfile?.volumeConfirmationRate)} />
                                <MetricCard label="Fakeout" value={formatDecimal(diagnosticRectangle.qualityProfile?.averageFakeoutRisk, 1)} tone="warning" />
                              </Box>
                              {diagnosticRectangle.summary?.lowSample ? (
                                <Alert severity="warning" sx={{ borderRadius: 3 }}>Echantillon &lt; {lowSampleThreshold}: le profil Rectangle reste descriptif.</Alert>
                              ) : null}
                            </Stack>
                          </SectionCard>

                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                            <SectionCard
                              icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                              title="Rectangle quality profile"
                              description="Geometry confidence, structure quality, confidence, target confidence, volume et fakeout sur la population Rectangle V4."
                            >
                              <Stack spacing={1.5}>
                                <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                                  Geometry confidence
                                </Typography>
                                <BucketPerformanceTable rows={diagnosticRectangle.geometryConfidenceBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket geometry confidence n est disponible." />
                                <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                                  Structure quality
                                </Typography>
                                <BucketPerformanceTable rows={diagnosticRectangle.structureQualityBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket structure quality n est disponible." />
                                <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                                  Confidence / target confidence
                                </Typography>
                                <BucketPerformanceTable rows={diagnosticRectangle.confidenceBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket confidence n est disponible pour Rectangle." />
                                <BucketPerformanceTable rows={diagnosticRectangle.targetConfidenceBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket target confidence n est disponible pour Rectangle." />
                                <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                                  Volume / trend
                                </Typography>
                                <CategoryCountTable rows={diagnosticRectangle.volumeConfirmationStates} emptyMessage="Aucune ventilation volume confirmation n est disponible." />
                                <CategoryCountTable rows={diagnosticRectangle.volumeTrendStates} emptyMessage="Aucune ventilation volume trend n est disponible." />
                              </Stack>
                            </SectionCard>

                            <SectionCard
                              icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                              title="Rectangle score / MTF / fakeout"
                              description="Score buckets, MTF buckets, fakeout et matrice score x MTF pour tester la calibration Rectangle sans introduire de nouveau seuil."
                            >
                              <Stack spacing={1.5}>
                                <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                                  Score buckets
                                </Typography>
                                <BucketPerformanceTable rows={diagnosticRectangle.scoreBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket score Rectangle n est disponible." />
                                <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                                  MTF buckets
                                </Typography>
                                <BucketPerformanceTable rows={diagnosticRectangle.mtfBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket MTF Rectangle n est disponible." />
                                <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                                  Fakeout risk
                                </Typography>
                                <BucketPerformanceTable rows={diagnosticRectangle.fakeoutRiskBuckets} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucun bucket fakeout Rectangle n est disponible." />
                                <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                                  Score x MTF
                                </Typography>
                                <DiagnosticScoreMtfMatrix cells={diagnosticRectangle.scoreMtfMatrix} lowSampleThreshold={lowSampleThreshold} emptyMessage="Aucune matrice score x MTF Rectangle n est disponible." />
                              </Stack>
                            </SectionCard>
                          </Box>

                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' }, gap: 1.5 }}>
                            <SectionCard
                              icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                              title="Rectangle structural inspection"
                              description="Touches support / resistance, pivots, slopes, compression, durée et métriques géométriques réellement présentes dans l historique."
                            >
                              <RectangleStructureTable rows={diagnosticRectangle.structures} emptyMessage="Aucune structure Rectangle historisée n est disponible." />
                            </SectionCard>

                            <SectionCard
                              icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                              title="Rectangle potential calibration issues"
                              description="Sous-liste prioritaire des Rectangles avec score faible et confidence élevée, sans les qualifier automatiquement de faux positifs."
                            >
                              <PotentialCalibrationIssueTable rows={diagnosticRectangle.suspectDetections} emptyMessage="Aucun Rectangle suspect n a ete isolé sur la cohorte courante." />
                            </SectionCard>
                          </Box>
                        </>
                      ) : null}
                    </>
                  ) : null}

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                    <SectionCard
                      icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title="Population brute"
                      description="Cohorte filtrée avant alignement `same cohort`."
                    >
                      <DiagnosticVersionTable
                        rowsByVersion={diagnostic.byVersion}
                        versions={diagnosticVersions}
                        emptyMessage="Aucune version n est disponible dans la cohorte filtrée."
                      />
                    </SectionCard>

                    <SectionCard
                      icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title="Same cohort"
                      description="Même symbole, même exchange, même timeframe et même bougie fermée dans les deux versions."
                    >
                      <Stack spacing={1.2}>
                        {!diagnosticComparisonAvailable ? (
                          <Alert severity="warning">Les taux comparatifs passent à n/a tant qu aucune cohorte commune V3/V4 n existe.</Alert>
                        ) : null}
                        <DiagnosticVersionTable
                          rowsByVersion={diagnostic.sameCohortByVersion}
                          versions={diagnosticVersions}
                          comparisonAvailable={diagnosticComparisonAvailable}
                          emptyMessage="Aucun snapshot partagé entre V3 et V4 sur cette cohorte."
                        />
                      </Stack>
                    </SectionCard>
                  </Box>

                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Duplicate cohort candidates"
                    description="Détections multiples pour une même version et une même clé logique symbole / exchange / timeframe / closed candle. Aucun nettoyage automatique n est appliqué ici."
                  >
                    <DuplicateCohortTable rows={diagnosticDuplicates?.candidates} emptyMessage="Aucun doublon de cohorte n a ete detecte sur la population courante." />
                  </SectionCard>

                  <SectionCard
                    icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Common snapshots"
                    description={`Classification BOTH_AGREE / BOTH_DIFFER / ${diagnosticVersionLabels.leftTag}_ONLY / ${diagnosticVersionLabels.rightTag}_ONLY à l’échelle du même market snapshot.`}
                  >
                    <DiagnosticSnapshotDiffTable rows={diagnostic.snapshotDiff} emptyMessage="Aucun diff de snapshot n est disponible." versionLabels={diagnosticVersionLabels} />
                  </SectionCard>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                    <SectionCard
                      icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title="Par pattern"
                      description="Breakdowns same cohort par pattern réel détecté. Taux affichés sur toutes les détections du segment."
                    >
                      <DiagnosticSegmentTable rows={diagnosticPatterns} versions={diagnosticVersions} comparisonAvailable={diagnosticComparisonAvailable} emptyMessage="Aucun segment pattern n est disponible." />
                    </SectionCard>

                    <SectionCard
                      icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title="Par timeframe"
                      description="Breakdowns same cohort par timeframe."
                    >
                      <DiagnosticSegmentTable rows={diagnosticTimeframes} versions={diagnosticVersions} comparisonAvailable={diagnosticComparisonAvailable} emptyMessage="Aucun segment timeframe n est disponible." />
                    </SectionCard>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                    <SectionCard
                      icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title="Par asset et score"
                      description="Vue same cohort par actif, puis par bucket de score pattern historisé."
                    >
                      <Stack spacing={2}>
                        <DiagnosticSegmentTable rows={diagnosticAssets} versions={diagnosticVersions} comparisonAvailable={diagnosticComparisonAvailable} emptyMessage="Aucun segment asset n est disponible." />
                        <Divider />
                        <DiagnosticSegmentTable rows={diagnosticScoreBuckets} versions={diagnosticVersions} comparisonAvailable={diagnosticComparisonAvailable} emptyMessage="Aucun bucket score n est disponible." />
                      </Stack>
                    </SectionCard>

                    <SectionCard
                      icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title="MTF, volume, fakeout, maturité"
                      description="Le bucket maturité décrit la cohorte brute, les autres tableaux restent ancrés sur le same cohort."
                    >
                      <Stack spacing={2}>
                        <DiagnosticSegmentTable rows={diagnosticMtfBuckets} versions={diagnosticVersions} comparisonAvailable={diagnosticComparisonAvailable} emptyMessage="Aucun bucket MTF n est disponible." />
                        <Divider />
                        <DiagnosticSegmentTable rows={diagnosticVolumeStates} versions={diagnosticVersions} comparisonAvailable={diagnosticComparisonAvailable} emptyMessage="Aucun segment volume n est disponible." />
                        <Divider />
                        <DiagnosticSegmentTable rows={diagnosticFakeoutBuckets} versions={diagnosticVersions} comparisonAvailable={diagnosticComparisonAvailable} emptyMessage="Aucun segment fakeout n est disponible." />
                        <Divider />
                        <DiagnosticSegmentTable rows={diagnosticMaturityBuckets} versions={diagnosticVersions} comparisonAvailable={diagnosticComparisonAvailable} emptyMessage="Aucun bucket maturité n est disponible." />
                      </Stack>
                    </SectionCard>
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' }, gap: 1.5 }}>
                    <SectionCard
                      icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title="Audit des niveaux"
                      description="Mesure historique des niveaux stockés sans recalcul du moteur."
                    >
                      <LevelAuditTable rows={diagnosticLevels} emptyMessage="Aucune ligne d audit des niveaux n est disponible." />
                    </SectionCard>

                    <SectionCard
                      icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                      title="Audit RR"
                      description="RR recalculé à partir des niveaux stockés, avec repérage des cas proches de 0.9259."
                    >
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
                          <MetricCard label="RR calculé moyen" value={formatDecimal(diagnosticRrAudit?.averageComputedRiskReward, 2)} />
                          <MetricCard label="RR stocké moyen" value={formatDecimal(diagnosticRrAudit?.averageStoredRiskReward, 2)} />
                          <MetricCard label="RR calculable" value={diagnosticRrAudit?.calculableCount || 0} helper={`RR unavailable ${diagnosticRrAudit?.unavailableCount || 0}`} tone="accent" />
                          <MetricCard label="Anomalies RR" value={diagnosticRrAudit?.anomalyCount || 0} tone="warning" helper={formatPercent(diagnosticRrAudit?.anomalyRate)} />
                          <MetricCard label="Référence anomalie" value={formatDecimal(diagnosticRrAudit?.anomalyReference, 4)} helper={`Tolérance ±${formatDecimal(diagnosticRrAudit?.anomalyTolerance, 4)}`} tone="accent" />
                        </Box>
                        <Alert severity="info" sx={{ borderRadius: 3 }}>
                          {diagnosticRrAudit?.formula || 'Formule RR indisponible.'}
                        </Alert>
                        <Divider />
                        <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                          Distribution du RR calculé
                        </Typography>
                        <CategoryCountTable rows={diagnosticRrAudit?.distribution} emptyMessage="Aucune distribution RR n est disponible." />
                        <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                          Raisons RR unavailable
                        </Typography>
                        <CategoryCountTable rows={diagnosticRrAudit?.unavailableReasons} emptyMessage="Tous les RR calculables disposent des niveaux requis." />
                        <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                          Concentration des anomalies par timeframe
                        </Typography>
                        <CategoryCountTable rows={diagnosticRrAudit?.byTimeframe} emptyMessage="Aucune anomalie RR par timeframe." />
                        <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                          Concentration des anomalies par pattern
                        </Typography>
                        <CategoryCountTable rows={diagnosticRrAudit?.byPattern} emptyMessage="Aucune anomalie RR par pattern." />
                        <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                          Concentration des anomalies par symbol
                        </Typography>
                        <CategoryCountTable rows={diagnosticRrAudit?.bySymbol} emptyMessage="Aucune anomalie RR par symbol." />
                        <Typography variant="subtitle2" sx={{ color: PAGE_COLORS.ink, fontWeight: 800 }}>
                          Cas RR proches de 0.9259
                        </Typography>
                        <LevelAuditTable rows={diagnosticRrAudit?.anomalies} emptyMessage="Aucun cas RR proche de 0.9259." />
                      </Stack>
                    </SectionCard>
                  </Box>
                </Stack>
              )
            ) : null}

            {activeTab === 'patterns' ? (
              <Stack spacing={1.5}>
                <SectionCard
                  icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Performance par pattern"
                  description="Vue consolidée des familles les plus fiables, avec score moyen et version dominante."
                >
                  {summary.patterns?.length ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800 }}>Pattern</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Quality</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Score moyen</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>MTF moyen</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Best version</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {summary.patterns.map((row) => (
                            <TableRow key={row.pattern} hover>
                              <TableCell>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.pattern}</Typography>
                                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                </Stack>
                              </TableCell>
                              <TableCell>{row.detections}</TableCell>
                              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
                              <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
                              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
                              <TableCell>{formatPercent(row.qualityScore)}</TableCell>
                              <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
                              <TableCell>{formatScore(row.averageMtfScore, 1)}</TableCell>
                              <TableCell>{formatDuration(row.medianTimeToTargetSeconds)}</TableCell>
                              <TableCell>{formatValue(row.bestEngineVersion)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">Aucun pattern ne correspond aux filtres actifs.</Alert>
                  )}
                </SectionCard>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Pattern × bucket score"
                    description="Permet d identifier les patterns qui gardent un target rate stable selon le score brut."
                  >
                    <GroupedMetricsTable rows={summary.patternScoreBuckets} groupLabel="Pattern" lowSampleThreshold={lowSampleThreshold} />
                  </SectionCard>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Pattern × bucket MTF"
                    description="Lecture croisée du pattern et de son contexte multi-timeframe historisé."
                  >
                    <GroupedMetricsTable rows={summary.patternMtfBuckets} groupLabel="Pattern" lowSampleThreshold={lowSampleThreshold} />
                  </SectionCard>
                </Box>
              </Stack>
            ) : null}

            {activeTab === 'versions' ? (
              <Stack spacing={1.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<CompareArrowsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Cohorte par version"
                    description="Lecture complète des versions présentes dans la cohorte, même hors benchmark ciblé."
                  >
                    {summary.versions?.length ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Score moyen</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>MTF médian</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summary.versions.map((row) => (
                              <TableRow key={row.engineVersion} hover>
                                <TableCell>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.engineVersion}</Typography>
                                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                  </Stack>
                                </TableCell>
                                <TableCell>{row.detections}</TableCell>
                                <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
                                <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
                                <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
                                <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
                                <TableCell>{formatScore(row.medianMtfScore, 1)}</TableCell>
                                <TableCell>{formatDuration(row.medianTimeToTargetSeconds)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">Aucune version n est disponible pour la cohorte courante.</Alert>
                    )}
                  </SectionCard>

                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Benchmark ciblé"
                    description="Compare les versions demandées à iso-filtres pour juger le drift ou le gain."
                  >
                    <Stack spacing={1.2}>
                      <BenchmarkOrchestrationNotice />
                      {compareRows.length === 0 ? (
                        <Alert severity="info">Aucune comparaison ciblée n est disponible.</Alert>
                      ) : (
                        <Stack spacing={1.2}>
                          {compareRows.map((row) => (
                            <Paper key={row.engineVersion} variant="outlined" sx={{ p: 1.8, borderRadius: 3 }}>
                              <Stack spacing={1.1}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{row.engineVersion}</Typography>
                                    <Chip size="small" label={`${row.detections} détection(s)`} sx={{ fontWeight: 700 }} />
                                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                  </Stack>
                                  <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>
                                    Quality {formatPercent(row.qualityScore)}
                                  </Typography>
                                </Stack>
                                <RateBar label="Breakout" value={row.breakoutRate} color={PAGE_COLORS.inkSoft} />
                                <RateBar label="Target final" value={row.targetRate} color={PAGE_COLORS.success} />
                                <RateBar label="Invalidation" value={row.invalidationRate} color={PAGE_COLORS.danger} />
                                <RateBar label="Expiration" value={row.expirationRate} color={PAGE_COLORS.warning} />
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                                  <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>Score moyen: <strong>{formatScore(row.averagePatternScore, 1)}</strong></Typography>
                                  <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>MTF moyen: <strong>{formatScore(row.averageMtfScore, 1)}</strong></Typography>
                                  <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>Median target: <strong>{formatDuration(row.medianTimeToTargetSeconds)}</strong></Typography>
                                  <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>Cohorte: <strong>{formatDateTime(row.cohortStart)} → {formatDateTime(row.cohortEnd)}</strong></Typography>
                                </Box>
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </SectionCard>
                </Box>

                <SectionCard
                  icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Version × bucket score"
                  description="Répartition historique du target rate par version et tranche de score."
                >
                  <GroupedMetricsTable rows={summary.versionScoreBuckets} groupLabel="Version" lowSampleThreshold={lowSampleThreshold} />
                </SectionCard>

                <SectionCard
                  icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Axes V3 par version"
                  description="Compare la qualité pattern, la qualité breakout, le biais directionnel et la confiance setup par version historisée."
                >
                  <V3AggregateTable
                    rows={v3VersionRows}
                    groupLabel="Version"
                    lowSampleThreshold={lowSampleThreshold}
                    emptyMessage="Aucun agrégat V3 par version n est disponible pour cette cohorte."
                  />
                </SectionCard>
              </Stack>
            ) : null}

            {activeTab === 'timeframes' ? (
              <Stack spacing={1.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Cohorte par timeframe"
                    description="Lecture directe de la qualité historique par horizon d exécution."
                  >
                    {summary.timeframes?.length ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 800 }}>Timeframe</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Score moyen</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>MTF moyen</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summary.timeframes.map((row) => (
                              <TableRow key={row.timeframe} hover>
                                <TableCell>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.timeframe}</Typography>
                                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                  </Stack>
                                </TableCell>
                                <TableCell>{row.detections}</TableCell>
                                <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
                                <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
                                <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
                                <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
                                <TableCell>{formatScore(row.averageMtfScore, 1)}</TableCell>
                                <TableCell>{formatDuration(row.medianTimeToTargetSeconds)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">Aucun timeframe n est disponible pour la cohorte courante.</Alert>
                    )}
                  </SectionCard>

                  <SectionCard
                    icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Répartition par timeframe"
                    description="Compare les buckets score et MTF à l intérieur de chaque horizon temporel." 
                  >
                    <Stack spacing={1.2}>
                      <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>
                        Les timeframes courts et longs n ont pas le même profil de résolution. Cette vue permet de lire le drift de qualité par horizon, sans mélanger 1H, 4H et 1D.
                      </Typography>
                      <RateBar label="Breakout global" value={global?.breakoutRate} color={PAGE_COLORS.inkSoft} />
                      <RateBar label="Target final global" value={global?.targetFinalRate} color={PAGE_COLORS.success} />
                      <RateBar label="Invalidation globale" value={global?.invalidationRate} color={PAGE_COLORS.danger} />
                    </Stack>
                  </SectionCard>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Timeframe × bucket score"
                    description="Répartition historique du target rate par timeframe et tranche de score pattern."
                  >
                    <GroupedMetricsTable rows={summary.timeframeScoreBuckets} groupLabel="Timeframe" lowSampleThreshold={lowSampleThreshold} />
                  </SectionCard>

                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Timeframe × bucket MTF"
                    description="Lecture croisée du timeframe et du score multi-timeframe historisé."
                  >
                    <GroupedMetricsTable rows={summary.timeframeMtfBuckets} groupLabel="Timeframe" lowSampleThreshold={lowSampleThreshold} />
                  </SectionCard>
                </Box>

                <SectionCard
                  icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Axes V3 par timeframe"
                  description="Lit la séparation Pattern Quality / Breakout Quality / Directional Bias / Setup Confidence selon l horizon temporel."
                >
                  <V3AggregateTable
                    rows={v3TimeframeRows}
                    groupLabel="Timeframe"
                    lowSampleThreshold={lowSampleThreshold}
                    emptyMessage="Aucun agrégat V3 par timeframe n est disponible pour cette cohorte."
                  />
                </SectionCard>
              </Stack>
            ) : null}

            {activeTab === 'v3' ? (
              <Stack spacing={1.5}>
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Les axes V3 sont lus uniquement depuis les snapshots qui exposent diagnostics.v3. Les versions legacy restent visibles dans le comparatif avec une couverture à 0 et des métriques n/a tant qu aucun snapshot V3 n existe pour elles.
                </Alert>

                {v3Global ? (
                  <SectionCard
                    icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Synthèse V3"
                    description="Vue consolidée des quatre axes V3 et de leur couverture sur la cohorte active."
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 1.2 }}>
                      <MetricCard label="Couverture V3" value={formatPercent(v3Global.coverageRate)} helper={formatV3CoverageHelper(v3Global, global?.detections)} tone="accent" />
                      <MetricCard label="Qualité pattern V3" value={formatScore(v3Global.averagePatternQualityScore, 1)} helper={formatV3CoverageHelper(v3Global, global?.detections)} tone="accent" />
                      <MetricCard label="Qualité breakout V3" value={formatScore(v3Global.averageBreakoutQualityScore, 1)} helper={formatV3CoverageHelper(v3Global, global?.detections)} tone="success" />
                      <MetricCard label="Biais directionnel V3" value={formatV3AggregateBiasValue(v3Global)} helper={formatV3AggregateBiasHelper(v3Global)} tone="accent" />
                      <MetricCard label="Confiance setup V3" value={formatScore(v3Global.averageSetupConfidenceScore, 1)} helper={formatV3CoverageHelper(v3Global, global?.detections)} />
                    </Box>
                  </SectionCard>
                ) : null}

                <SectionCard
                  icon={<CompareArrowsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Comparatif V1 / V3 / V4"
                  description="Prépare la comparaison transverse des versions sur les axes V3 séparés, sans rétrofiter les snapshots anciens."
                >
                  <V3AggregateTable
                    rows={comparisonV3Rows}
                    groupLabel="Version"
                    lowSampleThreshold={lowSampleThreshold}
                    emptyMessage="Aucune version n est comparable sur la cohorte actuelle."
                  />
                </SectionCard>

                <SectionCard
                  icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Lecture V3 par timeframe"
                  description="Permet de comparer les quatre axes V3 selon l horizon 1H, 4H ou 1D."
                >
                  <V3AggregateTable
                    rows={v3TimeframeRows}
                    groupLabel="Timeframe"
                    lowSampleThreshold={lowSampleThreshold}
                    emptyMessage="Aucun timeframe V3 n est disponible pour la cohorte actuelle."
                  />
                </SectionCard>
              </Stack>
            ) : null}

            {activeTab === 'score' ? (
              <Stack spacing={1.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Buckets score pattern"
                    description="Calibration brute du score pattern sans pondération artificielle des statistiques."
                  >
                    <BucketMetricsTable rows={summary.scoreBuckets} lowSampleThreshold={lowSampleThreshold} firstLabel="Bucket score" />
                  </SectionCard>
                  <SectionCard
                    icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Score × confirmation volume"
                    description="Permet de vérifier si la confirmation volume améliore ou non la qualité du score."
                  >
                    {summary.scoreVolumeBuckets?.length ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 800 }}>Bucket</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Volume</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summary.scoreVolumeBuckets.map((row) => (
                              <TableRow key={`${row.scoreBucket}-${row.volumeLabel}`} hover>
                                <TableCell>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.scoreBucket}</Typography>
                                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                  </Stack>
                                </TableCell>
                                <TableCell>{formatValue(row.volumeLabel)}</TableCell>
                                <TableCell>{row.detections}</TableCell>
                                <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
                                <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">Aucune coupe volume n est encore disponible.</Alert>
                    )}
                  </SectionCard>
                </Box>

                <SectionCard
                  icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Calibration score pattern"
                  description="Lecture bucket par bucket pour voir si un score plus élevé correspond réellement à de meilleurs outcomes."
                >
                  <Stack spacing={1.2}>
                    {scoreCalibration.map((row) => (
                      <Paper key={row.bucket} variant="outlined" sx={{ p: 1.6, borderRadius: 3, bgcolor: PAGE_COLORS.sand }}>
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
                          <Box sx={{ minWidth: 140 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{row.bucket}</Typography>
                            <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>{row.sampleSize} détection(s)</Typography>
                            <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <RateBar label="Breakout" value={row.breakoutRate} color={PAGE_COLORS.inkSoft} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <RateBar label="Target final" value={row.targetRate} color={PAGE_COLORS.success} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <RateBar label="Invalidation" value={row.invalidationRate} color={PAGE_COLORS.danger} />
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </SectionCard>
              </Stack>
            ) : null}

            {activeTab === 'mtf' ? (
              <Stack spacing={1.5}>
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Le score MTF reflète la reconstruction historique du contexte 1H / 4H / 1D. Quand ce contexte n est pas disponible, le bucket reste UNKNOWN et n est jamais converti en 0.
                </Alert>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '0.9fr 1.1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Buckets score MTF"
                    description="Répartition des outcomes par tranche de score multi-timeframe historisé."
                  >
                    <BucketMetricsTable rows={summary.mtfBuckets} lowSampleThreshold={lowSampleThreshold} firstLabel="Bucket MTF" />
                  </SectionCard>
                  <SectionCard
                    icon={<CompareArrowsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Matrice score × MTF"
                    description="Croise le score pattern brut et le score MTF pour repérer les zones de cohérence ou de conflit."
                  >
                    <ScoreMtfMatrix cells={summary.scoreMtfMatrix} lowSampleThreshold={lowSampleThreshold} />
                  </SectionCard>
                </Box>
              </Stack>
            ) : null}

            {activeTab === 'calibration' ? (
              <Stack spacing={1.5}>
                {calibrationError ? <Alert severity="warning">{calibrationError}</Alert> : null}
                <CalibrationPanel
                  title="Calibration du score pattern"
                  description="Mesure si le score pattern historisé est réellement prédictif des outcomes."
                  rows={scoreCalibration}
                  lowSampleThreshold={lowSampleThreshold}
                />
                <CalibrationPanel
                  title="Calibration du score MTF"
                  description="Mesure l apport du contexte multi-timeframe indépendamment du score brut."
                  rows={mtfCalibration}
                  lowSampleThreshold={lowSampleThreshold}
                />
                <CalibrationPanel
                  title="Calibration de la confiance"
                  description="Met en regard la confiance, le target rate et l invalidation sur l historique réel."
                  rows={confidenceCalibration}
                  lowSampleThreshold={lowSampleThreshold}
                />
              </Stack>
            ) : null}

            <SectionCard
              icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
              title="Dernières détections trouvées"
              description="Tableau brut des dernières détections historisées de la cohorte active, affiché en bas de page pour garder un point d entrée rapide sur les derniers cas vus par le scanner."
            >
              <RecentDetectionsTable
                rows={latestDetections}
                onOpen={openDetail}
                emptyMessage="Aucune détection récente n est disponible pour la cohorte actuelle."
              />
            </SectionCard>
          </>
        )}

        <Dialog open={Boolean(detail) || detailLoading || Boolean(detailError)} onClose={closeDetail} maxWidth="lg" fullWidth>
          <DialogTitle>Timeline de détection pattern</DialogTitle>
          <DialogContent dividers>
            {detailLoading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress sx={{ color: '#19323c', mb: 1.5 }} />
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Chargement du détail de détection...
                </Typography>
              </Box>
            ) : detailError ? (
              <Alert severity="error">{detailError}</Alert>
            ) : !detail ? (
              <Alert severity="info">Aucune détection sélectionnée.</Alert>
            ) : (
              <Stack spacing={2.5}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                        {detail.detection.symbol} / {detail.detection.exchange} / {detail.detection.timeframe}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f646f', mt: 0.5 }}>
                        Pattern {detail.detection.pattern} · Type {formatValue(detail.detection.assetType, 'UNKNOWN')} · Version {detail.detection.engineVersion} · Confiance {formatPercent(detail.detection.patternConfidence)} · Score {formatScore(detail.detection.patternScore)} · Score MTF {formatScore(detail.detection.multiTimeframeScore)}
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.2 }}>
                        <Chip label={`Biais ${formatCanonicalBiasValue(detailCanonicalBias)}`} sx={{ fontWeight: 700, bgcolor: alpha(PAGE_COLORS.inkSoft, 0.12), color: PAGE_COLORS.ink }} />
                        <Chip label={formatCanonicalBreakoutValue(detailCanonicalBreakout)} sx={{ fontWeight: 700, bgcolor: alpha(PAGE_COLORS.accent, 0.14), color: PAGE_COLORS.ink }} />
                        {detailCoherence ? (
                          <Chip label={`Cohérence ${formatCanonicalCoherenceValue(detailCoherence)}`} sx={{ fontWeight: 700, bgcolor: alpha(PAGE_COLORS.success, 0.1), color: PAGE_COLORS.ink }} />
                        ) : null}
                      </Stack>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Chip label={`Statut ${detail.evaluation.status}`} sx={{ fontWeight: 700 }} />
                      <Chip label={`Détection ${formatDateTime(detail.detection.detectionCandleAt)}`} sx={{ fontWeight: 700 }} />
                    </Stack>
                  </Stack>
                </Paper>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
                  <MetricCard label="Biais canonique" value={formatCanonicalBiasValue(detailCanonicalBias)} helper={formatCanonicalBiasHelper(detailCanonicalBias)} />
                  <MetricCard label="Cassure canonique" value={formatCanonicalBreakoutValue(detailCanonicalBreakout)} helper={formatCanonicalBreakoutHelper(detailCanonicalBreakout)} tone="accent" />
                  <MetricCard label="Score pattern" value={formatScore(detail.detection.patternScore)} helper={`Conviction ${formatScore(detail.detection.convictionScore)}`} />
                  <MetricCard label="Score MTF" value={formatScore(detail.detection.multiTimeframeScore)} helper={`Alignement ${formatValue(detail.detection.mtfAlignmentState, 'UNKNOWN')}`} tone="accent" />
                  <MetricCard label="Type actif" value={formatValue(detail.detection.assetType, 'UNKNOWN')} helper={`Version ${formatValue(detail.detection.engineVersion)}`} />
                  <MetricCard label="Confiance cible" value={formatPercent(detail.detection.targetConfidence)} helper={`Volume ${detail.detection.volumeConfirmation === true ? 'confirmé' : detail.detection.volumeConfirmation === false ? 'non confirmé' : 'n/a'}`} />
                  <MetricCard label="Volume aligné" value={formatScore(detail.detection.volumeAlignmentScore)} helper={`Ratio ${formatDecimal(detail.detection.breakoutVolumeRatio, 2)}`} />
                </Box>

                {detailV3Metadata ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 1.2 }}>
                    <MetricCard label="Pattern sélectionné V3" value={formatValue(detailV3Metadata.selectedPattern)} helper={formatV3RenderingHelper(detailV3Metadata.rendering)} />
                    <MetricCard label="Qualité pattern V3" value={formatScore(detailV3Metadata.patternQuality?.score)} helper={formatV3ScoreHelper(detailV3Metadata.patternQuality)} tone="accent" />
                    <MetricCard label="Qualité breakout V3" value={formatScore(detailV3Metadata.breakoutQuality?.score)} helper={formatV3ScoreHelper(detailV3Metadata.breakoutQuality)} tone="success" />
                    <MetricCard label="Biais directionnel V3" value={formatCanonicalBiasValue(detailV3Bias)} helper={formatV3DirectionalBiasHelper(detailV3Bias)} tone="accent" />
                    <MetricCard label="Confiance setup V3" value={formatScore(detailV3Metadata.setupConfidence?.score)} helper={formatV3ScoreHelper(detailV3Metadata.setupConfidence)} />
                  </Box>
                ) : null}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.2 }}>
                  {[
                    { label: 'Breakout', value: detail.detection.breakoutPrice },
                    { label: 'Target 1', value: detail.detection.target1Price },
                    { label: 'Target 2', value: detail.detection.target2Price },
                    { label: 'Target final', value: detail.detection.targetFinalPrice },
                    { label: 'Invalidation', value: detail.detection.invalidationPrice },
                    { label: 'Stop', value: detail.detection.stopPrice },
                  ].map((item) => (
                    <Paper key={item.label} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                      <Typography variant="caption" sx={{ color: '#4f646f' }}>{item.label}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 800, color: '#17323c' }}>{item.value ?? 'n/a'}</Typography>
                    </Paper>
                  ))}
                </Box>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c', mb: 1.5 }}>
                    Timeline des événements
                  </Typography>
                  {detail.events?.length ? (
                    <Stack spacing={1.2}>
                      {detail.events.map((event) => (
                        <Box key={event.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #efe8dc' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#17323c' }}>{event.eventType}</Typography>
                            <Typography variant="caption" sx={{ color: '#4f646f' }}>{formatDateTime(event.eventCandleAt)}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ color: '#17323c' }}>{event.eventPrice ?? 'n/a'}</Typography>
                            <Typography variant="caption" sx={{ color: '#4f646f' }}>{formatDuration(event.elapsedSeconds)} · {event.elapsedCandles ?? 0} bougie(s)</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Aucun événement historisé pour cette détection.</Alert>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c', mb: 1.5 }}>
                    Durées et évaluation
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
                    {[
                      { label: 'Time to breakout', value: formatDuration(detail.duration.timeToBreakoutSeconds) },
                      { label: 'Time to target final', value: formatDuration(detail.duration.timeToTargetFinalSeconds) },
                      { label: 'Time to invalidation', value: formatDuration(detail.duration.timeToInvalidationSeconds) },
                      { label: 'Horizon', value: formatDateTime(detail.evaluation.evaluationUntil) },
                    ].map((item) => (
                      <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.75, borderBottom: '1px solid #efe8dc' }}>
                        <Typography variant="body2" sx={{ color: '#4f646f' }}>{item.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#17323c' }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default PatternPerformanceAdminPage;